import React, { useState, useRef, useEffect, useCallback } from 'react';
import useTypewriter from '../utils/useTypewriter';
import { ArrowUp, Paperclip, Sparkles, ChevronDown, ChevronRight, FileText, Presentation, Loader2, X, Upload, Trash2, Plus, Send, Users, GraduationCap, Briefcase, Globe, Megaphone, BarChart3, GitBranch, Clock, ArrowLeftRight, BookOpen, Palette, Moon, Minimize2, Building2, Zap, PieChart, TrendingUp, Shuffle, Image, Brain, Cpu, Eye, Wand2, ImagePlus, Languages, AlignLeft, Type, ListMinus, PanelLeftClose, PanelLeftOpen, Table2, Play, ShieldCheck, CheckCircle2, XCircle, AlertTriangle, ExternalLink, Crosshair, Layers, Download, Save, Share2, RefreshCw, Undo2, Redo2, Check, BoxSelect } from 'lucide-react';
import CreatePPTWorkspace from './CreatePPTWorkspace';
import InfographicWorkspace from './InfographicWorkspace';
import DocumentWorkspace from './DocumentWorkspace';
import DashboardWorkspace from './DashboardWorkspace';
import SheetsWorkspace from './SheetsWorkspace';
import ClarifyingQuestionsPanel, { DECIDE_FOR_ME_VALUE } from './ClarifyingQuestionsPanel';
import StatusChip from './StatusChip';
import { LayoutTemplate, Edit3 } from 'lucide-react';
import NativePPTWorkspace from './NativePPTWorkspace';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { toast } from 'sonner';
import { withBackend } from '../utils/api';
import { authHeaders } from '../utils/authHeaders';
import { motion, AnimatePresence } from 'framer-motion';
import * as XLSX from 'xlsx';
import ReactMarkdown from 'react-markdown';
import { getDefaultModel } from '../utils/models';
import { maybeConfirmCost } from '../utils/costConfirm';

// ─── Question definitions (shared with AgenticFlow) ─────────────────────────

// Build a dynamic brand identity question from the user's saved brands
function buildBrandQuestion(brandIdentities) {
  if (!brandIdentities || brandIdentities.length === 0) return null;
  const noBrandOption = { label: 'No brand — let AI decide', value: 'none', icon: 'Sparkles', desc: 'AI picks colors and style based on your content' };

  // 3 or fewer brands: show as cards with "No brand" option
  if (brandIdentities.length <= 3) {
    const options = brandIdentities.map(bi => ({
      label: bi.name,
      value: bi.id,
      icon: 'Palette',
      colorSwatch: bi.primary_color,
      desc: [bi.tagline, bi.tone_of_voice, bi.industry].filter(Boolean).join(' · ') || 'Apply brand colors, fonts, and style',
    }));
    options.push(noBrandOption);
    return {
      id: 'brandIdentity',
      botMessage: "Would you like to apply a saved brand identity?",
      options,
    };
  }

  // 4+ brands: use dropdown
  const dropdownOptions = brandIdentities.map(bi => ({
    label: bi.name,
    value: bi.id,
    desc: [bi.tagline, bi.tone_of_voice].filter(Boolean).join(' · ') || '',
    colorSwatch: bi.primary_color,
  }));
  return {
    id: 'brandIdentity',
    botMessage: "Would you like to apply a saved brand identity?",
    type: 'brand-dropdown',
    dropdownOptions,
  };
}

// Project-type-aware label helper. The system stores everything as `slides[]`
// internally (legacy from when only presentations existed) but user-facing
// labels should reflect the actual unit type. Use this whenever you'd
// otherwise hardcode 'Slide N'.
function getUnitLabel(projectType, capitalized = true) {
  const map = {
    presentation: capitalized ? 'Slide' : 'slide',
    document: capitalized ? 'Section' : 'section',
    infographic: capitalized ? 'Section' : 'section',
    dashboard: capitalized ? 'Panel' : 'panel',
    sheets: capitalized ? 'Tab' : 'tab',
  };
  return map[projectType] || (capitalized ? 'Slide' : 'slide');
}

function getPresentationQuestions(answers, brandIdentities = []) {
  const questions = [];

  const brandQ = buildBrandQuestion(brandIdentities);
  if (brandQ) questions.push(brandQ);

  // Generation-mode question retired — the platform always uses Intelligent
  // (HTML). The Imaginative ('ai') path stays reachable only when the user
  // states it in the prompt (prefs.generation_mode).
  if ((answers.generationMode || 'html') === 'html') {
    questions.push({
      id: 'htmlModel', botMessage: "Speed vs quality for slide generation:", options: [
        { label: 'Gemini 3 Flash', value: 'flash', icon: 'Zap', desc: 'Fast generation, great for iteration' },
        { label: 'Gemini 3.1 Pro', value: 'pro', icon: 'Cpu', desc: 'Vision-strong, great when using design references' },
        { label: 'Claude Sonnet 5', value: 'sonnet', icon: 'Sparkles', desc: 'Balanced: fast and high quality' },
        { label: 'Claude Opus 5', value: 'opus', icon: 'Sparkles', desc: 'Highest quality, slower but more polished' },
        { label: 'GPT-5.6 Sol', value: 'sol', icon: 'Sparkles', desc: 'OpenAI flagship — premium tier, slightly pricier than Opus' },
        { label: 'GPT-6 Astra', value: 'astra', icon: 'Sparkles', desc: 'OpenAI frontier — Ultra tier, 2× Premium credits' },
      { label: 'Kimi K3', value: 'kimi', icon: 'Sparkles', desc: 'Moonshot flagship — 1M context, Sonnet-class pricing' },
      ],
    });
  }
  {
    questions.push({
      id: 'imageryMode', botMessage: "How should we handle imagery in this deck?", options: [
        { label: 'AI photos where they fit', value: 'auto', icon: 'Image', desc: 'Premium photographic imagery on covers and hero slides' },
        { label: 'Illustrations / icons only', value: 'illustrations', icon: 'Shapes', desc: 'SVG illustrations, no photographs' },
        { label: 'No imagery — typography-led', value: 'none', icon: 'Type', desc: 'Bold type, color, and restrained accents only' },
      ],
    });
    questions.push({
      id: 'runQa', botMessage: "Run a quality check after generating?", options: [
        { label: 'Yes — polished output', value: 'yes', icon: 'Sparkles', desc: 'Catches layout glitches; takes longer and uses more credits' },
        { label: 'No — fastest possible', value: 'no', icon: 'Zap', desc: 'Skip QA, ship the first draft' },
      ],
    });
    questions.push({
      id: 'outlinePreview', botMessage: "Want to review the outline before we design the slides?", options: [
        { label: 'Generate straight away', value: 'skip', icon: 'Zap', desc: 'Go straight to designed slides — fastest' },
        { label: 'Review the outline first', value: 'review', icon: 'Eye', desc: 'See and edit the structure before generation' },
      ],
    });
    questions.push({
      id: 'wantVariations', botMessage: "Would you like to see design variations before generating?", options: [
        { label: 'Yes, show me options', value: 'yes', icon: 'Palette', desc: 'Compare multiple style directions first' },
        { label: 'No, generate directly', value: 'no', icon: 'Zap', desc: 'Skip to generation — faster results' },
      ],
    });
    if (answers.wantVariations === 'yes') {
      questions.push({
        id: 'variationCount', botMessage: "How many design variations would you like to compare?", options: [
          { label: '2 variations', value: '2', icon: 'ArrowLeftRight', desc: 'Quick A/B comparison' },
          { label: '3 variations', value: '3', icon: 'Palette', desc: 'Good range of options' },
          { label: '4 variations', value: '4', icon: 'Shuffle', desc: 'Maximum variety to choose from' },
        ],
      });
    }
  }
  return questions;
}

function getInfographicQuestions(answers, brandIdentities = []) {
  const questions = [];
  const brandQ = buildBrandQuestion(brandIdentities);
  if (brandQ) questions.push(brandQ);
  // Generation-mode question retired — always Intelligent (HTML); see
  // getPresentationQuestions for rationale.
  if ((answers.generationMode || 'html') === 'html') {
    questions.push({
      id: 'htmlModel', botMessage: "Speed vs quality for infographic generation:", options: [
        { label: 'Gemini 3 Flash', value: 'flash', icon: 'Zap', desc: 'Fast generation, great for iteration' },
        { label: 'Gemini 3.1 Pro', value: 'pro', icon: 'Cpu', desc: 'Vision-strong, great when using design references' },
        { label: 'Claude Sonnet 5', value: 'sonnet', icon: 'Sparkles', desc: 'Balanced: fast and high quality' },
        { label: 'Claude Opus 5', value: 'opus', icon: 'Sparkles', desc: 'Highest quality, slower but more polished' },
        { label: 'GPT-5.6 Sol', value: 'sol', icon: 'Sparkles', desc: 'OpenAI flagship — premium tier, slightly pricier than Opus' },
        { label: 'GPT-6 Astra', value: 'astra', icon: 'Sparkles', desc: 'OpenAI frontier — Ultra tier, 2× Premium credits' },
      { label: 'Kimi K3', value: 'kimi', icon: 'Sparkles', desc: 'Moonshot flagship — 1M context, Sonnet-class pricing' },
      ],
    });
    questions.push({
      id: 'runQa', botMessage: "Run a quality check after generating?", options: [
        { label: 'Yes — polished output', value: 'yes', icon: 'Sparkles', desc: 'Catches layout glitches; takes longer and uses more credits' },
        { label: 'No — fastest possible', value: 'no', icon: 'Zap', desc: 'Skip QA, ship the first draft' },
      ],
    });
  }
  return questions;
}

function getDocumentQuestions(answers, brandIdentities = []) {
  const questions = [];
  const brandQ = buildBrandQuestion(brandIdentities);
  if (brandQ) questions.push(brandQ);
  questions.push({
    id: 'htmlModel', botMessage: "Speed vs quality for document generation:", options: [
      { label: 'Fast (Gemini 3 Flash)', value: 'flash', icon: 'Zap', desc: 'Quick generation, great for drafts and iteration' },
      { label: 'Gemini 3.1 Pro', value: 'pro', icon: 'Cpu', desc: 'Vision-strong, great when using design references' },
      { label: 'Claude Sonnet 5', value: 'sonnet', icon: 'Sparkles', desc: 'Balanced: fast and high quality' },
      { label: 'Claude Opus 5', value: 'opus', icon: 'Sparkles', desc: 'Premium quality, richer content and formatting' },
      { label: 'GPT-5.6 Sol', value: 'sol', icon: 'Sparkles', desc: 'OpenAI flagship — premium tier, slightly pricier than Opus' },
        { label: 'GPT-6 Astra', value: 'astra', icon: 'Sparkles', desc: 'OpenAI frontier — Ultra tier, 2× Premium credits' },
      { label: 'Kimi K3', value: 'kimi', icon: 'Sparkles', desc: 'Moonshot flagship — 1M context, Sonnet-class pricing' },
    ],
  });
  questions.push({
    id: 'imageryMode', botMessage: "How should we handle imagery in this document?", options: [
      { label: 'AI photos where they fit', value: 'auto', icon: 'Image', desc: 'Premium photographic imagery on cover and hero sections' },
      { label: 'Illustrations / icons only', value: 'illustrations', icon: 'Shapes', desc: 'SVG illustrations, no photographs' },
      { label: 'No imagery — typography-led', value: 'none', icon: 'Type', desc: 'Bold type, color, and restrained accents only' },
    ],
  });
  questions.push({
    id: 'runQa', botMessage: "Run a quality check after generating?", options: [
      { label: 'Yes — polished output', value: 'yes', icon: 'Sparkles', desc: 'Catches layout glitches; takes longer and uses more credits' },
      { label: 'No — fastest possible', value: 'no', icon: 'Zap', desc: 'Skip QA, ship the first draft' },
    ],
  });
  return questions;
}

function getDashboardQuestions(answers, brandIdentities = []) {
  const questions = [];
  const brandQ = buildBrandQuestion(brandIdentities);
  if (brandQ) questions.push(brandQ);
  if (!answers._hasExcelAttachment) {
    questions.push({
      id: 'googleSheetUrl',
      botMessage: "Want to connect a live Google Sheet? Paste the URL below, or skip to use static data.\n\n(The sheet must be shared as 'Anyone with the link can view')",
      type: 'freetext-optional',
      placeholder: 'https://docs.google.com/spreadsheets/d/...',
    });
  }
  questions.push({
    id: 'htmlModel', botMessage: "Speed vs quality for dashboard generation:", options: [
      { label: 'Fast (Gemini 3 Flash)', value: 'flash', icon: 'Zap', desc: 'Quick generation, great for drafts and iteration' },
      { label: 'Gemini 3.1 Pro', value: 'pro', icon: 'Cpu', desc: 'Vision-strong, great when using design references' },
      { label: 'Claude Sonnet 5', value: 'sonnet', icon: 'Sparkles', desc: 'Balanced: fast and high quality' },
      { label: 'Claude Opus 5', value: 'opus', icon: 'Sparkles', desc: 'Premium quality, richer layouts and visualizations' },
      { label: 'GPT-5.6 Sol', value: 'sol', icon: 'Sparkles', desc: 'OpenAI flagship — premium tier, slightly pricier than Opus' },
        { label: 'GPT-6 Astra', value: 'astra', icon: 'Sparkles', desc: 'OpenAI frontier — Ultra tier, 2× Premium credits' },
      { label: 'Kimi K3', value: 'kimi', icon: 'Sparkles', desc: 'Moonshot flagship — 1M context, Sonnet-class pricing' },
    ],
  });
  questions.push({
    id: 'runQa', botMessage: "Run a quality check after generating?", options: [
      { label: 'Yes — polished output', value: 'yes', icon: 'Sparkles', desc: 'Catches layout glitches; takes longer and uses more credits' },
      { label: 'No — fastest possible', value: 'no', icon: 'Zap', desc: 'Skip QA, ship the first draft' },
    ],
  });
  return questions;
}

function getSheetsQuestions(answers, brandIdentities = []) {
  const questions = [];
  const brandQ = buildBrandQuestion(brandIdentities);
  if (brandQ) questions.push(brandQ);
  if (!answers._hasExcelAttachment) {
    questions.push({
      id: 'generateSampleData', botMessage: "Should I generate sample/dummy data for the spreadsheet, or will you work with an empty structure?", options: [
        { label: 'Generate sample data', value: 'yes', icon: 'Sparkles', desc: 'AI creates realistic placeholder data to get started' },
        { label: 'Empty structure only', value: 'no', icon: 'FileText', desc: 'Just headers and formulas, you fill in the data' },
      ],
    });
  }
  return questions;
}

// Annotate each model option with its remaining monthly quota.
// 'flash', 'pro', and 'opus' all have separate per-tier limits on the
// free plan, so each is checked against its matching usage row.
// Legacy 'gpt'/'gpt55' values still map to the opus usage bucket since
// they classify as 'opus' on the backend.
// r129: how many content pages the outline model READS VISUALLY. Must track
// the backend default (_OUTLINE_PAGE_IMG_CAP_DEFAULT / OUTLINE_PAGE_IMAGES_CAP
// env) — the backend trims anything past its own cap. Full TEXT of every page
// always goes in regardless; images are sampled evenly past this cap.
const OUTLINE_PAGE_IMAGE_CAP = 70;

const _MODEL_VALUE_TO_TIER = {
  flash:  'flash',
  pro:    'pro',
  sonnet: 'pro',   // Claude Sonnet shares the mid (pro) quota — matches backend classify_model_tier
  opus:   'opus',
  gpt:    'opus',
  gpt55:  'opus',
  sol:    'opus',  // GPT-5.6 Sol is Opus-tier priced and up — shares the premium quota
  astra:  'ultra', // r178: GPT-6 Astra at $10/$50 = 2× the premium tier — its own Ultra quota + rates
  kimi:   'pro',   // Kimi K3 at $3/$15 = Sonnet-class pricing — mid (pro) quota, matches backend classify_model_tier
};

// The chat PLANNER backend speaks haiku|sonnet|opus|gemini-pro only (unknown
// values fall back to haiku server-side). The unified model picker also lists
// flash/kimi/sol for EDITS, so planning maps them to the nearest supported
// planner instead of silently degrading to haiku.
const CHAT_PLAN_MODEL = {
  haiku: 'haiku', flash: 'haiku', 'gemini-pro': 'gemini-pro',
  sonnet: 'sonnet', kimi: 'sonnet', opus: 'opus', sol: 'opus', astra: 'opus',
};

// r180: proposal scoping rows join the clarifier panel as freetext questions
// (one questionnaire, not two). Planned questions sit in the visible
// 'direction' group; uncovered RFP requirements that need vendor input sit in
// 'details' with "The RFP requires -" provenance. Blank = placeholder in the
// deck (the frozen block carries the spec's not-answered instruction).
const buildScopeQuestions = (scope) => {
  const qs = [];
  (scope?.questions || []).forEach((q, i) => {
    const parts = (q.parts || []).length ? ` Cover: ${q.parts.join('; ')}.` : '';
    qs.push({
      id: `pq_${i}`, type: 'freetext', group: 'direction', label: q.question,
      sub_help: `${q.hint ? `${q.hint}.` : ''}${parts}`.trim() || 'Leave blank to keep it as a placeholder in the deck.',
      allow_decide_for_me: false, required: false, _scoping: true,
    });
  });
  const rows = Array.isArray(scope?.fill_rows) ? scope.fill_rows : [];
  rows.forEach((j) => {
    const r = scope.requirements?.[j];
    if (!r) return;
    qs.push({
      id: `preq_${j}`, type: 'freetext', group: 'details', label: `The RFP requires - ${r.text}`,
      sub_help: `[${r.category}] Leave blank to keep it as a placeholder in the deck.`,
      allow_decide_for_me: false, required: false, _scoping: true,
    });
  });
  return qs;
};

function _annotateModelOptions(questions, genType, usageData) {
  if (!usageData?.usage) return questions;
  const genUsage = usageData.usage[genType];
  if (!genUsage) return questions;
  return questions.map(q => {
    if (q.id !== 'htmlModel') return q;
    return {
      ...q,
      options: q.options.map(opt => {
        const tierKey = _MODEL_VALUE_TO_TIER[opt.value];
        if (!tierKey) return opt;
        const usage = genUsage[tierKey];
        if (!usage) return opt;
        if (usage.limit === 0) {
          // limit 0 = excluded from the CURRENT plan — which is not always
          // Free (e.g. Starter has no premium sheets); name the actual plan.
          const planName = usageData?.tier
            ? usageData.tier.charAt(0).toUpperCase() + usageData.tier.slice(1)
            : 'Free';
          return { ...opt, disabled: true, desc: `Not available on the ${planName} plan` };
        }
        if (usage.remaining === 0) {
          return { ...opt, disabled: true, desc: `Monthly limit reached (${usage.used}/${usage.limit})` };
        }
        if (usage.remaining != null && usage.limit != null) {
          const unitLabel = usage.unit === 'slides' ? ' slides' : '';
          return { ...opt, desc: `${opt.desc} (${usage.remaining}/${usage.limit}${unitLabel} left this month)` };
        }
        return opt;
      }),
    };
  });
}

function getQuestions(answers, projectType, brandIdentities = [], usageData = null) {
  const genTypeMap = { presentation: 'presentation', infographic: 'infographic', document: 'document', dashboard: 'dashboard', sheets: 'sheet' };
  let questions;
  if (projectType === 'infographic') questions = getInfographicQuestions(answers, brandIdentities);
  else if (projectType === 'document') questions = getDocumentQuestions(answers, brandIdentities);
  else if (projectType === 'dashboard') questions = getDashboardQuestions(answers, brandIdentities);
  else if (projectType === 'sheets') questions = getSheetsQuestions(answers, brandIdentities);
  else questions = getPresentationQuestions(answers, brandIdentities);
  return _annotateModelOptions(questions, genTypeMap[projectType] || 'presentation', usageData);
}

const OPTION_ICONS = {
  Users, GraduationCap, Briefcase, Globe, Megaphone, BarChart3, GitBranch, Clock,
  ArrowLeftRight, BookOpen, Palette, Moon, Minimize2, Building2, Zap, PieChart,
  TrendingUp, Shuffle, Image, Brain, Cpu, Eye, Wand2, ImagePlus, Sparkles, FileText,
  Languages, AlignLeft, Type, ListMinus,
};

const PROJECT_TYPE_LABELS = {
  presentation: 'Presentation',
  infographic: 'Infographic',
  document: 'Document',
  dashboard: 'Dashboard',
  sheets: 'Spreadsheet',
};

const PROJECT_TYPE_COLORS = {
  presentation: 'from-green-500 to-emerald-500',
  infographic: 'from-purple-500 to-violet-500',
  document: 'from-blue-500 to-cyan-500',
  dashboard: 'from-orange-500 to-amber-500',
  sheets: 'from-teal-500 to-emerald-500',
};

const PROJECT_TYPE_ICONS = {
  presentation: Presentation,
  infographic: PieChart,
  document: FileText,
  dashboard: BarChart3,
  sheets: Table2,
};

// ─── Main Component ─────────────────────────────────────────────────────────

export default function UnifiedWorkspace({
  projectType = 'presentation', onBack, onOutlineReady, userId, onSwitchProjectType,
  // r131: proposal mode — facts-only generation; unknowns become placeholders
  proposalMode = false,
  // Workspace props (forwarded to workspace components)
  onComplete, onUpdate, onProjectCreated, onFullscreen, existingProjectId, existingProject,
  // Saved project data — when provided, skip straight to workspace phase
  savedFlowResult,
  // Initial prompt from homepage — auto-submits on mount
  initialPrompt = '',
  // Initial file from homepage — processed on mount
  initialFile = null,
  // Initial files (multi-upload from homepage) — all processed on mount
  initialFiles = null,
  // Inline control choices from the home prompt (model/brand) — pre-seed the flow
  initialAnswers = null,
  // Usage tracking
  usageData = null,
  onUsageRefresh = null,
}) {
  // ── Phase management ──
  const [flowPhase, setFlowPhase] = useState(savedFlowResult ? 'workspace' : 'prompt'); // prompt | clarifying-questions | questionnaire | generating-outline | outline-ready | workspace
  const [clarifyingPayload, setClarifyingPayload] = useState({ questions: [], rationale: '', defaults: null });
  // Outline preview is OPT-IN. By default we skip the review gate and go straight
  // to generation once the outline is ready (the outline LLM call still runs — it
  // produces the slide list generation needs — we just don't pause for review).
  // Users can turn review back on via the toggle; the choice persists in
  // localStorage. Default (unset) = skip = straight to generation.
  const [skipOutlinePreview, setSkipOutlinePreview] = useState(
    () => { try { return localStorage.getItem('skipOutlinePreview') !== 'false'; } catch { return true; } }
  );

  // ── Brand identities ──
  const [brandIdentities, setBrandIdentities] = useState([]);
  const brandIdentitiesRef = useRef([]);
  // Resolves when the brands fetch has settled. A prompt submitted before it
  // lands classified against an EMPTY brands list, so a brand named in the
  // prompt was unresolvable — it fell into special_instructions as prose,
  // the deck generated BRANDLESS, and the clarifier offered invented theme
  // directions instead. (Live 11/08: a brand created seconds earlier, then
  // "regenerate ... using our 966 innovation hub branding".)
  const brandsReadyRef = useRef(null);

  // Fetch user's brand identities on mount
  useEffect(() => {
    if (!userId) return;
    brandsReadyRef.current = fetch(withBackend(`/api/brand-identities?user_id=${userId}`))
      .then(r => r.json())
      .then(data => {
        const bis = data.brand_identities || [];
        setBrandIdentities(bis);
        brandIdentitiesRef.current = bis;
      })
      .catch(() => {});
  }, [userId]);

  // ── Prompt state ──
  const [prompt, setPrompt] = useState(initialPrompt || '');
  const initialPromptFiredRef = useRef(false);
  const [submittedPrompt, setSubmittedPrompt] = useState('');
  const submittedPromptRef = useRef('');
  // r141: submits AWAIT this instead of bouncing off the parsing guard
  const fileParsePromiseRef = useRef(null);
  // r141b: overlapping drops each hold a count — the flag clears only when
  // the LAST batch finishes (one boolean let batch 1 release a submit while
  // batch 2 was still parsing)
  const parseDepthRef = useRef(0);
  // r141: round-1 classification, reused on clarification re-entries
  const unifiedPrefsRef = useRef(null);
  // ── Workspace phase state ──
  const [workspaceData, setWorkspaceData] = useState(savedFlowResult || null); // { outline, questionnaireData, flowConfig }
  const workspaceRef = useRef(null);
  // r140: user-requested stop for the direct pipeline (the workspace Cancel
  // signals the parent loop through this)
  const directCancelRef = useRef(false);
  // r144: mirror of the child's deck edit mode, for the header button's
  // active styling (the child returns the new state on toggle)
  const [wsDeckEditMode, setWsDeckEditMode] = useState(false);
  // r145: a running plan/edit batch — powers the Stop button and blocks
  // overlapping sends for the WHOLE execution, not just the planner call.
  const [planRunning, setPlanRunning] = useState(false);
  const planCancelRef = useRef(false);
  useEffect(() => { setWsDeckEditMode(false); }, [workspaceData]); // child remounts reset to off
  // r140b: true while the direct pipeline streams slides — chat edits are
  // locked out (a mid-feed delete/duplicate splices the deck while adopts
  // keep writing at the original indices)
  const directFeedActiveRef = useRef(false);
  // r149: the feed's placeholder project row + a serialized persistence
  // chain (single writer — the backend does read-modify-write)
  const feedPidRef = useRef(null);
  const feedChainRef = useRef(Promise.resolve());
  // Chat controls mirrored from CreatePPTWorkspace ref
  const [wsRegionMode, setWsRegionMode] = useState(false);
  const [wsApplyAll, setWsApplyAll] = useState(false);
  const [pendingPlan, setPendingPlan] = useState(null); // {plan, originalMessage} — awaiting user confirm
  const [wsEditModel, setWsEditModel] = useState('flash');
  // Chat picker model — the "thinking" model that plans, analyzes, and
  // writes back. Distinct from the deck's generation model.
  // Options: 'haiku' (fast), 'sonnet' (balanced default), 'opus' (complex), 'gemini-pro' (vision-strong).
  const [chatModel, setChatModel] = useState('sonnet');
  const [showChatModelMenu, setShowChatModelMenu] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  // Pre-download notice shown before an editable-PPTX export (shapes need a
  // manual "Convert to Shape" step in PowerPoint; some shapes / fonts caveats).
  const [showEditablePptxModal, setShowEditablePptxModal] = useState(false);

  // ── Questionnaire ──
  const [answers, setAnswers] = useState({});
  const [questionIndex, setQuestionIndex] = useState(0);

  // ── Chat messages ──
  // Detect whether this saved project was created via the MCP integration
  // (Claude / Cursor / any AI assistant) so we can show a small "Created
  // via Claude" badge in the chat header. The metadata flag is set by
  // the backend when /api/create-presentation-job receives created_via=mcp.
  const createdVia = savedFlowResult?.createPPTData?.metadata?.created_via
    || savedFlowResult?.metadata?.created_via
    || null;
  const isMcpCreated = createdVia === 'mcp';
  const [messages, setMessages] = useState(
    savedFlowResult?.savedChatHistory?.length > 0
      ? savedFlowResult.savedChatHistory
      : savedFlowResult
        ? [{
            role: 'system',
            text: isMcpCreated
              ? `🤖 Originally created via Claude. You can keep editing this ${projectType} using the chat below.`
              : `Loaded saved ${projectType}. You can edit it using the chat below.`,
            isStatus: true,
          }]
        : []
  );

  // ── Chat persistence ──
  // Chat used to piggyback on slide saves (which native decks never do, and
  // which HTML decks drop on rev conflicts) — so reopened projects lost the
  // conversation. Persist it on its own lightweight path instead.
  const workspaceProjectIdRef = useRef(existingProjectId || null);
  // Latched at MOUNT: `existingProjectId` is a live prop that App.js flips
  // truthy as soon as onProjectCreated fires mid-generation, so reading it
  // later cannot distinguish "reopened a saved project" from "fresh project
  // that just got its id". Only a mount-time id means a reopen.
  const isReopenRef = useRef(!!existingProjectId);
  const chatSaveTimerRef = useRef(null);
  const handleProjectCreated = useCallback((projectId, name, meta) => {
    workspaceProjectIdRef.current = projectId;
    onProjectCreated?.(projectId, name, meta);
  }, [onProjectCreated]);

  // Only light display fields survive persistence — messages carry huge
  // non-restorable payloads (batch slide snapshots, questionnaire option
  // arrays, attachment blobs) that would bloat the project row. Interactive
  // bubbles (plan confirmations, batch reverts) and payload-dependent ones
  // (QA/fact-check flags without their issues/claims) are stripped or
  // downgraded to plain text — a dead button is worse than no button.
  const sanitizeMessagesForSave = (msgs) => (msgs || [])
    .filter(m => m && !m.isLoading && !m.isPlanConfirmation && !m.isBatchRevert && !m.isEditDiff && !m.isCoverage && (m.text || m.chipLabel))
    .map(({ role, text, chipLabel, chipState, isResearch, isIntentClarification, isImprovement, isStatus, questionId, isError }) => ({
      role, text, chipLabel,
      chipState: chipState === 'running' ? 'done' : chipState,
      isResearch, isIntentClarification, isImprovement, isStatus, questionId, isError,
    }));

  // Debounced chat save whenever the transcript CHANGES after load. The
  // reopen-baseline only applies to EXISTING projects (a reopen must not
  // immediately re-POST what it just read) — for a fresh generation the
  // whole transcript is new and must be persisted. The old code baselined
  // unconditionally on the first workspace-phase run, which swallowed the
  // entire generation conversation: generate-and-leave saved NO chat, and
  // reopening showed only the greeting.
  const lastSavedChatRef = useRef(null);
  const unsavedChatRef = useRef(null);
  useEffect(() => {
    const pid = workspaceProjectIdRef.current;
    // No flowPhase gate: the project id exists from job start, and the
    // generation-time transcript should persist even if the user leaves
    // before reaching the workspace phase.
    if (!pid || !userId || !messages.length) return undefined;
    const payload = JSON.stringify(sanitizeMessagesForSave(messages));
    if (lastSavedChatRef.current === null) {
      if (isReopenRef.current) {
        lastSavedChatRef.current = payload;
        return undefined;
      }
      lastSavedChatRef.current = '';
    }
    if (payload === lastSavedChatRef.current) return undefined;
    unsavedChatRef.current = { pid, payload };
    clearTimeout(chatSaveTimerRef.current);
    chatSaveTimerRef.current = setTimeout(() => {
      const attemptSave = (attempt) => {
        fetch(withBackend(`/api/projects/${pid}/chat`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: userId, chat_history: JSON.parse(payload) }),
        }).then((r) => {
          // Advance the baseline only on a confirmed save — on failure the
          // pending snapshot stays in unsavedChatRef so the retry below
          // (or the next transcript change / unmount flush) re-sends it.
          if (r.ok) {
            lastSavedChatRef.current = payload;
            if (unsavedChatRef.current?.payload === payload) unsavedChatRef.current = null;
          } else if (attempt < 3 && unsavedChatRef.current?.payload === payload) {
            // 409 (rev race during generation) or transient server error:
            // without a re-arm, an idle session's final transcript delta
            // would stay unsaved forever — nothing else re-fires the effect.
            chatSaveTimerRef.current = setTimeout(() => attemptSave(attempt + 1), 8000 * (attempt + 1));
          }
        }).catch(() => {
          if (attempt < 3 && unsavedChatRef.current?.payload === payload) {
            chatSaveTimerRef.current = setTimeout(() => attemptSave(attempt + 1), 8000 * (attempt + 1));
          }
        });
      };
      attemptSave(0);
    }, 2500);
    return () => clearTimeout(chatSaveTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, flowPhase, userId]);

  // Flush a pending (debounced, unsent) chat save on unmount so the last
  // exchange isn't lost when the user navigates away within the window.
  useEffect(() => () => {
    const u = unsavedChatRef.current;
    if (u && userId) {
      unsavedChatRef.current = null;
      fetch(withBackend(`/api/projects/${u.pid}/chat`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, chat_history: JSON.parse(u.payload) }),
        // keepalive lets the request outlive the unmounting tree on
        // in-app navigations (React cleanups never run on tab close —
        // that path is covered by the mid-session retry above).
        keepalive: true,
      }).catch(() => { /* best-effort */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Outline ──
  const [outlineTitle, setOutlineTitle] = useState('');
  const [outlineSlides, setOutlineSlides] = useState([]);
  const [outlineLoading, setOutlineLoading] = useState(false);
  const [webResearch, setWebResearch] = useState('');
  const [outlineMetadata, setOutlineMetadata] = useState(null);

  // ── Variations ──
  const [variations, setVariations] = useState([]); // [{ direction: { name, description, colors }, slides: [{ html_source }] }]
  const [selectedVariationIndex, setSelectedVariationIndex] = useState(null);
  const [variationSampleIndices, setVariationSampleIndices] = useState([]); // The 3 outline slide indices used for samples
  const variationSelectedRef = useRef(false); // Tracks if user already picked a variation (prevents SSE complete from resetting flowPhase)
  const variationAbortRef = useRef(null); // AbortController for variation SSE — aborted when user selects early
  // AbortController for the outline SSE. Typing while the outline is still
  // streaming STEERS it: the in-flight stream is aborted and a fresh one
  // starts with the user's instruction folded in. Without this, a mid-stream
  // message would spawn a second stream racing the first over outlineSlides.
  const outlineStreamAbortRef = useRef(null);
  // Outline generation epoch. abort() only rejects PENDING reads — a run
  // whose final read already resolved (or the unabortable deterministic
  // marker-split fetch) would still land its outline and flow-phase change
  // AFTER a steer replaced it. Every new outline run / steer bumps the
  // epoch; stale continuations compare and bail.
  const outlineEpochRef = useRef(0);
  const [variationBrandMode, setVariationBrandMode] = useState(true); // true = on-brand layout variations, false = creative style variations

  // ── Thinking ──
  const [thinkingText, setThinkingText] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const displayedThinking = useTypewriter(thinkingText, isThinking ? 80 : 9999);
  const [thinkingOpen, setThinkingOpen] = useState(true);

  // ── Toolbar ──
  // Deep mode is retired — UI toggle was removed and the extra questions it
  // gated (audience, language, presentationType, visualStyle, textStyle) are
  // either LLM-inferred or covered by classify-prompt-preferences now.
  const [webSearch, setWebSearch] = useState(false);
  const [outlineModel, setOutlineModel] = useState('gemini-flash');
  const [showModelMenu, setShowModelMenu] = useState(false);

  // ── Attachments ──
  // Primary attachment (first file dropped) + extras (additional files).
  // Multi-file: user can drop multiple files in one go OR one after another.
  // Haiku classifies each file's purpose (content / design_reference) after
  // the user submits their prompt — no manual toggles.
  const [attachment, setAttachment] = useState(null);
  const [extraAttachments, setExtraAttachments] = useState([]);
  const extraAttachmentsRef = useRef([]);
  useEffect(() => { extraAttachmentsRef.current = extraAttachments; }, [extraAttachments]);
  const [isFileParsing, setIsFileParsing] = useState(false);
  // Ref mirror of isFileParsing, set SYNCHRONOUSLY so the submit guard can't read
  // a stale closure value. The state version drives the button-disabled UI; the
  // ref version is the authoritative block (a quick upload-then-submit was
  // slipping through because setIsFileParsing hadn't re-rendered yet, so the
  // clarifier ran before the file finished parsing — has_file:false).
  const isFileParsingRef = useRef(false);
  const [fileParseInfo, setFileParseInfo] = useState(null); // { name, stage }

  // ── One-at-a-time submission guard ──
  // Claude-style: while a user prompt is being processed (classify, outline,
  // generation), block submitting another prompt. Ref-based to survive async
  // React state batching.
  const isSubmitInFlightRef = useRef(false);
  const [isSubmitInFlight, setIsSubmitInFlight] = useState(false);
  const [chatDragOver, setChatDragOver] = useState(false);
  const attachmentRef = useRef(null);
  const fileInputRef = useRef(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const logoInputRef = useRef(null);

  // ── Chat panel ──
  const [chatCollapsed, setChatCollapsed] = useState(false);

  // ── Refs ──
  const chatEndRef = useRef(null);
  const textareaRef = useRef(null);
  const webSearchRef = useRef(webSearch);
  // True once the user has clicked the globe themselves — the classifier's
  // auto-research verdict never overrides an explicit human choice.
  const webSearchUserSetRef = useRef(false);
  // Set when the classifier auto-enabled research; consumed (once) to post
  // the explanatory notice at generation time, where it belongs.
  const webSearchAutoNoticeRef = useRef(false);

  // Sync refs
  useEffect(() => { webSearchRef.current = webSearch; }, [webSearch]);
  useEffect(() => { attachmentRef.current = attachment; }, [attachment]);

  // Close model menu on outside click
  useEffect(() => {
    if (!showModelMenu) return;
    const handler = () => setShowModelMenu(false);
    setTimeout(() => document.addEventListener('click', handler), 0);
    return () => document.removeEventListener('click', handler);
  }, [showModelMenu]);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
    }
  }, [prompt]);

  const isOutlinePhase = flowPhase === 'generating-outline' || flowPhase === 'outline-ready';
  // "Generate straight away" means the outline streams INVISIBLY — the phase
  // machine is untouched (auto-fire and variations still work), but the
  // outline panel only renders when the user asked to review it. The
  // variations carve-out is mandatory: with variations pending, the panel's
  // Generate button is the only trigger for startVariationGeneration.
  const outlineReviewVisible = isOutlinePhase && (
    !skipOutlinePreview || (answers?.wantVariations === 'yes' && selectedVariationIndex === null)
  );
  const isVariationPhase = flowPhase === 'generating-variations' || flowPhase === 'choosing-variation';

  // ── Show next questionnaire question ──
  const showNextQuestion = useCallback((qIndex, currentAnswers) => {
    const questions = getQuestions(currentAnswers, projectType, brandIdentitiesRef.current, usageData);
    // A saved template locks the visual design — this static fallback must not
    // re-ask the imagery question the dynamic clarifier suppresses.
    const designLocked = !!(currentAnswers.nativeTemplateId || currentAnswers.deckTemplateId);
    let idx = qIndex;
    while (idx < questions.length && (
      (currentAnswers[questions[idx].id] && questions[idx].type !== 'logo-upload')
      || (designLocked && questions[idx].id === 'imageryMode')
    )) {
      idx++;
    }
    if (idx < questions.length) {
      const q = questions[idx];
      setQuestionIndex(idx);
      setMessages(prev => [...prev, {
        role: 'bot',
        text: q.botMessage,
        options: q.options,
        questionId: q.id,
        questionType: q.type || 'options',
        placeholder: q.placeholder,
        dropdownOptions: q.dropdownOptions,
      }]);
    } else {
      // Dashboard: skip outline, go straight to workspace
      if (projectType === 'dashboard') {
        const flowConfig = {
          projectType: 'dashboard',
          generationMode: 'dashboard-html',
          htmlModel: currentAnswers.htmlModel || getDefaultModel(),
          prompt: submittedPromptRef.current || submittedPrompt || prompt,
          language: currentAnswers.language || 'auto',
          dashboardTheme: currentAnswers.dashboardTheme || 'dark-analytics',
          dashboardLayout: currentAnswers.dashboardLayout || 'balanced',
          chartPreference: currentAnswers.chartPreference || 'mixed',
          logoUrl,
          webResearch,
          excelData: attachmentRef.current?.excelData || null,
          googleSheetUrl: currentAnswers.googleSheetUrl || null,
          refreshInterval: 60,
          brandIdentityId: (currentAnswers.brandIdentity && currentAnswers.brandIdentity !== 'none') ? currentAnswers.brandIdentity : null,
          runQa: currentAnswers.runQa || 'yes',
          // OneDrive source info for live data refresh (like Google Sheets URL)
          driveSourceInfo: attachmentRef.current?.driveSourceInfo || null,
          designReferenceImages: collectDesignReferenceImageUrls(),
        };
        // Audience folds into specialInstructions — that's the channel the
        // dashboard/sheets workspaces actually send to the backend.
        const questionnaireData = {
          htmlModel: currentAnswers.htmlModel || getDefaultModel(),
          specialInstructions: [currentAnswers.specialInstructions, currentAnswers.audience ? `Audience: ${currentAnswers.audience}` : ''].filter(Boolean).join('\n'),
        };
        transitionToWorkspace(null, questionnaireData, flowConfig);
        return;
      }
      // Sheets: skip outline, go straight to workspace
      if (projectType === 'sheets') {
        const flowConfig = {
          projectType: 'sheets',
          generationMode: 'sheet-data',
          htmlModel: currentAnswers.htmlModel || getDefaultModel(),
          prompt: submittedPromptRef.current || submittedPrompt || prompt,
          language: currentAnswers.language || 'auto',
          excelData: attachmentRef.current?.excelData || null,
          generateSampleData: attachmentRef.current?.excelData ? 'no' : (currentAnswers.generateSampleData || 'yes'),
          brandIdentityId: (currentAnswers.brandIdentity && currentAnswers.brandIdentity !== 'none') ? currentAnswers.brandIdentity : null,
        };
        const questionnaireData = {
          htmlModel: currentAnswers.htmlModel || getDefaultModel(),
          specialInstructions: [currentAnswers.specialInstructions, currentAnswers.audience ? `Audience: ${currentAnswers.audience}` : ''].filter(Boolean).join('\n'),
        };
        transitionToWorkspace(null, questionnaireData, flowConfig);
        return;
      }
      // Document + preserve: skip the outline LLM call entirely. The pasted
      // content IS the deliverable copy; no need to generate sections. We
      // synthesize a single-section outline from the user's content and hand
      // off straight to the document HTML route, which respects preserve mode.
      if (projectType === 'document' && currentAnswers._intentValue === 'preserve') {
        const sourceContent = (submittedPromptRef.current || submittedPrompt || prompt || '').trim();
        const synthTitle = currentAnswers.documentType === 'one-pager'
          ? 'One-pager'
          : 'Document';
        const synthOutline = {
          title: synthTitle,
          slides: [{
            slide_number: 1,
            title: synthTitle,
            content: sourceContent,
            bullet_points: [],
            slide_type: 'body',
          }],
        };
        setOutlineTitle(synthTitle);
        setOutlineSlides(synthOutline.slides);
        setMessages(prev => [
          ...prev.map(m => m.chipState === 'running' ? { ...m, chipState: 'done' } : m),
          { role: 'bot', chipLabel: 'Content preserved — generating', chipState: 'running' },
        ]);
        // Build flowConfig and jump to workspace, mirroring handleGenerate's path
        const flowConfig = {
          projectType: 'document',
          generationMode: 'document-html',
          htmlModel: currentAnswers.htmlModel || getDefaultModel(),
          prompt: sourceContent,
          language: currentAnswers.language || 'auto',
          documentType: currentAnswers.documentType || null,
          documentOrientation: currentAnswers.documentOrientation || null,
          documentStyle: currentAnswers.documentStyle || 'corporate',
          contentRelationship: 'preserve',
          specialInstructions: currentAnswers.specialInstructions || '',
          logoUrl,
          webResearch,
          excelData: attachmentRef.current?.excelData || null,
          // Only flow text content + extracted images downstream when the
          // primary attachment is acting as content. If it's a design
          // reference (visual style only) or an embed asset (placed verbatim via
          // embedImages), passing its text as fileContent would leak it into the
          // generated copy.
          fileContent: (attachmentRef.current?.role === 'design_reference' || attachmentRef.current?.role === 'embed_asset') ? null : (attachmentRef.current?.content || null),
          fileImages: attachmentRef.current?.role === 'design_reference' ? null : getFileImages(),
          embedImages: collectEmbedImages(),
          brandIdentityId: (currentAnswers.brandIdentity && currentAnswers.brandIdentity !== 'none') ? currentAnswers.brandIdentity : null,
          runQa: currentAnswers.runQa || 'yes',
          designReferenceImages: collectDesignReferenceImageUrls(),
          imageryMode: currentAnswers.imageryMode || currentAnswers.imagery_mode || null,
          artDirection: currentAnswers.artDirection || currentAnswers.art_direction || null,
        };
        const questionnaireData = {
          htmlModel: currentAnswers.htmlModel || getDefaultModel(),
          specialInstructions: currentAnswers.specialInstructions || '',
          documentType: currentAnswers.documentType || null,
          audience: currentAnswers.audience || null,
          runQa: currentAnswers.runQa || 'yes',
          imageryMode: currentAnswers.imageryMode || currentAnswers.imagery_mode || null,
          artDirection: currentAnswers.artDirection || currentAnswers.art_direction || null,
        };
        transitionToWorkspace(synthOutline, questionnaireData, flowConfig);
        return;
      }
      // All questions answered — start outline generation
      const genLabel = projectType === 'infographic' ? 'infographic sections' : projectType === 'document' ? 'document outline' : 'outline';
      setMessages(prev => [
        ...prev.map(m => m.chipState === 'running' ? { ...m, chipState: 'done' } : m),
        { role: 'bot', chipLabel: `Generating ${genLabel}`, chipState: 'running', _outlineGenChip: true },
      ]);
      setFlowPhase('generating-outline');
      startOutlineGeneration(currentAnswers);
    }
  }, [projectType]); // eslint-disable-line react-hooks/exhaustive-deps

  // r131: the proposal-mode contract is VISIBLE — the user must be able to
  // trust that facts are never invented in this flow.
  useEffect(() => {
    if (!proposalMode) return;
    setMessages(prev => (prev.some(m => m._proposalBadge) ? prev : [{
      role: 'bot',
      _proposalBadge: true,
      text: 'Proposal mode: facts only. Team names, prices, certifications and references come from your documents and answers — anything missing becomes a clearly marked placeholder instead of being invented.',
    }, ...prev]));
  }, [proposalMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Build full conversation history for every LLM call ──
  // All classifier + generation endpoints get the complete session context.
  // No cap — modern model windows handle it, and the user's earlier intent
  // clarifications / topic answers / preference statements must survive.
  const buildConversationHistory = () => {
    return (messages || [])
      .filter(m => !m._intentReading && !m._prefsAnalyzing && !m._submitAnalyzing && !m._directProgress && !m.isLoading)
      .filter(m => m.role === 'user' || m.role === 'bot')
      .map(m => ({ role: m.role, text: (m.text || '').trim() }))
      .filter(m => m.text);
  };

  // ── Collect data-URLs from every design_reference attachment ──
  // Used by document, infographic, dashboard, and slide-direct routes as
  // visual style anchors (not content sources). Caps at 6 to keep payloads sane.
  const collectDesignReferenceImageUrls = () => {
    const urls = [];
    const allAtts = [
      attachmentRef.current || attachment,
      ...(extraAttachmentsRef.current?.length ? extraAttachmentsRef.current : extraAttachments),
    ].filter(Boolean);
    for (const att of allAtts) {
      if (att?.role !== 'design_reference') continue;
      if (att.imageData) urls.push(att.imageData);
      if (Array.isArray(att.pages)) {
        att.pages.slice(0, 8).forEach(p => { if (p?.image_data_url) urls.push(p.image_data_url); });
      }
    }
    return urls.slice(0, 8);
  };

  // ── r128: content page images for the OUTLINE step ──
  // Page RENDERS of content-role uploads ride with the outline request so the
  // model SEES tables, charts and layout instead of their flattened text
  // (see-vs-read). Sampled evenly when the doc exceeds the cap (first + last
  // pages always kept); labeled with the source page number so the model can
  // cross-reference the extracted text.
  const collectContentPageImageUrls = () => {
    const pages = getScopedContentPages().filter(p => p?.image_data_url);
    if (!pages.length) return null;
    const CAP = OUTLINE_PAGE_IMAGE_CAP;
    let picked = pages;
    if (pages.length > CAP) {
      picked = [];
      const seen = new Set();
      for (let i = 0; i < CAP; i++) {
        const idx = Math.round(i * (pages.length - 1) / (CAP - 1));
        if (!seen.has(idx)) { seen.add(idx); picked.push(pages[idx]); }
      }
    }
    return picked.map(p => ({ page: p.source_page || p.page, file: p.source_file || null, data_url: p.image_data_url }));
  };

  // ── Collect user images to EMBED VERBATIM (role === 'embed_asset') ──
  // The user uploaded an image and asked to place it on a slide as-is (a logo,
  // screenshot, photo, chart). Each becomes {slot_id, data_url, name,
  // description}; the backend stores it, picks the best slide via the outline,
  // and fills the matching <img data-user-asset> placeholder it tells the model
  // to emit. Distinct from design_reference (a style to imitate, not embedded).
  const collectEmbedImages = () => {
    const out = [];
    const allAtts = [
      attachmentRef.current || attachment,
      ...(extraAttachmentsRef.current?.length ? extraAttachmentsRef.current : extraAttachments),
    ].filter(Boolean);
    let slot = 0;
    for (const att of allAtts) {
      if (att?.role !== 'embed_asset') continue;
      if (att.type === 'image' && att.imageData) {
        out.push({
          slot_id: slot,
          data_url: att.imageData,
          name: att.name || `image ${slot + 1}`,
          description: att.content || '',   // vision-extracted text: placement + caption hint
        });
        slot += 1;
      }
    }
    return out.slice(0, 6);
  };

  // ── File images for the DOCUMENT path (embedded verbatim) ──
  // Combines images extracted from uploaded docs (PDF/PPTX page images) with
  // standalone images the user asked to embed (role==='embed_asset'). Both share
  // the {data_url, name} shape the backend document handler expects. The slide
  // path ignores this field (it uses embedImages); documents read it as
  // file_images and re-embed each verbatim.
  const getFileImages = () => {
    const imgs = [];
    const pageImgs = attachmentRef.current?.images;
    if (Array.isArray(pageImgs)) imgs.push(...pageImgs);
    for (const e of collectEmbedImages()) {
      imgs.push({ data_url: e.data_url, name: e.name });
    }
    return imgs.length ? imgs : null;
  };

  // ── Collect all content-role attachments in upload order ──
  // Primary first (when present and role=content), then extras in order. Used
  // by the direct pipeline and outline paths to merge multi-file uploads into
  // one source stream.
  const getAllContentAttachments = () => {
    const primary = attachmentRef.current || attachment;
    const extras = (extraAttachmentsRef.current?.length ? extraAttachmentsRef.current : extraAttachments) || [];
    const all = [];
    if (primary && primary.role === 'content') all.push(primary);
    for (const a of extras) { if (a && a.role === 'content') all.push(a); }
    // Fallback: if nothing has an explicit role yet (classify-intent hasn't
    // run), treat everything as content in upload order — EXCEPT files whose
    // role was explicitly decided as design_reference/embed_asset (r128: the
    // classifier's decision stands; a style deck must never become the
    // content source just because no content file exists).
    const isDecidedNonContent = (a) => a?.role === 'design_reference' || a?.role === 'embed_asset';
    if (all.length === 0) {
      if (primary && !isDecidedNonContent(primary)) all.push(primary);
      for (const a of extras) { if (a && !isDecidedNonContent(a)) all.push(a); }
    }
    return all;
  };

  // Merge pages from every content-role attachment into one ordered array.
  // Each page keeps its original per-file page number but gets a new global
  // position (1-based) so the generator can label them consistently in the
  // combined deck.
  const getMergedContentPages = () => {
    const merged = [];
    let globalIdx = 0;
    for (const att of getAllContentAttachments()) {
      if (!Array.isArray(att.pages)) continue;
      for (const p of att.pages) {
        globalIdx += 1;
        merged.push({
          ...p,
          page: globalIdx,                   // global page number across all files
          source_file: att.name,             // for debugging / per-page labels
          source_page: p.page,               // original page number within its source file
        });
      }
    }
    return merged;
  };

  // r99: the SOURCE-PAGE SCOPE the intent classifier extracted ("regenerate
  // the first 5 slides" on a 33-slide upload). The classifier always
  // understood it — its own interpretation said so — but nothing carried the
  // decision, so every page was extracted AND generated (live 11/08: 33
  // slides rendered for a 5-slide request). Code executes the LLM's
  // decision here; it never re-derives it.
  const pageScopeRef = useRef(null);
  // r176: the SOURCE slide's visual system as a theme lock (promise while the
  // vision read is in flight, then the lock string or '').
  const sourceThemeLockRef = useRef(null);
  // r180: proposal scoping — the in-flight scope call and its normalized result.
  const proposalScopePromiseRef = useRef(null);
  const proposalScopeRef = useRef(null);

  // Content pages the request actually covers. Sliced 1-based inclusive, then
  // renumbered so the produced deck is self-consistent (source_page keeps the
  // original position for per-page labels and debugging).
  const getScopedContentPages = () => {
    const all = getMergedContentPages();
    const sc = pageScopeRef.current;
    if (!sc || !all.length) return all;
    // A scope whose START is past the end is an UNUSABLE decision, not a
    // narrower one — clamping both bounds independently turned "slides 10-20"
    // on a 5-page file into a single slide built from page 5 (review P2).
    // Generation seams fail open.
    if (sc.from > all.length) {
      console.info(`[page-scope] scope starts past the end (${sc.from} > ${all.length}) — using the whole file`);
      return all;
    }
    const from = Math.max(1, Math.min(sc.from, all.length));
    const to = Math.min(all.length, Math.max(sc.to, from));
    if (from === 1 && to === all.length) return all;
    const sliced = all.slice(from - 1, to).map((p, i) => ({ ...p, page: i + 1 }));
    console.info(`[page-scope] using source pages ${from}-${to} of ${all.length} (${sliced.length} to generate)`);
    return sliced;
  };

  // Concatenate text content from every content-role attachment (upload order).
  // Each file gets labeled so the outline LLM can see the source boundaries.
  const getMergedContentText = () => {
    const atts = getAllContentAttachments().filter(a => (a.content || '').trim());
    if (atts.length === 0) return null;
    if (atts.length === 1) return atts[0].content;
    return atts.map(a => `=== FILE: ${a.name} ===\n${a.content}`).join('\n\n');
  };

  // Merge spreadsheet data from every content-role attachment. Each file's
  // excelData is an array of sheet objects; for multiple workbooks we concat the
  // sheets and prefix each sheet name with its source file so they stay
  // distinguishable downstream. Single-file uploads are returned unchanged so the
  // common case behaves exactly as before.
  const getMergedExcelData = () => {
    const atts = getAllContentAttachments().filter(a => Array.isArray(a.excelData) && a.excelData.length);
    if (atts.length === 0) return null;
    if (atts.length === 1) return atts[0].excelData;
    const merged = [];
    for (const a of atts) {
      for (const sheet of a.excelData) {
        merged.push({
          ...sheet,
          sheet_name: `${a.name} — ${sheet.sheet_name || 'Sheet'}`,
          source_file: a.name,
        });
      }
    }
    return merged;
  };

  // ── Handle prompt submission ──
  // No orchestrator call — classification happens inside the outline generation LLM call (post-questionnaire).
  // `clarification` is set when the user has answered an intent-disambiguation question; we skip the
  // ambiguity check on the re-entry and thread the clarification into downstream generation.
  const handleSubmit = async (clarification = null) => {
    if (!clarification && !prompt.trim() && !attachment) return;
    if (isSubmitInFlightRef.current) {
      toast.error("I'm still working on your last request. Give me a moment.");
      return;
    }
    isSubmitInFlightRef.current = true;
    setIsSubmitInFlight(true);
    try {
      if (isFileParsingRef.current || isFileParsing) {
        // r141: WAIT instead of bouncing — the bounce dead-ended the
        // homepage auto-submit (its 30s cap fired into this guard and the
        // submit was LOST) and made every early Enter classify blind, then
        // re-run: the exact duplicate rounds in the 26/08 logs. Waiting
        // INSIDE the in-flight latch so a second Enter during the wait
        // bounces instead of queueing a duplicate.
        toast.loading('Reading your file — starting right after...', { id: 'parse-wait' });
        // r141b: LOOP — a second drop overwrites the promise ref, so one
        // await can resolve while another batch still parses; the 150ms
        // tick also keeps a resolved-promise re-check from hot-spinning
        while (isFileParsingRef.current) {
          try { await (fileParsePromiseRef.current || Promise.resolve()); } catch { /* parse errors surface their own toast */ }
          if (isFileParsingRef.current) await new Promise(r => setTimeout(r, 150));
        }
        // one macrotask so React commits the parsed attachment state before
        // the classify payload is built from it
        await new Promise(r => setTimeout(r, 0));
        toast.dismiss('parse-wait');
      }
      await handleSubmitInner(clarification);
    } finally {
      isSubmitInFlightRef.current = false;
      setIsSubmitInFlight(false);
    }
  };

  const handleSubmitInner = async (clarification = null) => {

    const userMessage = clarification?.userMessage ?? prompt.trim();
    if (!clarification) {
      setPrompt('');
      setMessages(prev => [...prev, { role: 'user', text: userMessage }]);
    }

    // Status chip covering the entire submit pipeline (content-offer +
    // intent + prefs + clarifier). Marked done when we're about to show
    // the panel, the static questionnaire, or hand control back to the chat.
    setMessages(prev => [
      ...prev.map(m => m.chipState === 'running' ? { ...m, chipState: 'done' } : m),
      {
        role: 'bot',
        chipLabel: 'Analyzing your request',
        chipState: 'running',
        _submitAnalyzing: true,
      },
    ]);
    const clearSubmitLoader = () => setMessages(prev => prev.map(m =>
      m._submitAnalyzing && m.chipState === 'running' ? { ...m, chipState: 'done' } : m
    ));

    // ── Unified classification — content-offer + intent + preferences in ONE
    // backend call (/api/classify-unified) that fans out to three parallel
    // Haiku calls server-side. Replaces three serial round-trips (~2s vs
    // ~4-5s before the questionnaire appears). Results are checked in the
    // SAME order as the old chain to preserve every early-exit:
    // content-offer halt → intent ambiguity halt → prefs merged into
    // defaults further down.
    const recentHistory = buildConversationHistory();
    // r149: check the LIVE ref too — this closure can be born mid-parse
    // (homepage auto-submit) and resume after it, with the state snapshot
    // still null. The stale read sent "Of course! Paste the content" to a
    // user who had attached two documents.
    const runContentOffer = !clarification && flowPhase === 'prompt' && !attachment && !attachmentRef.current;

    let classifiedRelationship = null;
    let unifiedPrefs = null;
    const hasPages = !!(attachmentRef.current?.pages?.length) || !!(attachment?.pages?.length);
    console.log('[intent] pre-guard — hasAttachment=', !!attachment, 'hasAttachmentRef=', !!attachmentRef.current, 'pages=', attachmentRef.current?.pages?.length || attachment?.pages?.length || 0, 'promptLen=', userMessage.length);
    {
      try {
        let sampledImages = [];
        let allAttachmentsForClassify = [];
        if (!clarification) {
          // Sample pages from the merged content-attachments stream so Haiku
          // sees a representative cross-section even when the user uploaded
          // multiple files.
          const pagesSource = getMergedContentPages();
          if (pagesSource.length) {
            const allPages = pagesSource.filter(p => p.image_data_url);
            const sampleIndices = (() => {
              if (allPages.length <= 5) return allPages.map((_, i) => i);
              const step = (allPages.length - 1) / 4;
              return [0, Math.round(step), Math.round(step * 2), Math.round(step * 3), allPages.length - 1];
            })();
            sampledImages = sampleIndices.map(i => allPages[i].image_data_url);
          }
          // Standalone image attachments have no `pages` — include their imageData
          // directly so the classifier can SEE the image and judge embed_asset vs
          // design_reference vs content (otherwise it classifies blind from text).
          const standaloneImgs = [
            attachmentRef.current || attachment,
            ...(extraAttachmentsRef.current?.length ? extraAttachmentsRef.current : extraAttachments),
          ].filter(a => a && a.type === 'image' && a.imageData).map(a => a.imageData);
          if (standaloneImgs.length) {
            sampledImages = [...sampledImages, ...standaloneImgs].slice(0, 5);
          }

          setMessages(prev => [
            ...prev.map(m => m.chipState === 'running' ? { ...m, chipState: 'done' } : m),
            {
              role: 'bot',
              chipLabel: sampledImages.length
                ? `Reading ${attachmentRef.current?.name || attachment?.name || 'your file'}`
                : 'Understanding your request',
              chipState: 'running',
              _intentReading: true,
            },
          ]);

          // Build attachments metadata array for Haiku — primary + extras
          allAttachmentsForClassify = [
            ...(attachmentRef.current ? [attachmentRef.current] : (attachment ? [attachment] : [])),
            ...(extraAttachmentsRef.current?.length ? extraAttachmentsRef.current : extraAttachments),
          ].filter(Boolean).map(a => ({ name: a.name, type: a.type }));
        }

        // The preferences classifier can only resolve a brand NAMED in the
        // prompt against the brands list we send it — wait for the mount
        // fetch rather than classifying against [].
        try { await brandsReadyRef.current; } catch { /* fetch failed — classify brandless */ }
        // A fresh request re-derives its own page scope; a clarification
        // follow-up keeps the scope its original message established.
        if (!clarification) pageScopeRef.current = null;
        // r141b: a fresh submit must not leave a PREVIOUS prompt's prefs for
        // its clarification re-entry to reuse (the prefs leg can fail null
        // while intent still asks a question — the old stash then applied
        // the wrong brand silently)
        if (!clarification) unifiedPrefsRef.current = null;

        // r141: a clarification re-entry reuses the round-1 classification —
        // it re-POSTed the full fan-out with run_intent=false, burning
        // Haiku calls for data already in hand.
        let resp = null;
        if (clarification && unifiedPrefsRef.current) {
          unifiedPrefs = unifiedPrefsRef.current;
        } else {
          resp = await fetch(withBackend('/api/classify-unified'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              prompt: userMessage,
              message: userMessage,
              proposal_mode: !!proposalMode,
              conversation_history: recentHistory,
              run_content_offer: runContentOffer,
              run_intent: !clarification,
              page_images: sampledImages,
              file_name: attachmentRef.current?.name || attachment?.name || '',
              project_type: projectType,
              attachments: allAttachmentsForClassify,
              // Legacy single-file field for backwards compat with older deployments
              attachment_type: attachmentRef.current?.type || attachment?.type || '',
              // For the preferences classifier
              brands: brandIdentitiesRef.current.map(bi => ({ id: bi.id, name: bi.name })),
            }),
          });
        }

        setMessages(prev => prev.map(m =>
          m._intentReading && m.chipState === 'running' ? { ...m, chipState: 'done' } : m
        ));
        if (resp && resp.ok) {
          const unified = await resp.json();
          unifiedPrefs = unified.preferences || null;
          unifiedPrefsRef.current = unifiedPrefs || unifiedPrefsRef.current; // r141

          // 1) Content-offer halt (old order preserved)
          if (runContentOffer && unified.content_offer?.is_content_offer) {
            const typeLabel = PROJECT_TYPE_LABELS[projectType] || 'something great';
            clearSubmitLoader();
            setMessages(prev => [...prev, {
              role: 'bot',
              text: `Of course! Paste the content or upload your file — I'll turn it into a ${typeLabel} for you.`,
            }]);
            return; // Stay in 'prompt' phase, awaiting actual content
          }

          // 2) Intent — roles first, then ambiguity halts (old order preserved)
          const intent = (!clarification && unified.intent) ? unified.intent : null;
          if (intent) {
          // Apply Haiku-decided per-file roles FIRST — BEFORE the ambiguity
          // early-returns below. The classifier can flag the request ambiguous
          // (e.g. asking the topic) WHILE still deciding how to use the file;
          // if we returned first, a detected embed_asset / design_reference role
          // would be lost and the image would wrongly fall through as content.
          // r99: carry the source-page scope the classifier extracted, so
          // "the first 5 slides" actually limits what gets generated.
          {
            const sc = intent.source_page_scope;
            pageScopeRef.current = (sc && Number(sc.from) >= 1 && Number(sc.to) >= Number(sc.from))
              ? { from: Number(sc.from), to: Number(sc.to) }
              : null;
            if (pageScopeRef.current) {
              console.info('[page-scope] classifier scoped the request to source pages',
                pageScopeRef.current.from, '-', pageScopeRef.current.to);
            }
          }
          const purposes = intent.attachment_purposes || [];
          const purposeByName = {};
          for (const p of purposes) { if (p?.name) purposeByName[p.name] = p.purpose; }
          const singlePurpose = intent.attachment_purpose;
          if (attachmentRef.current) {
            // Safety net: if the classifier returns no role for a standalone
            // image, default to embed_asset rather than dropping it to no-role
            // (which would silently OCR it into content and never embed it).
            // A user who uploads a bare image almost always wants it placed.
            const imageDefault = attachmentRef.current.type === 'image' ? 'embed_asset' : null;
            const decided = purposeByName[attachmentRef.current.name] || singlePurpose || attachmentRef.current.role || imageDefault;
            if (decided) {
              setAttachment(prev => prev ? { ...prev, role: decided } : prev);
              attachmentRef.current = { ...attachmentRef.current, role: decided };
            }
          }
          if (extraAttachmentsRef.current?.length) {
            const updated = extraAttachmentsRef.current.map(a => {
              const decided = purposeByName[a.name] || a.role || null;
              return decided ? { ...a, role: decided } : a;
            });
            setExtraAttachments(updated);
            extraAttachmentsRef.current = updated;
          }
          // Mode-mismatch check-in (round 74): the words ask for a different
          // deliverable than the active mode (a one-pager prompt pasted into
          // presentation mode generated a deck — user report). Offer the
          // switch instead of silently producing the wrong thing.
          {
            const OUTPUT_TO_TYPE = {
              presentation: 'presentation', 'one-pager': 'document', document: 'document',
              infographic: 'infographic', dashboard: 'dashboard', sheet: 'sheets',
            };
            // r131b: proposal mode IS presentation-shaped by design — the
            // mismatch check-in would walk the user out of the mode (and
            // there is no way back in via switchWorkspaceType)
            const wantedType = proposalMode ? null : (OUTPUT_TO_TYPE[intent.intended_output] || null);
            if (wantedType && wantedType !== projectType && typeof onSwitchProjectType === 'function') {
              clearSubmitLoader();
              const wantedLabel = intent.intended_output === 'one-pager' ? 'one-pager'
                : (PROJECT_TYPE_LABELS[wantedType] || wantedType).toLowerCase();
              const currentLabel = (PROJECT_TYPE_LABELS[projectType] || projectType).toLowerCase();
              setMessages(prev => [...prev, {
                role: 'bot',
                text: `This reads like a ${wantedLabel} request, but we're in ${currentLabel} mode. Which should I make?`,
                isIntentClarification: true,
                intentOptions: [
                  { label: `Make the ${wantedLabel}`, value: `__switch__:${wantedType}`, hint: `Switches to ${wantedLabel} mode and regenerates from this prompt` + (attachmentRef.current ? ' — re-attach your file after the switch' : '') },
                  { label: `Continue as ${currentLabel}`, value: '__keepmode__', hint: 'Generate in the current mode' },
                ],
                // relationship rides along so keep-mode re-entry preserves the
                // classifier's verdict (pasted one-pager copy = preserve; a
                // hardcoded 'generate' ordered a REWRITE of finished copy).
                intentContext: { userMessage, relationship: intent.content_relationship || null },
              }]);
              return;
            }
          }
          if (intent.is_ambiguous && intent.options?.length > 0) {
            // Tappable-option ambiguity (upload/generate, preserve/restructure, etc.)
            clearSubmitLoader();
            setMessages(prev => [...prev, {
              role: 'bot',
              text: intent.clarifying_question,
              isIntentClarification: true,
              intentOptions: intent.options,
              intentContext: { userMessage },
            }]);
            return; // wait for user to pick an option — resume via handleIntentAnswer
          }
          if (intent.is_ambiguous && intent.clarifying_question) {
            // Freetext ambiguity — model is asking for details the user must type
            // (e.g. "What's your pitch deck about?"). Show the bot question and
            // pause. User's next message will re-enter handleSubmit; classify-
            // intent will see it along with the full conversation history.
            clearSubmitLoader();
            setMessages(prev => [...prev, {
              role: 'bot',
              text: intent.clarifying_question,
            }]);
            return;
          }
            classifiedRelationship = intent.content_relationship || null;
          }
        }
      } catch { /* classification is best-effort; fall through */ }
    }

    setSubmittedPrompt(userMessage);
    submittedPromptRef.current = userMessage;

    // Start from any answers that already exist (re-entry after an outline
    // failure or a clarification round used to wipe everything and re-ask).
    // handleSubmitInner is recreated per render, so `answers` is fresh.
    const defaults = { ...(answers || {}), _hasExcelAttachment: !!attachmentRef.current?.excelData };
    // Generation-mode question is retired — Intelligent (HTML) is the platform
    // default. Seeded BEFORE the prompt-prefs merge, so a prompt that
    // explicitly asks for AI-image slides still overrides to 'ai'.
    if (!defaults.generationMode) defaults.generationMode = 'html';

    // Thread the classification (from either clarification or auto-classify) into
    // downstream generation via answers, so handleGenerate / outline-stream can
    // use it as an authoritative directive.
    const effectiveRelationship = clarification?.value || classifiedRelationship || null;
    if (effectiveRelationship) {
      defaults._intentValue = effectiveRelationship;
    }

    // Thread user's intent clarification into special_instructions so the outline LLM
    // respects it (preserve vs rewrite vs summarize, etc.)
    if (clarification) {
      const relBlurb = {
        preserve: 'The user wants their source content preserved VERBATIM. Do not rewrite or paraphrase. Keep titles, body text, and bullet points exactly as they appear in the source. Only design/layout should change.',
        restructure: 'The user wants the source structure preserved (same sections, same order) but the content rewritten for presentation.',
        summarize: 'The user wants the source content condensed into fewer slides while preserving key facts and data points.',
        enhance: 'The user wants the source content rewritten to be more compelling while preserving the core message and data.',
        generate: 'The user wants new content generated from the topic — the uploaded file is background context, not verbatim source.',
      }[clarification.value] || clarification.hint || '';
      defaults.specialInstructions = relBlurb;
      defaults._intentValue = clarification.value;
      // Remember the clarification so handleGenerate can route to the direct
      // pipeline after the questionnaire completes. Questionnaire still runs
      // normally so the user picks brand, model, variations, etc.
    }

    // Smart Google Sheet URL detection from the prompt — skip asking the question later
    if (projectType === 'dashboard') {
      const sheetMatch = userMessage.match(/https:\/\/docs\.google\.com\/spreadsheets\/d\/[^\s"'<>]+/i);
      if (sheetMatch) {
        defaults.googleSheetUrl = sheetMatch[0];
      }
    }

    // Auto-apply default brand identity if set (LLM detection may override below)
    const autoApplyBrand = brandIdentitiesRef.current.find(bi => bi.is_default && bi.auto_apply);
    if (autoApplyBrand) {
      defaults.brandIdentity = autoApplyBrand.id;
    }

    // Inline controls chosen on the home prompt (model/brand) are an explicit
    // user choice: they win over the auto-applied default brand, but are still
    // overridden by anything the LLM extracts from the prompt text below.
    if (initialAnswers) Object.assign(defaults, initialAnswers);

    // Prompt-preferences from the unified classification (fetched above, in
    // parallel with content-offer + intent — no extra round-trip). Applied
    // HERE, after initialAnswers, so precedence is unchanged: inline
    // home-prompt controls are overridden by anything the LLM extracted
    // from the prompt text.
    {
      const prefs = unifiedPrefs;
      if (prefs) {
        if (prefs.brand_id) defaults.brandIdentity = prefs.brand_id;
        // Belt for the classifier missing a brand it was handed (or being
        // handed none at all through a fetch race / deploy skew): the prompt
        // NAMES a saved brand, so resolve it here by name. Without this the
        // name survives only as prose in special_instructions and the deck
        // ships unbranded — the loudest possible failure for a brand tool.
        if (!prefs.brand_id && !defaults.brandIdentity) {
          const _hay = `${userMessage || ''} ${prefs.special_instructions || ''}`.toLowerCase();
          // WORD-BOUNDARY match, not a substring scan (review P1): `includes`
          // made "Nova" match "in-nova-tion", "Core" match "s-core-card" and
          // "Pro" match "pro-posal" — 12/12 false positives on realistic
          // brand-kit names, silently generating under the WRONG brand. Same
          // mistake the r98b review caught in the PDF filename classifier.
          const _esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const _named = (brandIdentitiesRef.current || [])
            .filter(bi => bi?.id && String(bi.name || '').trim().length >= 3)
            .filter(bi => new RegExp(`(^|[^a-z0-9])${_esc(String(bi.name).trim().toLowerCase())}([^a-z0-9]|$)`).test(_hay))
            .sort((a, b) => String(b.name).length - String(a.name).length)[0];
          if (_named) {
            defaults.brandIdentity = _named.id;
            console.info('[BRAND] resolved by name from the prompt:', _named.name);
          }
        }
        // brand_suggested_id is NOT auto-applied — it's stashed so the clarifier
        // can frame the brand question as a confirmation (smart-but-explicit).
        if (prefs.brand_suggested_id) defaults._brandSuggestedId = prefs.brand_suggested_id;
        if (prefs.html_model) defaults.htmlModel = prefs.html_model;
        if (prefs.generation_mode) defaults.generationMode = prefs.generation_mode;
        // Prompt-extracted variation prefs must not override a native-master
        // seed (variations are HTML designs — meaningless for native decks).
        if (prefs.want_variations != null && !defaults.nativeTemplateId) defaults.wantVariations = prefs.want_variations ? 'yes' : 'no';
        if (prefs.variation_count && !defaults.nativeTemplateId) defaults.variationCount = String(prefs.variation_count);
        if (prefs.document_subtype) defaults.documentType = prefs.document_subtype;
        if (prefs.document_orientation) defaults.documentOrientation = prefs.document_orientation;
        // Imagery / QA / outline-preview stated in the prompt count as answers
        // (previously unextracted, so "no quality check, skip the outline"
        // still got asked in chat).
        if (prefs.imagery_mode) defaults.imageryMode = prefs.imagery_mode;
        // r175: the user wants the ATTACHED file's own look kept ("same theme
        // and branding") — the attachment IS the visual direction, so the
        // theme picker and the brand picker are redundant (the redesign image
        // carries the look). The LLM decided; the code executes the suppression.
        if (prefs.keep_source_design === true) defaults._keepSourceDesign = true;
        if (prefs.run_qa != null) defaults.runQa = prefs.run_qa ? 'yes' : 'no';
        if (prefs.outline_preview) {
          defaults.outlinePreview = prefs.outline_preview;
          // The static question's handler normally syncs this flow toggle —
          // seeding skips the question, so sync it here too.
          const _skip = prefs.outline_preview !== 'review';
          setSkipOutlinePreview(_skip);
          try { localStorage.setItem('skipOutlinePreview', _skip ? 'true' : 'false'); } catch { /* ignore */ }
        }
        if (prefs.special_instructions && !(defaults.specialInstructions || '').includes(prefs.special_instructions)) {
          // Merge with any clarification-derived instructions so both survive
          // (the .includes guard stops double-accumulation on re-entry).
          defaults.specialInstructions = defaults.specialInstructions
            ? `${defaults.specialInstructions}\n\n${prefs.special_instructions}`
            : prefs.special_instructions;
        }
        // Auto web research (round 68): the classifier decided this prompt
        // names a real-world subject that needs grounding (the SPARK deck
        // came out fact-free with search off). Visible + reversible: never
        // overrides an explicit globe click, never fires with attached
        // source material (the outline call also force-disables it then).
        // Only for types whose generation actually RUNS the research (the
        // outline pre-research step) — dashboards/sheets never reach it,
        // and an auto-on notice there would claim research that never runs.
        if (prefs.web_research === 'needed'
            && ['presentation', 'infographic', 'document'].includes(projectType)
            && !webSearchUserSetRef.current
            && !webSearchRef.current
            && !(attachmentRef.current?.content)) {
          setWebSearch(true);
          // The notice posts at GENERATION time (round 72): posting it here
          // put it ABOVE the clarifier questions, describing a step that
          // happens minutes later. The globe still lights up immediately.
          webSearchAutoNoticeRef.current = true;
        }
      }
    }

    // A design-reference upload IS the visual direction — the backend clarifier
    // already skips the brand question in that case; mirror it here so the
    // static questionnaire doesn't re-ask. Fires only when nothing else set a
    // brand (auto-apply, home picker, and prompt-named brands all win).
    {
      const _allAtts = [
        attachmentRef.current || attachment,
        ...(extraAttachmentsRef.current?.length ? extraAttachmentsRef.current : extraAttachments),
      ].filter(Boolean);
      // r175: a redesign that keeps the source's own look is the same case —
      // the attachment is the brand direction.
      if ((_allAtts.some(a => a?.role === 'design_reference') || defaults._keepSourceDesign) && !defaults.brandIdentity) {
        defaults.brandIdentity = 'none';
      }
    }

    setAnswers(defaults);

    const typeLabel = PROJECT_TYPE_LABELS[projectType] || 'presentation';
    // Truncate for display — don't echo back 15K chars
    const displayPrompt = userMessage.length > 100 ? userMessage.slice(0, 97) + '...' : userMessage;

    defaults.textStyle = 'balanced';
    setAnswers(defaults);

    // Dynamic clarifier — SSE stream. Open the panel immediately with an
    // empty question list + streaming flag, then append each question as
    // its event arrives. If the stream returns zero questions or fails, fall
    // back to the static questionnaire.
    const allAttsForPurpose = [
      attachmentRef.current || attachment,
      ...(extraAttachmentsRef.current?.length ? extraAttachmentsRef.current : extraAttachments),
    ].filter(Boolean);
    const attachmentPurposes = allAttsForPurpose.map(a => ({
      name: a.name,
      purpose: a.role === 'design_reference' ? 'design_reference'
        : a.role === 'embed_asset' ? 'embed_asset'
        : 'content',
    }));
    const alreadyAnswered = {};
    // Preferences the user already stated are forwarded to the clarifier (below) so
    // it skips those questions. An explicitly-named brand (defaults.brandIdentity)
    // and a stated imagery preference (defaults.specialInstructions, e.g. "no
    // imagery") go through as resolved_brand_id / special_instructions; the backend
    // folds them into already_answered and suppresses the brand + imagery questions.
    // A brand merely *mentioned* in pasted content (defaults._brandSuggestedId) still
    // surfaces as a one-click confirm instead of being auto-applied.
    if (defaults.htmlModel) alreadyAnswered.html_model = defaults.htmlModel;
    if (defaults.brandIdentity) alreadyAnswered.resolved_brand_id = defaults.brandIdentity;
    if (defaults.imageryMode) alreadyAnswered.imagery_mode = defaults.imageryMode;
    // r175: "keep the source's design" answers the visual-direction question —
    // the backend's suppressed-ids gate drops the theme picker for this key.
    // r180: proposal scoping runs alongside the clarifier stream — the RFP (or
    // the typed brief when nothing is attached) yields the planned questions
    // and the requirements checklist. Fail-open: no scope = generate anyway.
    proposalScopePromiseRef.current = null;
    proposalScopeRef.current = null;
    if (proposalMode) {
      const _srcText = (getMergedContentText() || '').slice(0, 60000);
      // r181: the bearer identifies the tenant — without it the backend serves
      // no fact sheet (withheld, never guessed). The default brand, if any,
      // lets the sheet keep answered requirements out of the fill-in rows.
      const _scopeBrand = (defaults.brandIdentity && defaults.brandIdentity !== 'none') ? defaults.brandIdentity : null;
      proposalScopePromiseRef.current = (async () => {
        const r = await fetch(withBackend('/api/proposal/scope'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
          body: JSON.stringify({ source_text: _srcText, prompt: userMessage, source_kind: _srcText ? 'rfp' : 'direct', brand_identity_id: _scopeBrand }),
        });
        return r.ok ? r.json() : null;
      })().catch(() => null);
    }
    if (defaults._keepSourceDesign) {
      alreadyAnswered.theme_direction = 'source';
      // r176: read the SOURCE slide's visual system while the clarifier
      // streams — it becomes the deck's theme lock (the same plumbing a
      // picked direction uses), so the original's palette and type are
      // PINNED from slide 1 instead of merely suggested by a reference image.
      const _srcImg = (attachmentRef.current || attachment)?.imageData;
      if (_srcImg && sourceThemeLockRef.current == null) {
        sourceThemeLockRef.current = fetch(withBackend('/api/theme-from-image'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ image: _srcImg }),
        })
          .then(r => (r.ok ? r.json() : null))
          .then(d => { sourceThemeLockRef.current = (d && d.theme_lock) || ''; })
          .catch(() => { sourceThemeLockRef.current = ''; });
      }
    }
    if (defaults.generationMode) alreadyAnswered.generation_mode = defaults.generationMode;
    if (defaults.wantVariations) alreadyAnswered.want_variations = defaults.wantVariations;
    if (defaults.variationCount) alreadyAnswered.variation_count = defaults.variationCount;
    if (defaults.runQa) alreadyAnswered.run_qa = defaults.runQa;
    if (defaults.outlinePreview) alreadyAnswered.outline_preview = defaults.outlinePreview;

    // Deterministic duplicate gate: never show a streamed clarifier question
    // whose answer we already hold — prompt-level suppression alone leaks
    // duplicates when the LLM ignores instructions (the reported
    // asked-brand-and-mode-twice bug), and this also covers deploy skew
    // against older backends.
    const CLARIFIER_TO_ANSWER_KEY = {
      brand_identity: 'brandIdentity',
      html_model: 'htmlModel',
      generation_mode: 'generationMode',
      imagery_mode: 'imageryMode',
      want_variations: 'wantVariations',
      variation_count: 'variationCount',
      infographic_size: 'infographicSize',
      export_target: 'exportTarget',
      run_qa: 'runQa',
      outline_preview: 'outlinePreview',
    };
    const questionAlreadyAnswered = (q) => {
      const k = CLARIFIER_TO_ANSWER_KEY[q?.id];
      return !!(k && defaults[k]);
    };

    let streamedQuestions = [];
    let streamedRationale = '';
    let streamErrored = false;
    let panelOpened = false;
    try {
      const cResp = await fetch(withBackend('/api/generate-clarifying-questions-stream'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: userMessage,
          proposal_mode: !!proposalMode,
          project_type: projectType,
          brands: brandIdentitiesRef.current.map(bi => ({ id: bi.id, name: bi.name })),
          has_attachment: !!(attachmentRef.current || attachment) || (extraAttachmentsRef.current?.length > 0),
          attachment_purposes: attachmentPurposes,
          conversation_history: buildConversationHistory(),
          already_answered: alreadyAnswered,
          suggested_brand_id: defaults._brandSuggestedId || null,
          // Resolved upstream by classify-prompt-preferences; lets the clarifier
          // skip the brand + imagery questions when the user already stated them.
          resolved_brand_id: defaults.brandIdentity || null,
          special_instructions: defaults.specialInstructions || '',
          // r139: the theme proposer reads the uploaded material too — a file
          // that DESCRIBES brand guidelines becomes the first proposed
          // direction instead of losing to topic-derived themes.
          attachment_excerpt: allAttsForPurpose.map(a => a?.content || '').filter(Boolean).join('\n\n').slice(0, 4000),
          // A selected saved template (native PPTX master or deck template) IS
          // the visual design — the clarifier must ask content questions only.
          design_locked: !!(defaults.nativeTemplateId || defaults.deckTemplateId),
        }),
      });

      if (!cResp.ok || !cResp.body) {
        throw new Error(`clarifier stream returned ${cResp.status}`);
      }

      const reader = cResp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';
        for (const raw of lines) {
          const line = raw.trim();
          if (!line.startsWith('data:')) continue;
          const payload = line.slice(5).trim();
          if (!payload) continue;
          let evt;
          try { evt = JSON.parse(payload); } catch { continue; }
          if (evt.type === 'question' && evt.question) {
            // Drop questions whose answers we already hold (see gate above).
            if (questionAlreadyAnswered(evt.question)) continue;
            // First question: open the panel with an empty list and the
            // streaming flag set, THEN append. Switching phase before any
            // questions arrive would flash an empty panel.
            if (!panelOpened) {
              clearSubmitLoader();
              setClarifyingPayload({ questions: [], rationale: '', defaults, streaming: true });
              setFlowPhase('clarifying-questions');
              panelOpened = true;
            }
            streamedQuestions.push(evt.question);
            const questionsSoFar = [...streamedQuestions];
            setClarifyingPayload(prev => ({ ...prev, questions: questionsSoFar, streaming: true }));
          } else if (evt.type === 'complete') {
            streamedRationale = evt.rationale || '';
          } else if (evt.type === 'error') {
            streamErrored = true;
          }
        }
      }
    } catch (e) {
      streamErrored = true;
    }

    // r180: merge the proposal scoping rows into the SAME panel before it
    // settles (bounded wait — the scope call ran concurrently with the stream).
    if (proposalMode && proposalScopePromiseRef.current) {
      try {
        const scope = await Promise.race([
          proposalScopePromiseRef.current,
          new Promise(res => setTimeout(() => res(null), 8000)),
        ]);
        if (scope && scope.ok !== false) {
          proposalScopeRef.current = scope;
          const scopeQs = buildScopeQuestions(scope);
          if (scopeQs.length) {
            streamedQuestions = [...streamedQuestions, ...scopeQs];
            if (!panelOpened) {
              clearSubmitLoader();
              setClarifyingPayload({ questions: [], rationale: '', defaults, streaming: true });
              setFlowPhase('clarifying-questions');
              panelOpened = true;
            }
          }
        }
      } catch { /* scoping is optional — generate anyway */ }
    }

    if (panelOpened) {
      // Stream finished — flip the streaming flag off so the loading
      // indicator disappears and the Continue button enables when ready.
      setClarifyingPayload(prev => ({
        ...prev,
        questions: streamedQuestions,
        rationale: streamedRationale,
        streaming: false,
      }));
      return;
    }

    if (streamedQuestions.length > 0) {
      // Stream produced questions but the panel never opened (race) — open
      // it now in non-streaming state.
      clearSubmitLoader();
      setClarifyingPayload({ questions: streamedQuestions, rationale: streamedRationale, defaults, streaming: false });
      setFlowPhase('clarifying-questions');
      return;
    }

    // Stream errored or returned zero questions — fall back to the static
    // sequential questionnaire so generation still proceeds.
    if (streamErrored) console.warn('[CLARIFIER] stream failed, falling back to static questionnaire');
    clearSubmitLoader();
    setMessages(prev => [...prev, { role: 'bot', text: `Got it! One quick question before I start.` }]);
    setFlowPhase('questionnaire');
    setQuestionIndex(0);
    setTimeout(() => showNextQuestion(0, defaults), 400);
  };

  // ── Map dynamic clarifier answers back into the questionnaire shape ──
  // Translates known IDs (brand_identity → brandIdentity, html_model → htmlModel)
  // and dumps the rest into specialInstructions so downstream prompts see them.
  const mergeClarifierAnswers = (defaults, rawAnswers) => {
    const merged = { ...defaults };
    const directionLines = [];
    const KNOWN = {
      brand_identity: 'brandIdentity',
      html_model: 'htmlModel',
      generation_mode: 'generationMode',
      want_variations: 'wantVariations',
      variation_count: 'variationCount',
      language: 'language',
      audience: 'audience',
      output_subtype: 'documentType',
      // The clarifier prompt's canonical id — was missing, so subtype
      // answers folded into specialInstructions as loose text.
      document_subtype: 'documentType',
      document_type: 'documentType',
      document_orientation: 'documentOrientation',
      orientation: 'documentOrientation',
      visual_style: 'visualStyle',
      text_style: 'textStyle',
      imagery_mode: 'imageryMode',
      art_direction: 'artDirection',
      infographic_size: 'infographicSize',
      export_target: 'exportTarget',
      run_qa: 'runQa',
      outline_preview: 'outlinePreview',
    };
    for (const [qid, val] of Object.entries(rawAnswers || {})) {
      if (val == null || val === '') continue;
      if (val === DECIDE_FOR_ME_VALUE) continue;
      const mappedKey = KNOWN[qid];
      if (mappedKey) {
        // For multi-select, join into a comma-separated string
        if (Array.isArray(val)) {
          const cleaned = val.filter(v => v && v !== DECIDE_FOR_ME_VALUE);
          if (cleaned.length > 0) merged[mappedKey] = cleaned.join(',');
        } else if (typeof val === 'string' && val.startsWith('other:')) {
          merged[mappedKey] = val.slice(6);
        } else {
          merged[mappedKey] = val;
        }
        continue;
      }
      // Unknown question → fold into specialInstructions
      const labelGuess = qid.replace(/_/g, ' ');
      if (Array.isArray(val)) {
        const cleaned = val.filter(v => v && v !== DECIDE_FOR_ME_VALUE);
        if (cleaned.length > 0) directionLines.push(`${labelGuess}: ${cleaned.join(', ')}`);
      } else if (typeof val === 'number') {
        directionLines.push(`${labelGuess}: ${val}`);
      } else if (typeof val === 'string') {
        const v = val.startsWith('other:') ? val.slice(6) : val;
        if (v) directionLines.push(`${labelGuess}: ${v}`);
      }
    }
    if (directionLines.length > 0) {
      const block = directionLines.join('\n');
      merged.specialInstructions = merged.specialInstructions
        ? `${merged.specialInstructions}\n\n${block}`
        : block;
    }
    // wantVariations expects 'yes'|'no' string for the static questionnaire
    if (typeof merged.wantVariations === 'boolean') {
      merged.wantVariations = merged.wantVariations ? 'yes' : 'no';
    }
    // r176: "keep the source's design" resolves to the source-derived theme
    // lock (brand still wins — applying both would fight over the palette).
    // Covers the panel submit AND the pre-answered/static paths, which never
    // pass through handleClarifyingSubmit's picked-theme resolution.
    const _stl = sourceThemeLockRef.current;
    if (!merged.customThemeRequest && merged._keepSourceDesign
        && (!merged.brandIdentity || merged.brandIdentity === 'none')
        && typeof _stl === 'string' && _stl) {
      merged.customThemeRequest = _stl;
    }
    return merged;
  };

  const handleClarifyingSubmit = async (rawAnswers) => {
    const baseDefaults = clarifyingPayload.defaults || answers || {};
    // r180: lift the proposal-scoping answers out BEFORE the generic merge —
    // unknown ids would otherwise fold into specialInstructions as loose text.
    const scopeAnswers = {};
    if (rawAnswers) {
      for (const k of Object.keys(rawAnswers)) {
        if (k.startsWith('pq_') || k.startsWith('preq_')) { scopeAnswers[k] = rawAnswers[k]; delete rawAnswers[k]; }
      }
    }
    // Theme direction (round 70): resolve the picked card into the option's
    // THEME LOCK string BEFORE the generic merge — an unmapped id would
    // otherwise fold into special instructions as loose text.
    let themeLock = '';
    if (rawAnswers?.theme_direction && rawAnswers.theme_direction !== DECIDE_FOR_ME_VALUE) {
      const tq = (clarifyingPayload.questions || []).find(q => q.id === 'theme_direction');
      const topt = tq?.options?.find(o => o.value === rawAnswers.theme_direction);
      if (topt?.theme_lock) themeLock = topt.theme_lock;
    } else {
      // r139/r140b: no explicit pick, a blank submit, or "decide for me" —
      // all resolve to the document-described brand system when one exists
      // (the Qahwa BLK run picked a random topic theme over the branding
      // file the user uploaded specifically to be used). The panel also
      // pre-selects it via `suggested`, but a blank submit must not drop it.
      const tq = (clarifyingPayload.questions || []).find(q => q.id === 'theme_direction');
      const bopt = tq?.options?.find(o => o.from_brand_doc && o.theme_lock);
      if (bopt) themeLock = bopt.theme_lock;
    }
    if (rawAnswers && 'theme_direction' in rawAnswers) delete rawAnswers.theme_direction;
    const merged = mergeClarifierAnswers(baseDefaults, rawAnswers);
    // r180: freeze the scoping block ONCE (snapshot discipline) and ride it
    // into special instructions — the outline, every slide and every edit
    // re-read the same frozen text. Fail-open: no block, generate anyway.
    if (proposalMode && proposalScopeRef.current) {
      try {
        // r181: the chosen brand's approved fact sheet is folded into the
        // frozen block server-side (tenant from the bearer; expired withheld).
        const _freezeBrand = (merged.brandIdentity && merged.brandIdentity !== 'none') ? merged.brandIdentity : null;
        const fr = await fetch(withBackend('/api/proposal/freeze'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
          body: JSON.stringify({
            scope: proposalScopeRef.current, answers: scopeAnswers, extra_notes: '', brand_identity_id: _freezeBrand,
            // r182: ranking context for the past-proposal digest (client, gist, RFP text)
            context_text: [proposalScopeRef.current?.client_name, proposalScopeRef.current?.gist, (getMergedContentText() || '').slice(0, 20000)].filter(Boolean).join('\n'),
          }),
        });
        const fd = fr.ok ? await fr.json() : null;
        const block = fd?.frozen_block || '';
        merged.proposalScope = { ...proposalScopeRef.current, answers: scopeAnswers, frozen_block: block, post_draft_facts: [] };
        if (block) merged.specialInstructions = [merged.specialInstructions, block].filter(Boolean).join('\n\n');
      } catch { /* scoping is optional */ }
    }
    // Brand wins over a picked theme: applying both would fight over the
    // palette (the backend only offers the question brandless, but a brand
    // can still be chosen in this same panel).
    if (themeLock && (!merged.brandIdentity || merged.brandIdentity === 'none')) {
      merged.customThemeRequest = themeLock;
    }
    // Safety net: if Haiku emitted a brand NAME as the value, look up its id.
    // The endpoint instructs Haiku to use ids, but defensive parsing keeps
    // brand application working if the model deviates.
    if (merged.brandIdentity && merged.brandIdentity !== 'none') {
      const brands = brandIdentitiesRef.current || [];
      const isKnownId = brands.some(b => b.id === merged.brandIdentity);
      if (!isKnownId) {
        const byName = brands.find(b => (b.name || '').toLowerCase() === String(merged.brandIdentity).toLowerCase());
        if (byName) merged.brandIdentity = byName.id;
        else delete merged.brandIdentity;
      }
    }
    // Validate canonical-vocabulary fields with plain equality. If the LLM
    // emits anything outside the canonical set we drop the value rather than
    // regex-massage it — the clarifier prompt is the source of truth.
    const validate = (v, allowed) => (typeof v === 'string' && allowed.includes(v)) ? v : null;
    const HTML_MODEL = ['flash', 'pro', 'sonnet', 'opus', 'sol', 'astra', 'kimi'];
    const GEN_MODE = ['html', 'ai'];
    const DOC_TYPE = ['one-pager', 'report', 'proposal', 'resume', 'policy', 'research'];
    const DOC_ORIENT = ['portrait', 'landscape', 'tabloid'];
    const IMAGERY_MODE = ['auto', 'illustrations', 'none'];
    if (merged.imageryMode != null) {
      // Case/whitespace tolerant + obvious synonyms. The old case-sensitive
      // check DELETED an LLM-capitalized "None", and the asked-but-unanswered
      // default below then rewrote the user's explicit "no images" to 'auto'
      // (production incident: photos on a no-images deck).
      const raw = String(merged.imageryMode).trim().toLowerCase();
      const synonyms = { 'no': 'none', 'no images': 'none', 'no imagery': 'none', 'typography-led': 'none', 'text-only': 'none', 'icons': 'illustrations', 'illustrations only': 'illustrations' };
      const v = validate(synonyms[raw] || raw, IMAGERY_MODE);
      if (v) merged.imageryMode = v; else delete merged.imageryMode;
    }
    if (merged.htmlModel != null) {
      const v = validate(merged.htmlModel, HTML_MODEL);
      if (v) merged.htmlModel = v; else delete merged.htmlModel;
    }
    if (merged.generationMode != null) {
      const v = validate(merged.generationMode, GEN_MODE);
      if (v) merged.generationMode = v; else delete merged.generationMode;
    }
    if (merged.documentType != null) {
      const v = validate(merged.documentType, DOC_TYPE);
      if (v) merged.documentType = v; else delete merged.documentType;
    }
    if (merged.documentOrientation != null) {
      const v = validate(merged.documentOrientation, DOC_ORIENT);
      if (v) merged.documentOrientation = v; else delete merged.documentOrientation;
    }
    if (merged.infographicSize != null) {
      const v = validate(merged.infographicSize, ['web', 'story', 'a4']);
      if (v) merged.infographicSize = v; else delete merged.infographicSize;
    }
    if (rawAnswers?.infographic_size === DECIDE_FOR_ME_VALUE && !merged.infographicSize) {
      merged.infographicSize = 'web';
    }
    if (merged.exportTarget != null) {
      const v = validate(merged.exportTarget, ['screen', 'pptx']);
      if (v) merged.exportTarget = v; else delete merged.exportTarget;
    }
    if (rawAnswers?.export_target === DECIDE_FOR_ME_VALUE && !merged.exportTarget) {
      merged.exportTarget = 'screen';
    }
    // Clarifier folds the variation COUNT into want_variations ('no'|'2'|'3'|'4')
    // so the panel asks ONE question; unfold to the questionnaire's shape.
    if (merged.wantVariations != null) {
      const raw = String(merged.wantVariations);
      if (['2', '3', '4'].includes(raw)) {
        merged.wantVariations = 'yes';
        merged.variationCount = raw;
      } else {
        const v = validate(raw, ['yes', 'no']);
        if (v) merged.wantVariations = v; else delete merged.wantVariations;
      }
    }
    if (merged.variationCount != null) {
      const v = validate(String(merged.variationCount), ['2', '3', '4']);
      if (v) merged.variationCount = v; else delete merged.variationCount;
    }
    if (merged.runQa != null) {
      const v = validate(merged.runQa, ['yes', 'no']);
      if (v) merged.runQa = v; else delete merged.runQa;
    }
    if (merged.outlinePreview != null) {
      const v = validate(merged.outlinePreview, ['skip', 'review']);
      if (v) merged.outlinePreview = v; else delete merged.outlinePreview;
    }
    // Dedupe: if the clarifier already ASKED about generation mode / imagery,
    // make sure that question carries a value so the static questionnaire
    // (showNextQuestion) skips it instead of re-asking. "Decide for me" and
    // validation-dropped answers would otherwise leave the key unset and the
    // user would be asked the same question a second time.
    // Key the dedupe on the questions actually ASKED, not just answered:
    // an untouched question in the collapsed details group submits no key,
    // and the old-style bubble would resurface for it.
    const clarifierAsked = [
      ...((clarifyingPayload.questions || []).map(q => q?.id).filter(Boolean)),
      ...Object.keys(rawAnswers || {}),
    ];
    if (clarifierAsked.includes('generation_mode') && !merged.generationMode) {
      merged.generationMode = 'html';
    }
    // NEVER fabricate 'auto' from an ANSWERED imagery question — this exact
    // line used to overwrite a real "no images" whose value failed the old
    // case-sensitive check. Default only when the user explicitly delegated
    // (decide-for-me), mirroring the brand/model delegation pattern below.
    if (clarifierAsked.includes('imagery_mode') && !merged.imageryMode
        && (rawAnswers?.imagery_mode === DECIDE_FOR_ME_VALUE || rawAnswers?.imagery_mode == null)) {
      merged.imageryMode = 'auto';
    }
    // Same dedupe for the flow questions now living in the clarifier: an
    // asked-but-dropped answer must not resurface as an old-style bubble.
    if (clarifierAsked.includes('run_qa') && !merged.runQa) {
      merged.runQa = 'yes';
    }
    if (clarifierAsked.includes('outline_preview') && !merged.outlinePreview) {
      merged.outlinePreview = 'skip';
    }
    if (clarifierAsked.includes('want_variations') && !merged.wantVariations) {
      merged.wantVariations = 'no';
    }
    // CRITICAL: the outline-review flow is DRIVEN by skipOutlinePreview
    // state + localStorage, not by answers.outlinePreview — the static
    // question's handleAnswer performs this sync, but the clarifier path
    // suppresses that question, so sync here too (mirrors the prefs-path
    // sync above). Without it, "Review the outline first" is silently
    // ignored and the deck auto-generates.
    if (merged.outlinePreview) {
      const _skipOutline = merged.outlinePreview !== 'review';
      setSkipOutlinePreview(_skipOutline);
      try { localStorage.setItem('skipOutlinePreview', _skipOutline ? 'true' : 'false'); } catch { /* private mode */ }
    }
    // "Decide for me" on brand / model / variations is a real answer — a
    // delegation to the system default. Convert it (checking the RAW value so
    // a validation-dropped garbage answer still falls through to a re-ask):
    // 'none' = AI-decided styling, 'flash' = the universal model fallback,
    // 'no' = generate directly.
    if (rawAnswers?.brand_identity === DECIDE_FOR_ME_VALUE && !merged.brandIdentity) {
      merged.brandIdentity = 'none';
    }
    if (rawAnswers?.html_model === DECIDE_FOR_ME_VALUE && !merged.htmlModel) {
      merged.htmlModel = 'flash';
    }
    if (rawAnswers?.want_variations === DECIDE_FOR_ME_VALUE && !merged.wantVariations) {
      merged.wantVariations = 'no';
    }
    setAnswers(merged);
    setMessages(prev => [
      ...prev.map(m => m.chipState === 'running' ? { ...m, chipState: 'done' } : m),
      { role: 'bot', chipLabel: 'Got it — generating', chipState: 'running' },
    ]);
    // Hand back to the existing questionnaire path; showNextQuestion auto-skips
    // any questions whose answers are already in `merged`, then proceeds to
    // outline / direct generation as before.
    setFlowPhase('questionnaire');
    setQuestionIndex(0);
    setTimeout(() => showNextQuestion(0, merged), 200);
  };

  const handleClarifyingCancel = () => {
    const baseDefaults = clarifyingPayload.defaults || answers || {};
    setMessages(prev => [...prev, { role: 'bot', text: `Got it! One quick question before I start.` }]);
    setFlowPhase('questionnaire');
    setQuestionIndex(0);
    setTimeout(() => showNextQuestion(0, baseDefaults), 200);
  };

  // ── Process initial file(s) from homepage ──
  // Supports multiple files (initialFiles) from the landing's multi-upload, with
  // a single-file fallback. handleFileSelect already batch-processes all files
  // and holds the parsing flag for the whole batch.
  useEffect(() => {
    const files = (Array.isArray(initialFiles) && initialFiles.length)
      ? initialFiles
      : (initialFile ? [initialFile] : []);
    if (files.length && !attachment) {
      const dt = new DataTransfer();
      files.forEach(f => { try { dt.items.add(f); } catch { /* ignore */ } });
      handleFileSelect({ target: { files: dt.files } });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-submit initial prompt from homepage ──
  // When files are attached, WAIT for parsing to finish before submitting rather
  // than racing a fixed timer — otherwise the clarifier runs before the file is
  // ready and the user sees a jarring "still analyzing" interruption.
  useEffect(() => {
    if (initialPrompt && !initialPromptFiredRef.current && flowPhase === 'prompt' && !savedFlowResult) {
      initialPromptFiredRef.current = true;
      // r141: handleSubmit now AWAITS the in-flight parse itself — the old
      // 200ms poll's 30s cap fired handleSubmit into the parsing guard and
      // LOST the submit (a 20-page deck takes 30-45s to extract), and its
      // interval survived unmounts, submitting from dead instances.
      const _t141 = setTimeout(() => handleSubmit(), 300);
      // r141b: reset the latch in cleanup — StrictMode's dev double-mount
      // otherwise clears the timer on pass 1 and skips pass 2 entirely
      return () => { clearTimeout(_t141); initialPromptFiredRef.current = false; };
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handle questionnaire answer ──
  const handleAnswer = (questionId, value, label) => {
    // Outline review is a flow toggle, not a generation param — apply it to the
    // skipOutlinePreview state (and persist) so the auto-skip effect respects it.
    if (questionId === 'outlinePreview') {
      const skip = value !== 'review';
      setSkipOutlinePreview(skip);
      try { localStorage.setItem('skipOutlinePreview', skip ? 'true' : 'false'); } catch { /* ignore */ }
    }
    const newAnswers = { ...answers, [questionId]: value };
    setAnswers(newAnswers);
    setMessages(prev => [...prev, { role: 'user', text: label }]);
    const updatedQuestions = getQuestions(newAnswers, projectType, brandIdentitiesRef.current, usageData);
    const currentQIdx = updatedQuestions.findIndex(q => q.id === questionId);
    const nextIdx = currentQIdx + 1;
    setTimeout(() => showNextQuestion(nextIdx, newAnswers), 300);
  };

  // ── Handle logo upload ──
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      if (userId) formData.append('user_id', userId);
      const response = await fetch(withBackend('/api/upload-logo'), { method: 'POST', body: formData });
      if (response.ok) {
        const data = await response.json();
        setLogoUrl(data.logo_url || data.url);
        handleAnswer('logoUpload', 'uploaded', `Logo: ${file.name}`);
      } else {
        toast.error('Failed to upload logo');
      }
    } catch (err) {
      toast.error('Failed to upload logo');
    }
  };

  // ── SSE outline generation ──
  // ── Early slide-1 pipelining ────────────────────────────────────────────
  // On skip-outline decks, fire slide 1's generation the moment its outline
  // entry streams in, then hand the result to the main generation run as a
  // pre-generated slide (existing backend machinery: pre_generated_slides →
  // pre_gen_map → deck style anchor). Saves the outline-tail wait (~10-25s).
  // The early call sends NO user_id: no usage row, no job, no phantom
  // project — the main run records all slides including this one.
  const earlySlide1Ref = useRef(null);

  const fireEarlySlide1 = (entry0, qa, streamTitle, researchContent) => {
    try {
      // EXACT same mapping CreatePPTWorkspace applies to outline slides —
      // any drift here shows up as slide-1 style mismatch or a failed
      // staleness check at consumption.
      const mapped = {
        title: entry0.title || '',
        content: entry0.content || '',
        bulletPoints: (entry0.bullet_points || entry0.bulletPoints || []).filter(b => b && b.toString().trim()),
        slide_type: entry0.slide_type || entry0.slideType || '',
        needsImage: entry0.needs_image || entry0.needsImage || false,
        suggestedImage: entry0.suggested_image || entry0.suggestedImage || '',
        chart_config: entry0.chart_config || entry0.chartConfig || null,
      };
      const outlineKey = JSON.stringify(mapped);
      const controller = new AbortController();
      const formData = new FormData();
      formData.append('presentation_title', streamTitle || 'Presentation');
      formData.append('input_mode', 'structured');
      formData.append('slides_content', JSON.stringify([mapped]));
      formData.append('slide_count', '1');
      formData.append('enhance_text', 'false');
      if (qa.audience) formData.append('audience', qa.audience);
      if (qa.presentationType) formData.append('presentation_type', qa.presentationType);
      if (qa.visualStyle) formData.append('visual_style', qa.visualStyle);
      formData.append('html_model', qa.htmlModel || getDefaultModel());
      // Always send imagery_mode: a silent omission used to let the backend
      // default to 'auto' and put photos on a "no images" deck. 'none' also
      // carries the legacy include_images belt for older code paths.
      const _im = qa.imageryMode || qa.imagery_mode || 'auto';
      formData.append('imagery_mode', _im);
      if (_im === 'none') formData.append('include_images', 'no');
      const _ad = qa.artDirection || qa.art_direction;
      if (_ad) formData.append('art_direction', _ad);
      if (qa.specialInstructions) formData.append('special_instructions', qa.specialInstructions);
      // r131b: the early slide becomes the deck's style anchor AND its cover —
      // the highest-stakes slide for invented client names/dates
      if (proposalMode) formData.append('proposal_mode', 'true');
      // PPTX-safe must reach the early slide too — it becomes the deck's
      // style anchor, so an unsafe cover would propagate lossy constructs.
      if (qa.exportTarget === 'pptx') formData.append('pptx_safe', 'true');
      // run_qa OFF for the early pipelining call (round 78): its QA used to
      // be the slide's ONLY check, one weakest-judge look inside the 90s
      // early budget. The job now re-verifies + repairs this slide with the
      // tier judge and a measured-overflow gate before anchoring (regen_ok),
      // so QA here would be a slower duplicate that risks the 90s timeout.
      formData.append('run_qa', 'false');
      // This flow always maps to the ai-create theme preset downstream.
      formData.append('ai_create_theme', 'true');
      // THE USER'S THEME PICK MUST REACH THE EARLY SLIDE. This call
      // generates slide 1, and slide 1 becomes the deck's style ANCHOR:
      // every later slide is told to match it and QA enforces the match.
      // This field was missing, so a clarifier theme choice (the THEME LOCK
      // with exact hexes and fonts) shaped slides 2+ while the anchor was
      // generated themeless — and the whole deck then converged, perfectly
      // consistently, on whatever slide 1 invented instead (user's 10/08
      // deck: navy, when they had picked warm cream + Poppins).
      if (qa.customThemeRequest) formData.append('custom_theme_request', qa.customThemeRequest);
      const _promptText = submittedPromptRef.current || submittedPrompt || prompt;
      if (_promptText) formData.append('user_prompt', _promptText);
      if (researchContent) formData.append('web_research', researchContent);
      const _excel = getMergedExcelData();
      if (_excel) formData.append('excel_data', JSON.stringify(_excel));
      if (qa.brandIdentity && qa.brandIdentity !== 'none') formData.append('brand_identity_id', qa.brandIdentity);
      // Template-guided decks: the early slide 1 must use the template too,
      // or the pre-generated cover mismatches the rest of the deck.
      if (qa.deckTemplateId) formData.append('deck_template_id', qa.deckTemplateId);
      // NO brand_guidelines / logo: the backend pre-gen consumption path
      // re-injects brand CSS + logo. NO user_id (see block comment).

      const promise = (async () => {
        const resp = await fetch(withBackend('/create-html-slides-stream'), {
          method: 'POST', body: formData, signal: controller.signal,
        });
        if (!resp.ok || !resp.body) throw new Error(`early slide-1 HTTP ${resp.status}`);
        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const lines = buf.split('\n\n');
          buf = lines.pop() || '';
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            let evt;
            try { evt = JSON.parse(line.slice(6)); } catch { continue; }
            if (evt.type === 'slide' && evt.slide?.html_source) {
              // We have the HTML — close the stream so the server stops
              // (screenshots happen on the main run's pre-gen path anyway).
              try { controller.abort(); } catch { /* stream done */ }
              return { html_source: evt.slide.html_source };
            }
            if (evt.type === 'error' || evt.type === 'fatal_error') {
              throw new Error(evt.error || 'early slide-1 failed');
            }
          }
        }
        return null;
      })().then(
        (result) => {
          if (earlySlide1Ref.current?.outlineKey === outlineKey) {
            earlySlide1Ref.current.status = result ? 'done' : 'failed';
            earlySlide1Ref.current.result = result;
          }
          return result;
        },
        () => {
          // Never rejects — downstream awaits can't throw.
          if (earlySlide1Ref.current?.outlineKey === outlineKey) {
            earlySlide1Ref.current.status = 'failed';
          }
          return null;
        }
      );
      earlySlide1Ref.current = { promise, status: 'pending', result: null, outlineKey, controller };
      console.log('[early-slide1] fired for first outline entry');
    } catch (e) {
      console.warn('[early-slide1] fire failed:', e);
    }
  };

  const startOutlineGeneration = async (qa) => {
    // r129: the user asked to be TOLD about the visual-read limit. Full text
    // of every page still goes in; pages are sampled evenly past the cap.
    try {
      const _visPages = getScopedContentPages().filter(p => p?.image_data_url).length;
      if (_visPages > OUTLINE_PAGE_IMAGE_CAP) {
        setMessages(prev => [...prev, {
          role: 'bot',
          text: `Heads up: your upload has ${_visPages} pages. I read the full text of every page, and visually scan ${OUTLINE_PAGE_IMAGE_CAP} of them (sampled across the whole document, first and last always included). If specific pages matter most, name them in your prompt and I will prioritize their content.`,
        }]);
      }
    } catch { /* the notice is cosmetic — never block outline generation */ }
    const myEpoch = ++outlineEpochRef.current;
    setOutlineLoading(true);
    setOutlineSlides([]);
    setOutlineTitle('');
    setOutlineMetadata(null);
    setThinkingText('');
    setIsThinking(false);
    setWebResearch('');
    // Reset any previous outline attempt's early slide-1 (a regenerated
    // outline must not consume a stale early result).
    if (earlySlide1Ref.current?.controller) {
      try { earlySlide1Ref.current.controller.abort(); } catch { /* noop */ }
    }
    earlySlide1Ref.current = null;

    // The LLM decides slide count from the content/prompt — no frontend heuristics.
    // We only send a soft hint for non-presentation types where there's a natural default.
    const defaultSlides = projectType === 'infographic' ? 5
      : projectType === 'document' ? 5
      : projectType === 'dashboard' ? 6
      : 0;  // 0 = let LLM decide from the user's content

    // Read from qa (the fresh answers passed in) — answers closure may be stale
    // because setAnswers is async and startOutlineGeneration is often invoked
    // in the same tick as setAnswers(defaults).
    // r131c: in proposal mode the deck RESPONDS to the document — preserve/
    // restructure/summarize would reproduce the RFP itself. Deterministic
    // belt under the classifier fix (escalation ladder).
    const preClassified = proposalMode ? 'generate' : (qa?._intentValue || answers?._intentValue || null);
    const promptText = submittedPromptRef.current || submittedPrompt || prompt;
    console.log('[outline] preClassified=', preClassified, 'promptLen=', promptText?.length, 'hasPages=', !!attachmentRef.current?.pages?.length);

    // DETERMINISTIC PATH (FILE): When upstream intent is preserve/restructure
    // and the user uploaded a file with per-page text, build the outline 1:1
    // from the file's own pages. No LLM involved. Each page = one outline slide.
    // Then let handleGenerate's direct pipeline use Opus/Flash/Pro to actually
    // render each slide from the page image.
    // Merge pages from every content-role attachment in upload order. Design-
    // reference files are excluded (getMergedContentPages already filters).
    const filePagesForOutline = getScopedContentPages();
    if (
      projectType === 'presentation'
      && (preClassified === 'preserve' || preClassified === 'restructure')
      && filePagesForOutline.length > 0
    ) {
      console.log('[outline] file-preserve path — building deterministic outline from', filePagesForOutline.length, 'pages');
      const fileSlides = filePagesForOutline.map((p, i) => {
        const rawText = (p.text || '').trim();
        // Take the first non-empty line as the title; rest as content
        const lines = rawText.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
        const title = lines[0]?.slice(0, 80) || `Slide ${p.page || i + 1}`;
        const body = lines.slice(1).join('\n').slice(0, 2000);
        return {
          slide_number: p.page || (i + 1),
          slide_type: 'content',
          title,
          content: body,
          bullet_points: [],
          instructions: '',
          suggested_image: '',
          needs_image: false,
        };
      });
      const fileTitle = (attachmentRef.current?.name || attachment?.name || 'Untitled').replace(/\.[^.]+$/i, '');
      setOutlineTitle(fileTitle);
      setOutlineSlides(fileSlides);
      setOutlineMetadata({
        content_source: 'file_attachment',
        content_relationship: preClassified,
        output_fidelity: 'verbatim',
        structural_constraint: { type: 'content_driven', value: fileSlides.length },
        ambiguity_detected: false,
        clarifying_question: null,
        detected_slide_count: fileSlides.length,
        topic_summary: promptText.slice(0, 200),
        special_requests: [],
        deterministic_split: true,
        source: 'file_pages',
      });
      setOutlineLoading(false);
      setFlowPhase('outline-ready');
      setMessages(prev => [...prev, {
        role: 'bot',
        // r159: with skip-preview on (the default) generation auto-fires —
        // telling the user to click Generate while slides were already
        // streaming read as a broken flow.
        text: skipOutlinePreview
          ? `Your ${fileSlides.length}-page deck is parsed — redesigning each page with your chosen model.`
          : `Your ${fileSlides.length}-page deck is ready for review. Click Generate to redesign each slide with your chosen model.`,
      }]);
      return;
    }

    // DETERMINISTIC PATH (PASTED): When upstream intent is preserve/restructure
    // and the user's prompt/content contains explicit slide markers, skip the
    // outline LLM entirely. Backend parses markers and returns a verbatim outline.
    // Falls through on 422 (no markers).
    if (
      projectType === 'presentation'
      && (preClassified === 'preserve' || preClassified === 'restructure')
      && filePagesForOutline.length === 0
      && promptText
    ) {
      // No length gate — the backend marker parser returns 422 cleanly if no
      // markers are found, and we fall through to outline-stream. Any length
      // threshold here just risks skipping a valid preserve case.
      try {
        const detResp = await fetch(withBackend('/api/outline-from-content'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            content: promptText,
            prompt: promptText,
            content_relationship: preClassified,
            conversation_history: buildConversationHistory(),
            project_type: projectType,
          }),
        });
        if (detResp.ok) {
          const det = await detResp.json();
          // A steer arrived while this (unabortable) fetch was in flight —
          // the steered stream owns the outline now; discard this result.
          if (outlineEpochRef.current !== myEpoch) return;
          if (det.success && det.outline?.slides?.length > 0) {
            setOutlineTitle(det.outline.title || '');
            setOutlineSlides(det.outline.slides);
            setOutlineMetadata(det.outline.metadata || null);
            setOutlineLoading(false);
            setFlowPhase('outline-ready');
            setMessages(prev => [...prev, {
              role: 'bot',
              // r171: human copy — and say when embedded AI instructions were
              // set aside as steering instead of being typeset as slides.
              text: (() => {
                const n = det.outline.slides.length;
                const kept = preClassified === 'preserve' ? 'kept verbatim' : 'kept intact';
                const instr = det.outline.metadata?.special_requests?.length
                  ? ' Your instructions ride along as design guidance, not slide content.' : '';
                return `Read your content as ${n} slide${n === 1 ? '' : 's'} (${kept}).${instr}`;
              })(),
            }]);
            return;
          }
        }
        // 422 or missing: fall through to LLM outline
      } catch (detErr) {
        console.warn('[outline] deterministic split failed, falling back:', detErr);
      }
    }

    // Steering support: register this stream so a chat message sent while it
    // is still generating can abort it and restart with instructions.
    const outlineController = new AbortController();
    if (outlineStreamAbortRef.current) {
      try { outlineStreamAbortRef.current.abort(); } catch { /* noop */ }
    }
    outlineStreamAbortRef.current = outlineController;

    // Auto-research notice, posted at the moment research actually runs
    // (round 72: it used to appear ABOVE the clarifier, minutes early).
    // The ref clears UNCONDITIONALLY: a stale flag from a run where the
    // user toggled research off must not label a later MANUAL enable as
    // auto (verifier catch).
    if (webSearchAutoNoticeRef.current) {
      const _autoNotice = (qa.webSearch === 'yes' || webSearchRef.current)
        && !(attachmentRef.current?.content);
      webSearchAutoNoticeRef.current = false;
      if (_autoNotice) {
        setMessages(prev => [...prev, {
          role: 'bot',
          text: 'Web research is on for this one — the prompt references a specific subject worth looking up, so the content will use real facts instead of generic filler. Tap the globe button to turn it off next time.',
        }]);
      }
    }

    try {
      const response = await fetch(withBackend('/api/generate-outline-stream'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: outlineController.signal,
        body: JSON.stringify({
          prompt: submittedPromptRef.current || submittedPrompt || prompt,
          num_slides: defaultSlides,
          language: qa.language || 'auto',
          text_style: qa.textStyle || 'balanced',
          tone: 'professional',
          llm_provider: qa.llmProvider || outlineModel || 'openai-nano',
          audience: qa.audience || null,
          presentation_type: qa.presentationType || qa.infographicType || qa.documentType || null,
          visual_style: qa.visualStyle || null,
          imagery_mode: qa.imageryMode || qa.imagery_mode || null,
          art_direction: qa.artDirection || qa.art_direction || null,
          project_type: projectType,
          chart_types: qa.chartTypes || null,
          web_search: attachmentRef.current?.content ? false : (qa.webSearch === 'yes' || webSearchRef.current),
          special_instructions: qa.specialInstructions || '',
          dashboard_layout: qa.dashboardLayout || 'balanced',
          chart_preference: qa.chartPreference || 'mixed',
          excel_data: getMergedExcelData(),
          // Only pass file_content as source when the attachment is content-role.
          // Design-reference files provide visual style, not text.
          // Concat every content-role attachment's text (upload order). Design
          // refs are excluded (they contribute visual style only).
          file_content: getMergedContentText(),
          // r128: page renders of the content upload — the outline model SEES
          // the document (tables/charts/layout), not just its flattened text.
          content_page_images: collectContentPageImageUrls(),
          proposal_mode: !!proposalMode,
          pre_classified_relationship: preClassified,
          // User's chosen HTML model — backend routes outline generation to
          // match (e.g. opus → Claude Opus outline + HTML). Removes the old
          // forced-Gemini override.
          html_model: qa.htmlModel || null,
          // Proactive theme anchor: lets the outline model scope slide
          // structure to the brand's visual system.
          brand_identity_id: (qa.brandIdentity && qa.brandIdentity !== 'none') ? qa.brandIdentity : null,
          conversation_history: buildConversationHistory(),
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate outline');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let slidesReceived = 0;
      // Locals mirroring async state (state setters haven't flushed inside
      // this loop) — needed by the early slide-1 fire.
      let streamTitle = '';
      let researchContent = '';

      while (true) {
        const { done, value } = await reader.read();
        // Superseded by a steer between reads — abort() only rejects PENDING
        // reads, so an already-resolved read could still process buffered
        // events and stomp the steered outline.
        if (outlineEpochRef.current !== myEpoch) return;
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const event = JSON.parse(line.slice(6));
              switch (event.type) {
                case 'thinking':
                  setIsThinking(true);
                  setThinkingText(prev => prev + (event.text || ''));
                  break;
                case 'title':
                  setIsThinking(false);
                  setThinkingOpen(false);
                  setOutlineTitle(event.title);
                  streamTitle = event.title || '';
                  break;
                case 'slide':
                  setIsThinking(false);
                  slidesReceived++;
                  setOutlineSlides(prev => [...prev, event.slide]);
                  // Early slide-1 pipelining — fire on the FIRST outline entry,
                  // only for skip-outline presentation decks where the outline
                  // cannot be edited before consumption (no staleness window),
                  // no variations (their pre-gen wins), no embeds/redesign
                  // (those map against the full deck), and research (if
                  // requested) already arrived.
                  if (
                    slidesReceived === 1 && !earlySlide1Ref.current
                    && projectType === 'presentation'
                    && (qa?.generationMode || 'html') === 'html'
                    && skipOutlinePreview
                    && qa?.wantVariations !== 'yes'
                    && !qa?.nativeTemplateId
                    && collectEmbedImages().length === 0
                    && !(attachmentRef.current?.imageData
                         && attachmentRef.current?.role !== 'design_reference'
                         && attachmentRef.current?.role !== 'embed_asset')
                    && !(((qa?.webSearch === 'yes') || webSearchRef.current) && !researchContent)
                  ) {
                    fireEarlySlide1(event.slide, qa || {}, streamTitle, researchContent);
                  }
                  break;
                case 'summary':
                  if (event.metadata) setOutlineMetadata(event.metadata);
                  break;
                case 'web_research':
                  setWebResearch(event.content || '');
                  researchContent = event.content || '';
                  setIsThinking(false);
                  setMessages(prev => [...prev, { role: 'bot', isResearch: true, text: event.content || '' }]);
                  break;
                case 'ambiguity':
                  // LLM detected conflicting signals — pause and ask the user
                  setIsThinking(false);
                  setOutlineLoading(false);
                  setMessages(prev => [...prev, {
                    role: 'bot',
                    text: event.question || 'I need some clarification before continuing.',
                    isAmbiguity: true,
                  }]);
                  setFlowPhase('questionnaire'); // Let user respond via chat
                  return; // Stop processing — generation will restart after clarification
                case 'error':
                  throw new Error(event.error);
                default:
                  break;
              }
            } catch (parseError) {
              if (parseError.message && !parseError.message.includes('JSON')) throw parseError;
            }
          }
        }
      }

      if (outlineEpochRef.current !== myEpoch) return;
      if (slidesReceived === 0) throw new Error('No slides received from outline generation');
      setFlowPhase('outline-ready');
    } catch (error) {
      // Superseded by a steering request — the new stream owns the UI now.
      if (error.name === 'AbortError' || outlineEpochRef.current !== myEpoch) return;
      console.error('Outline generation error:', error);
      toast.error(error.message || 'Failed to generate outline');
      setFlowPhase('prompt');
      setMessages(prev => [...prev, { role: 'bot', text: "Something went wrong generating the outline. Please try again." }]);
    } finally {
      // Only clear the spinner if this stream is still the active one — a
      // steering restart sets outlineLoading(true) for ITS stream, and the
      // aborted stream's finally must not clobber it back to false.
      if (outlineStreamAbortRef.current === outlineController) setOutlineLoading(false);
    }
  };

  // ── Outline editing helpers ──
  const updateOutlineSlide = (slideIdx, field, value) => {
    setOutlineSlides(prev => prev.map((s, i) => i === slideIdx ? { ...s, [field]: value } : s));
  };
  const updateOutlineBullet = (slideIdx, bulletIdx, value) => {
    setOutlineSlides(prev => prev.map((s, i) => {
      if (i !== slideIdx) return s;
      const newBullets = [...(s.bullet_points || [])];
      newBullets[bulletIdx] = value;
      return { ...s, bullet_points: newBullets };
    }));
  };
  const removeOutlineBullet = (slideIdx, bulletIdx) => {
    setOutlineSlides(prev => prev.map((s, i) => {
      if (i !== slideIdx) return s;
      return { ...s, bullet_points: s.bullet_points.filter((_, j) => j !== bulletIdx) };
    }));
  };
  const addOutlineBullet = (slideIdx) => {
    setOutlineSlides(prev => prev.map((s, i) => {
      if (i !== slideIdx) return s;
      return { ...s, bullet_points: [...(s.bullet_points || []), ''] };
    }));
  };
  const removeOutlineSlide = (slideIdx) => {
    setOutlineSlides(prev => prev.filter((_, i) => i !== slideIdx));
  };
  const addOutlineSlide = () => {
    setOutlineSlides(prev => [...prev, { title: `New ${getUnitLabel(projectType)}`, content: '', slide_type: 'content', bullet_points: [''] }]);
  };

  // ── Outline edit request from chat ──
  const handleOutlineEditRequest = async () => {
    const editRequest = prompt.trim();
    if (!editRequest) return;
    if (isSubmitInFlightRef.current) {
      toast.error("I'm still working on your last request. Give me a moment.");
      return;
    }
    isSubmitInFlightRef.current = true;
    setIsSubmitInFlight(true);
    setPrompt('');
    setMessages(prev => [...prev, { role: 'user', text: editRequest }]);
    // Steering: if the outline is still streaming, abort it — this request
    // regenerates from scratch with the instruction folded in, and two live
    // streams would race over outlineSlides. Bumping the epoch also kills
    // continuations abort() can't reach (already-resolved reads, the
    // unabortable deterministic marker-split fetch).
    const wasStreaming = flowPhase === 'generating-outline';
    outlineEpochRef.current += 1;
    if (outlineStreamAbortRef.current) {
      try { outlineStreamAbortRef.current.abort(); } catch { /* noop */ }
    }
    // The steered outline invalidates any early slide-1 pre-generation —
    // without this, handleGenerate would block on (then discard) a stale
    // slide built from the pre-steer outline: a paid LLM call and up to 90s
    // of silent latency per steer.
    if (earlySlide1Ref.current?.controller) {
      try { earlySlide1Ref.current.controller.abort(); } catch { /* noop */ }
    }
    earlySlide1Ref.current = null;
    const editController = new AbortController();
    outlineStreamAbortRef.current = editController;
    setMessages(prev => [...prev, { role: 'bot', text: wasStreaming ? 'Got it — restarting the outline with that in mind...' : 'Updating the outline...' }]);
    setOutlineLoading(true);

    // Captured BEFORE the fetch: on ANY failure we restore this outline —
    // capturing after response.ok left aborted-stream partials in state when
    // the request itself failed.
    const prevOutlineSlides = outlineSlides;
    const prevOutlineTitle = outlineTitle;
    // Mid-stream steering never anchors the count to a partial slide list.
    // Presentations pass 0 (backend prompt says "determine the right
    // number"), but infographic/document/dashboard prompts interpolate the
    // number literally ("exactly N sections") — 0 there commands an empty
    // outline, so they steer with their startOutlineGeneration defaults.
    const steerSlideCount = projectType === 'presentation' ? 0
      : projectType === 'dashboard' ? 6
      : 5;

    try {
      const response = await fetch(withBackend('/api/generate-outline-stream'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: editController.signal,
        body: JSON.stringify({
          prompt: submittedPromptRef.current || submittedPrompt || 'presentation',
          num_slides: wasStreaming ? steerSlideCount : Math.max(outlineSlides.length, 8),
          // 'auto' (not 'en') when nothing is known yet: mid-stream steers run
          // before the summary event delivers detected_language, and a hard
          // 'en' would flip an auto-detected Arabic deck to English.
          language: answers.language || outlineMetadata?.detected_language || 'auto',
          text_style: 'enhance',
          tone: 'professional',
          llm_provider: answers.llmProvider || 'openai',
          audience: answers.audience || null,
          presentation_type: answers.presentationType || null,
          visual_style: answers.visualStyle || null,
          imagery_mode: answers.imageryMode || answers.imagery_mode || null,
          art_direction: answers.artDirection || answers.art_direction || null,
          instructions: editRequest,
          web_search: answers.webSearch === 'yes' || webSearchRef.current,
          file_content: getMergedContentText(),
          // r128: page renders of the content upload — the outline model SEES
          // the document (tables/charts/layout), not just its flattened text.
          content_page_images: collectContentPageImageUrls(),
          proposal_mode: !!proposalMode,
          pre_classified_relationship: answers._intentValue || null,
          html_model: answers.htmlModel || null,
          brand_identity_id: (answers.brandIdentity && answers.brandIdentity !== 'none') ? answers.brandIdentity : null,
          conversation_history: buildConversationHistory(),
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update outline');
      }

      setOutlineSlides([]);
      setOutlineTitle('');
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let streamError = null;
      let ambiguityQuestion = null;
      let slidesReceived = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const event = JSON.parse(line.slice(6));
              if (event.type === 'title') setOutlineTitle(event.title);
              if (event.type === 'slide') { slidesReceived += 1; setOutlineSlides(prev => [...prev, event.slide]); }
              if (event.type === 'web_research') setWebResearch(event.content || '');
              if (event.type === 'ambiguity') ambiguityQuestion = event.question || 'I need some clarification before continuing.';
              if (event.type === 'error') streamError = event.error || 'Failed to update outline';
            } catch {}
          }
        }
      }
      if (ambiguityQuestion && slidesReceived === 0) {
        // The model paused to ask instead of producing slides — surface the
        // question (mirrors the main stream's ambiguity handling) rather
        // than a generic failure toast.
        setOutlineSlides(prevOutlineSlides);
        setOutlineTitle(prevOutlineTitle);
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { role: 'bot', text: ambiguityQuestion, isAmbiguity: true };
          return updated;
        });
        setFlowPhase('questionnaire');
        return;
      }
      if (streamError || slidesReceived === 0) {
        throw new Error(streamError || 'The outline update returned no slides');
      }
      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = { role: 'bot', text: 'Outline updated! Review the changes on the right.' };
        return updated;
      });
      setFlowPhase('outline-ready');
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Outline edit error:', error);
        toast.error(error.message || 'Failed to update outline');
        // Restore the pre-request outline on any failure (covers both a
        // failed fetch and a stream that died after the reset above).
        setOutlineSlides(prevOutlineSlides);
        setOutlineTitle(prevOutlineTitle);
        // A failed STEER has no live stream left to reach 'outline-ready'
        // (the original was aborted) — without this the user is stranded in
        // 'generating-outline' with no Generate button or edit controls.
        if (wasStreaming) {
          setFlowPhase(prevOutlineSlides.length > 0 ? 'outline-ready' : 'prompt');
        }
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { role: 'bot', text: 'Failed to update the outline. Try again.' };
          return updated;
        });
      }
    } finally {
      if (outlineStreamAbortRef.current === editController) setOutlineLoading(false);
      isSubmitInFlightRef.current = false;
      setIsSubmitInFlight(false);
    }
  };

  // ── Transition to workspace phase ──
  const transitionToWorkspace = (outline, questionnaireData, flowConfig) => {
    setWorkspaceData({ outline, questionnaireData, flowConfig });
    setMessages(prev => [
      ...prev.map(m => m.chipState === 'running' ? { ...m, chipState: 'done' } : m),
      {
        role: 'bot',
        chipLabel: `Starting ${PROJECT_TYPE_LABELS[projectType] || 'content'} generation`,
        chipState: 'running',
        _workspaceStartChip: true,
      },
    ]);
    setFlowPhase('workspace');
    // Also notify App.js so it can track state (for saved project loading)
    if (onOutlineReady) onOutlineReady(outline, questionnaireData, flowConfig);
  };

  // ── Start variation generation (SSE stream) ──
  const startVariationGeneration = async () => {
    setFlowPhase('generating-variations');
    setVariations([]);
    setSelectedVariationIndex(null);
    variationSelectedRef.current = false;
    // Create abort controller so we can cancel this SSE if user picks a variation early
    const abortController = new AbortController();
    variationAbortRef.current = abortController;
    const count = parseInt(answers.variationCount) || 3;
    // r179: a variation samples at most 3 slides — never more than the outline
    // has (a one-slide deck gets one slide per variation, not three copies).
    const perVar = Math.max(1, Math.min(3, outlineSlides.length || 3));
    setMessages(prev => [...prev, { role: 'system', text: `Generating ${count} design variations (${perVar} sample slide${perVar === 1 ? '' : 's'} each)... This may take a few minutes.`, isStatus: true }]);

    try {
      const response = await fetch(withBackend('/api/generate-variations-stream'), {
        method: 'POST',
        signal: abortController.signal,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          outline_slides: outlineSlides.map(s => ({
            title: s.title || '',
            content: s.content || '',
            bullet_points: s.bullet_points || s.bulletPoints || [],
            slide_type: s.slide_type || s.slideType || '',
          })),
          variation_count: count,
          presentation_title: outlineTitle,
          visual_style: answers.visualStyle || 'professional',
          html_model: answers.htmlModel || getDefaultModel(),
          imagery_mode: answers.imageryMode || answers.imagery_mode || 'auto',
          art_direction: answers.artDirection || answers.art_direction || '',
          user_prompt: submittedPrompt || prompt,
          special_instructions: answers.specialInstructions || (outlineMetadata?.special_requests?.length ? outlineMetadata.special_requests.join('; ') : ''),
          proposal_mode: !!proposalMode,
          pptx_safe: answers.exportTarget === 'pptx',
          // When variationBrandMode is true, pass brand_identity_id so backend uses layout-only directions
          // When false, omit it so backend generates full creative style variations
          brand_identity_id: variationBrandMode && answers.brandIdentity && answers.brandIdentity !== 'none' ? answers.brandIdentity : null,
          user_id: userId || '',
        }),
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));
            switch (event.type) {
              case 'start':
                setVariationSampleIndices(event.sample_indices || []);
                break;
              case 'direction':
                setVariations(prev => {
                  const updated = [...prev];
                  while (updated.length <= event.variation_index) updated.push({ direction: null, slides: [] });
                  updated[event.variation_index] = { direction: event.direction, slides: [] };
                  return updated;
                });
                break;
              case 'variation_slide':
                setVariations(prev => {
                  const updated = [...prev];
                  if (updated[event.variation_index]) {
                    const v = { ...updated[event.variation_index], slides: [...updated[event.variation_index].slides] };
                    while (v.slides.length <= event.slide_index) v.slides.push(null);
                    v.slides[event.slide_index] = { html_source: event.html_source };
                    updated[event.variation_index] = v;
                  }
                  return updated;
                });
                break;
              case 'variation_complete':
                setMessages(prev => [...prev, { role: 'system', text: `Variation ${event.variation_index + 1} ready.`, isStatus: true }]);
                break;
              case 'complete':
                // Only transition to choosing-variation if user hasn't already picked one
                if (!variationSelectedRef.current) {
                  setFlowPhase('choosing-variation');
                  setMessages(prev => [...prev, { role: 'bot', text: 'All variations are ready! Pick your preferred design style on the right.' }]);
                }
                break;
              case 'error':
                setMessages(prev => [...prev, { role: 'system', text: `Variation ${(event.variation_index ?? 0) + 1} failed: ${event.message}`, isError: true }]);
                break;
              default:
                break;
            }
          } catch { /* skip malformed */ }
        }
      }
    } catch (err) {
      // Abort is expected when user selects a variation early — not an error
      if (err.name === 'AbortError') {
        console.log('[VARIATIONS] SSE stream aborted (user selected a variation)');
        return;
      }
      setMessages(prev => [...prev, { role: 'system', text: `Variation generation failed: ${err.message}`, isError: true }]);
      setFlowPhase('outline-ready'); // Fall back to outline
    }
  };

  // ── Auto-skip outline preview for documents in preserve mode ──
  // When the user said "don't change the content" (preserve intent) AND we're
  // in a document workspace, the outline preview adds nothing — the content
  // isn't being restructured. Jump straight to generation. Applies to one-
  // pagers and multi-page preserved documents alike.
  // Mark any running status chips as done when we reach a "stable" phase
  // (outline-ready or workspace). Without this the running chip keeps shimmer
  // animating while the user reviews their outline.
  useEffect(() => {
    if (flowPhase === 'outline-ready' || flowPhase === 'workspace' || flowPhase === 'choosing-variation') {
      setMessages(prev => prev.map(m =>
        m.chipState === 'running' && !m._directProgress ? { ...m, chipState: 'done' } : m
      ));
    }
  }, [flowPhase]);

  const docPreserveAutoFiredRef = useRef(false);
  useEffect(() => {
    if (docPreserveAutoFiredRef.current) return;
    if (flowPhase !== 'outline-ready') return;
    if (projectType !== 'document') return;
    if (answers?._intentValue !== 'preserve') return;
    docPreserveAutoFiredRef.current = true;
    setMessages(prev => [
      ...prev.map(m => m.chipState === 'running' ? { ...m, chipState: 'done' } : m),
      { role: 'bot', chipLabel: 'Content preserved — generating', chipState: 'running' },
    ]);
    setTimeout(() => handleGenerate(), 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowPhase, answers?._intentValue, projectType]);

  // Optional-outline auto-advance. When the user has the outline review toggle OFF
  // (the default), auto-fire generation as soon as the outline is ready instead of
  // waiting for a Generate click. Modeled on docPreserveAutoFiredRef above: a ref
  // guard + a wait for outlineSlides to be populated + a 100ms tick so streamed
  // state has flushed. Covers all three outline-ready entry points uniformly.
  const skipOutlineAutoFiredRef = useRef(false);
  useEffect(() => {
    if (skipOutlineAutoFiredRef.current) return;
    if (flowPhase !== 'outline-ready') return;
    if (!skipOutlinePreview) return;                 // review is ON — show the panel
    if (outlineSlides.length === 0) return;          // wait until the outline streamed in
    // Don't hijack the variations flow — let the choosing UI show first.
    if (answers?.wantVariations === 'yes' && selectedVariationIndex === null) return;
    // Document-preserve has its own auto-fire effect above; don't double-fire.
    if (projectType === 'document' && answers?._intentValue === 'preserve') return;
    skipOutlineAutoFiredRef.current = true;
    setMessages(prev => [
      ...prev.map(m => m.chipState === 'running' ? { ...m, chipState: 'done' } : m),
      { role: 'bot', chipLabel: `Designing your ${getUnitLabel(projectType, false)}s`, chipState: 'running' },
    ]);
    setTimeout(() => handleGenerate(), 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowPhase, skipOutlinePreview, outlineSlides.length, answers?.wantVariations, selectedVariationIndex, projectType, answers?._intentValue]);

  // ── Generate — build flowConfig and transition to workspace ──
  const handleGenerate = async () => {
    // DIRECT PIPELINE: when the user confirmed preserve/restructure AND we have
    // per-page images from the upload AND it's a presentation, generate slides
    // directly from source pages with their chosen model (Opus/Flash). Skips
    // the outline step; keeps everything else from the questionnaire intact.
    // Pull pages from EVERY content-role attachment in upload order so a deck
    // assembled from multiple files works end-to-end.
    const pagesSource = getScopedContentPages();
    const hasPages = pagesSource.length > 0;
    const intentValue = answers._intentValue;
    const directEligible = (intentValue === 'preserve' || intentValue === 'restructure')
      && hasPages
      // r131c: the direct pipeline rebuilds uploaded pages as slides — in
      // proposal mode that would output the RFP itself
      && !proposalMode
      && projectType === 'presentation';
    if (directEligible) {
      // If the user wants variations and hasn't picked one yet, let the normal
      // variations flow run on the deterministic outline. When they pick a
      // variation and handleGenerate re-fires, directEligible will still be
      // true AND selectedVariationIndex will be set — then direct pipeline
      // uses the chosen variation's first slide as style_reference_html.
      if (answers.wantVariations === 'yes' && selectedVariationIndex === null && !answers.nativeTemplateId) {
        // Fall through to the variations branch below. Direct pipeline will
        // re-evaluate after the user picks.
      } else {
        const clarification = { value: intentValue };
        const ok = await runDirectPipeline(clarification, submittedPrompt || prompt, answers);
        if (ok) return;
      }
    }

    // Branch into variations if user opted in and hasn't picked one yet.
    // Native masters never take this branch: variations are HTML designs and
    // the native engine would silently discard the user's pick.
    if (answers.wantVariations === 'yes' && selectedVariationIndex === null && !answers.nativeTemplateId) {
      startVariationGeneration();
      return;
    }

    const questionnaireData = {
      audience: answers.audience || null,
      presentationType: answers.presentationType || null,
      infographicType: answers.infographicType || null,
      visualStyle: answers.visualStyle || null,
      includeImages: answers.includeImages || null,
      imageStyle: answers.imageStyle || null,
      imageryMode: answers.imageryMode || answers.imagery_mode || null,
      artDirection: answers.artDirection || answers.art_direction || null,
      keepSourceDesign: !!answers._keepSourceDesign, // r176: the redesign caption reproduces the identity
      htmlModel: answers.htmlModel || getDefaultModel(),
      chartTypes: answers.chartTypes || null,
      infographicSize: answers.infographicSize || null,
      exportTarget: answers.exportTarget || null,
      specialInstructions: answers.specialInstructions || (outlineMetadata?.special_requests?.length ? outlineMetadata.special_requests.join('; ') : ''),
      customThemeRequest: answers.customThemeRequest || '',
      proposalScope: answers.proposalScope || null, // r180: frozen scope rides into the workspace + project metadata
      runQa: answers.runQa || 'yes',
    };
    const flowConfig = {
      projectType,
      generationMode: projectType === 'infographic'
        ? (answers.generationMode === 'ai' ? 'infographic-ai' : 'infographic')
        : projectType === 'document' ? 'document-html'
        : projectType === 'dashboard' ? 'dashboard-html'
        : (answers.nativeTemplateId && !proposalMode) ? 'native-pptx'
        : (answers.generationMode || 'html'),
      // r131b: native masters are NOT contract-aware — a proposal must never
      // ride an uncontracted pipeline while the badge promises facts-only
      nativeTemplateId: proposalMode ? null : (answers.nativeTemplateId || null),
      htmlModel: answers.htmlModel || getDefaultModel(),
      llmProvider: answers.llmProvider || outlineModel || 'openai-nano',
      prompt: submittedPrompt || prompt,
      language: answers.language || outlineMetadata?.detected_language || 'en',
      themePreset: 'ai-create',
      includeImages: answers.includeImages || 'no',
      imageStyle: answers.imageStyle || 'inline',
      imageryMode: answers.imageryMode || answers.imagery_mode || null,
      artDirection: answers.artDirection || answers.art_direction || null,
      keepSourceDesign: !!answers._keepSourceDesign, // r176: the redesign caption reproduces the identity
      logoUrl,
      webResearch,
      excelData: getMergedExcelData(),
      fileContent: getMergedContentText(),
      fileImages: getFileImages(),
      // redesignImage = the user wants THIS slide image redesigned/improved. Do
      // NOT route design_reference (a style to match) or embed_asset (an image
      // to place verbatim) here — those have their own channels below.
      redesignImage: (attachmentRef.current?.role === 'design_reference' || attachmentRef.current?.role === 'embed_asset') ? null : (attachmentRef.current?.imageData || null),
      // User images to embed verbatim into slides (role==='embed_asset').
      embedImages: collectEmbedImages(),
      documentType: answers.documentType || null,
      dashboardTheme: answers.dashboardTheme || 'dark-analytics',
      dashboardLayout: answers.dashboardLayout || 'balanced',
      chartPreference: answers.chartPreference || 'mixed',
      documentStyle: answers.documentStyle || 'corporate',
      contentRelationship: outlineMetadata?.content_relationship || null,
      brandIdentityId: (answers.brandIdentity && answers.brandIdentity !== 'none') ? answers.brandIdentity : null,
      deckTemplateId: answers.deckTemplateId || null,
      designReferenceImages: collectDesignReferenceImageUrls(),
      proposalMode: !!proposalMode,
      proposalScope: answers.proposalScope || null, // r180
      documentOrientation: answers.documentOrientation || null,
      // Forwarded to /create-html-slides-stream and downstream edit endpoints
      // so the QA pass can be skipped per the user's choice in the questionnaire.
      runQa: answers.runQa || 'yes',
    };
    console.log('[BRAND-DEBUG] handleGenerate answers.brandIdentity:', answers.brandIdentity, 'flowConfig.brandIdentityId:', flowConfig.brandIdentityId);

    // If a variation was chosen, include the style reference and pre-generated slides
    if (selectedVariationIndex !== null && variations[selectedVariationIndex]) {
      const chosen = variations[selectedVariationIndex];
      flowConfig.variationStyleReference = chosen.slides[0]?.html_source || '';
      flowConfig.variationPreGeneratedSlides = chosen.slides;
      flowConfig.variationSampleIndices = variationSampleIndices;
      flowConfig.variationDirection = chosen.direction;
      // Variation pre-gen wins — discard any early slide-1 AND the deck
      // template (mixing untemplated samples with template-guided slides
      // produces an inconsistent deck).
      flowConfig.deckTemplateId = null;
      try { earlySlide1Ref.current?.controller?.abort(); } catch { /* noop */ }
      earlySlide1Ref.current = null;
    } else if (earlySlide1Ref.current) {
      // Hand the early slide-1 to the workspace. In-memory only (carries a
      // live Promise + AbortController — serializes to {} if ever persisted).
      flowConfig.earlyFirstSlide = earlySlide1Ref.current;
    }

    // Check usage limits — monthly slide pool (e.g. 15 opus slides/month across all presentations)
    let finalOutlineSlides = outlineSlides;
    if (usageData?.usage) {
      const genTypeMap = { presentation: 'presentation', infographic: 'infographic', document: 'document', dashboard: 'dashboard', sheets: 'sheet' };
      const gt = genTypeMap[projectType] || 'presentation';
      // resolve the unpicked case the same way the request itself will
      // (getDefaultModel = kimi -> 'pro'), or the pre-gate warns about the
      // wrong quota bucket
      const modelTier = _MODEL_VALUE_TO_TIER[answers.htmlModel || getDefaultModel()] || 'flash';
      const tierUsage = usageData.usage[gt]?.[modelTier];
      const tierLabel = { flash: 'Flash', pro: 'Pro', opus: 'Opus' }[modelTier] || modelTier;

      // Per-item pool (e.g. opus slides — 10 slides/month spent across all presentations)
      if (tierUsage?.remaining != null && tierUsage.unit === 'slides') {
        if (tierUsage.remaining <= 0) {
          toast.error(`You've used all ${tierUsage.limit} ${tierLabel} slides this month. Try a different model or upgrade your plan.`, { duration: 8000 });
          return;
        }
        if (outlineSlides.length > tierUsage.remaining) {
          toast.warning(`You have ${tierUsage.remaining} ${tierLabel} slide${tierUsage.remaining === 1 ? '' : 's'} left this month. Your deck will be limited to ${tierUsage.remaining} slides.`, { duration: 8000 });
          finalOutlineSlides = outlineSlides.slice(0, tierUsage.remaining);
        }
      }
      // Per-generation pool (e.g. 3 infographics/month)
      else if (tierUsage?.remaining != null && tierUsage.remaining <= 0) {
        toast.error(`You've reached your monthly ${gt} limit. Upgrade for more.`, { duration: 8000 });
        return;
      }
      else if (tierUsage?.remaining != null && tierUsage.remaining <= 3 && tierUsage.remaining > 0) {
        toast.warning(`You have ${tierUsage.remaining} ${gt} generation${tierUsage.remaining === 1 ? '' : 's'} left this month.`);
      }
    }

    // Optional cost gate (Settings → AI & Generation) — the last stop
    // before credits are committed.
    if (!(await maybeConfirmCost({
      projectType,
      model: answers.htmlModel || getDefaultModel(),
      items: finalOutlineSlides.length || 1,
      userId,
    }))) {
      // Cancelled. The skip-review flow reaches here via ONE-SHOT auto-fire
      // effects that already burned their ref and left a 'running' chip —
      // without recovery the user is stranded with no Generate trigger
      // anywhere (r88 review P1). Re-arm the refs, stop the chip, and show
      // the outline panel (session-only — deliberately NOT persisted to
      // localStorage, this is recovery, not a preference change).
      skipOutlineAutoFiredRef.current = false;
      docPreserveAutoFiredRef.current = false;
      setSkipOutlinePreview(false);
      setMessages(prev => prev.map(m => m.chipState === 'running' ? { ...m, chipState: 'done' } : m));
      toast.info('Generation cancelled — review the outline and press Generate when ready.');
      return;
    }

    transitionToWorkspace({ title: outlineTitle, slides: finalOutlineSlides }, questionnaireData, flowConfig);
  };

  // ── Handle variation selection ──
  const handleVariationSelect = async (index) => {
    // Cost gate FIRST: everything below is destructive (aborts the live
    // variation stream, burns variationSelectedRef) — cancelling a cost
    // prompt must not kill the variation generation the user was watching
    // (r88 review P1).
    if (!(await maybeConfirmCost({
      projectType,
      model: answers.htmlModel || getDefaultModel(),
      items: outlineSlides.length || 1,
      userId,
    }))) return;
    variationSelectedRef.current = true;
    // Abort the variation SSE stream — prevents any further events (including 'complete') from firing
    if (variationAbortRef.current) {
      variationAbortRef.current.abort();
      variationAbortRef.current = null;
    }
    setSelectedVariationIndex(index);
    setMessages(prev => [...prev, { role: 'user', text: `Selected: ${variations[index]?.direction?.name || `Variation ${index + 1}`}` }]);
    // Directly build flowConfig here to avoid stale state
    const chosen = variations[index];
    const questionnaireData = {
      audience: answers.audience || null,
      presentationType: answers.presentationType || null,
      infographicType: answers.infographicType || null,
      visualStyle: answers.visualStyle || null,
      includeImages: answers.includeImages || null,
      imageStyle: answers.imageStyle || null,
      imageryMode: answers.imageryMode || answers.imagery_mode || null,
      artDirection: answers.artDirection || answers.art_direction || null,
      keepSourceDesign: !!answers._keepSourceDesign, // r176: the redesign caption reproduces the identity
      htmlModel: answers.htmlModel || getDefaultModel(),
      chartTypes: answers.chartTypes || null,
      infographicSize: answers.infographicSize || null,
      exportTarget: answers.exportTarget || null,
      specialInstructions: answers.specialInstructions || (outlineMetadata?.special_requests?.length ? outlineMetadata.special_requests.join('; ') : ''),
      customThemeRequest: answers.customThemeRequest || '',
      proposalScope: answers.proposalScope || null, // r180: frozen scope rides into the workspace + project metadata
      runQa: answers.runQa || 'yes',
    };
    const flowConfig = {
      projectType,
      generationMode: answers.generationMode || 'html',
      htmlModel: answers.htmlModel || getDefaultModel(),
      llmProvider: answers.llmProvider || outlineModel || 'openai-nano',
      prompt: submittedPrompt || prompt,
      language: answers.language || outlineMetadata?.detected_language || 'en',
      themePreset: 'ai-create',
      includeImages: answers.includeImages || 'no',
      imageStyle: answers.imageStyle || 'inline',
      imageryMode: answers.imageryMode || answers.imagery_mode || null,
      artDirection: answers.artDirection || answers.art_direction || null,
      keepSourceDesign: !!answers._keepSourceDesign, // r176: the redesign caption reproduces the identity
      logoUrl,
      webResearch,
      excelData: getMergedExcelData(),
      fileContent: getMergedContentText(),
      fileImages: attachmentRef.current?.images || null,
      redesignImage: (attachmentRef.current?.role === 'design_reference' || attachmentRef.current?.role === 'embed_asset') ? null : (attachmentRef.current?.imageData || null),
      // User images to embed verbatim into slides (role==='embed_asset').
      embedImages: collectEmbedImages(),
      variationStyleReference: chosen?.slides?.[0]?.html_source || '',
      variationPreGeneratedSlides: chosen?.slides || [],
      variationSampleIndices,
      variationDirection: chosen?.direction,
      brandIdentityId: (answers.brandIdentity && answers.brandIdentity !== 'none') ? answers.brandIdentity : null,
      // A chosen variation IS the design; mixing a template's layouts with
      // untemplated variation samples produces an inconsistent deck.
      deckTemplateId: null,
      designReferenceImages: collectDesignReferenceImageUrls(),
      proposalMode: !!proposalMode,
      proposalScope: answers.proposalScope || null, // r180
      runQa: answers.runQa || 'yes',
    };
    // Cap slides if usage limits apply (same logic as handleStartGeneration)
    let finalSlides = outlineSlides;
    if (usageData?.usage) {
      // resolve the unpicked case the same way the request itself will
      // (getDefaultModel = kimi -> 'pro'), or the pre-gate warns about the
      // wrong quota bucket
      const modelTier = _MODEL_VALUE_TO_TIER[answers.htmlModel || getDefaultModel()] || 'flash';
      const tierUsage = usageData.usage?.presentation?.[modelTier];
      const tierLabel = { flash: 'Flash', pro: 'Pro', opus: 'Opus' }[modelTier] || modelTier;
      if (tierUsage?.remaining != null && tierUsage.unit === 'slides' && finalSlides.length > tierUsage.remaining) {
        toast.warning(`You have ${tierUsage.remaining} ${tierLabel} slides left. Trimmed to ${tierUsage.remaining}.`, { duration: 6000 });
        finalSlides = finalSlides.slice(0, tierUsage.remaining);
      }
    }
    transitionToWorkspace({ title: outlineTitle, slides: finalSlides }, questionnaireData, flowConfig);
  };

  // ── Smart workspace chat — routes between edit and inquiry ──
  const getContentSummary = () => {
    // Try workspace ref data first (sheets, dashboard, document, infographic)
    const wsData = workspaceRef.current?.getData?.();
    if (wsData) {
      if (projectType === 'sheets' && wsData.columns) {
        return `Spreadsheet: ${wsData.title || 'Untitled'} — ${wsData.columns?.length || 0} columns, ${wsData.rows?.length || 0} rows`;
      }
      if (wsData.html) return `${projectType} workspace — ${wsData.title || 'Untitled'}`;
    }
    if (!workspaceData?.outline) return `${projectType} workspace`;
    const o = workspaceData.outline;
    return `${o.title || 'Untitled'} — ${o.slides?.length || 0} slides/sections`;
  };

  const getContentContext = () => {
    // Try workspace ref data first for rich context
    const wsData = workspaceRef.current?.getData?.();

    if (projectType === 'sheets' && wsData?.columns) {
      // Build detailed spreadsheet context with actual data
      const cols = wsData.columns || [];
      const rows = wsData.rows || [];
      const types = wsData.column_types || [];
      let ctx = `Spreadsheet: "${wsData.title || 'Untitled'}"\n`;
      ctx += `Columns: ${cols.map((c, i) => `${c} (${types[i] || 'text'})`).join(', ')}\n`;
      ctx += `Total rows: ${rows.length}\n\nSample data (first ${Math.min(rows.length, 20)} rows):\n`;
      rows.slice(0, 20).forEach((row, i) => {
        ctx += `Row ${i + 1}: ${cols.map((c, ci) => `${c}=${row[ci] ?? ''}`).join(', ')}\n`;
      });
      if (wsData.charts?.length) {
        ctx += `\nCharts: ${wsData.charts.map(c => `${c.type} chart of ${c.title || c.dataColumn || 'data'}`).join(', ')}`;
      }
      return ctx.slice(0, 6000);
    }

    if ((projectType === 'dashboard' || projectType === 'document' || projectType === 'infographic') && wsData?.html) {
      // Extract text content from HTML for context
      const div = document.createElement('div');
      // Strip scripts and style tags before extracting text
      const cleanHtml = wsData.html.replace(/<script[\s\S]*?<\/script>/gi, '').replace(/<style[\s\S]*?<\/style>/gi, '');
      div.innerHTML = cleanHtml;
      const textContent = (div.textContent || div.innerText || '').replace(/\s+/g, ' ').trim();
      return `${projectType.charAt(0).toUpperCase() + projectType.slice(1)}: "${wsData.title || 'Untitled'}"\n\nFull content extracted from the generated HTML:\n${textContent.slice(0, 5000)}`;
    }

    // Try reading HTML directly from workspace iframe as fallback
    if ((projectType === 'dashboard' || projectType === 'document' || projectType === 'infographic')) {
      const iframeEl = document.querySelector('iframe[title*="Preview"], iframe[title*="preview"]');
      if (iframeEl?.contentDocument?.body) {
        const textContent = (iframeEl.contentDocument.body.innerText || '').replace(/\s+/g, ' ').trim();
        if (textContent.length > 50) {
          return `${projectType.charAt(0).toUpperCase() + projectType.slice(1)} content:\n${textContent.slice(0, 5000)}`;
        }
      }
    }

    // Presentations: try to get actual generated slide content via getData()
    const wsSlideData = workspaceRef.current?.getData?.();
    if (wsSlideData?.slides?.length) {
      const title = wsSlideData.title || workspaceData?.outline?.title || 'Untitled';
      let ctx = `Presentation: "${title}" (${wsSlideData.slides.length} slides)\n\n`;
      // Include text content for each slide
      ctx += wsSlideData.slides.map(s =>
        `Slide ${s.index}: ${s.title}\n${s.content || ''}`
      ).join('\n\n');
      // Include HTML source for design/color/style analysis (first 2 slides, truncated)
      const htmlSlides = wsSlideData.slides.filter(s => s.html_source).slice(0, 2);
      if (htmlSlides.length) {
        ctx += '\n\n--- SLIDE HTML (for design/color analysis) ---\n';
        ctx += htmlSlides.map(s =>
          `Slide ${s.index} HTML:\n${s.html_source.slice(0, 3000)}`
        ).join('\n\n');
      }
      return ctx.slice(0, 20000);
    }

    // Fall back to outline data if workspace not rendered yet
    if (!workspaceData?.outline) return '';
    const slides = workspaceData.outline.slides || [];
    return slides.map((s, i) => `${i + 1}. ${s.title || 'Untitled'}: ${s.content || s.description || ''}`).join('\n').slice(0, 20000);
  };

  const handleWorkspaceChat = async () => {
    const message = prompt.trim();
    if (!message && !attachment) return;
    if (directFeedActiveRef.current) {
      // r140b P1: the deck is still streaming in — an edit now would splice
      // slides while the feed writes at the original indices
      toast.info('Still designing your slides — chat edits unlock when the deck finishes.');
      return;
    }
    if (isFileParsing) {
      // r141: the workspace chat keeps the BOUNCE — its handler reads the
      // attachment from a render-time closure, so awaiting here would
      // proceed with a stale null attachment (and this path never
      // classifies, so it cannot duplicate rounds).
      toast.error('Still analyzing your file — please wait a moment.');
      return;
    }
    if (isSubmitInFlightRef.current) {
      toast.error("I'm still working on your last request. Give me a moment.");
      return;
    }
    isSubmitInFlightRef.current = true;
    setIsSubmitInFlight(true);
    try {
      await handleWorkspaceChatInner();
    } finally {
      isSubmitInFlightRef.current = false;
      setIsSubmitInFlight(false);
    }
  };

  const handleWorkspaceChatInner = async () => {
    const message = prompt.trim();
    setPrompt('');
    const currentAttachment = attachment;
    // r145: extras used to be CLEARED UNREAD — drop three images, two vanish
    const currentExtras = (extraAttachmentsRef.current?.length ? extraAttachmentsRef.current : extraAttachments) || [];
    setAttachment(null); // Clear attachment after capturing
    setExtraAttachments([]); // Also clear any extra attachments

    // Build display message with attachment info
    const displayText = currentAttachment
      ? `${message || ''}${message ? '\n' : ''}📎 ${currentAttachment.name}`
      : message;
    setMessages(prev => [...prev, { role: 'user', text: displayText }]);

    // Build context from attachment if present
    let attachmentContext = '';
    let referenceImages = [];
    if (currentAttachment) {
      if (currentAttachment.type === 'image' && currentAttachment.imageData) {
        referenceImages = [currentAttachment.imageData];
        attachmentContext = `\n\n[Attached image: ${currentAttachment.name}] — use this as a reference/asset on the slide.`;
      } else if (currentAttachment.content) {
        attachmentContext = `\n\n[Attached file: ${currentAttachment.name}]\n${currentAttachment.content}`;
      } else if (currentAttachment.excelData) {
        const sheets = currentAttachment.excelData;
        attachmentContext = `\n\n[Attached spreadsheet: ${currentAttachment.name}]\n` +
          sheets.map(s => `Sheet "${s.sheet_name}": ${s.headers.join(', ')} (${s.total_rows} rows)`).join('\n');
      }
    }
    // r145: fold the EXTRA attachments in (images ride as references, text
    // as context) and cap the total context so a pasted book can't blow up
    // the planner prompt.
    for (const ex of currentExtras) {
      if (!ex) continue;
      if (ex.type === 'image' && ex.imageData) {
        referenceImages.push(ex.imageData);
        attachmentContext += `\n\n[Attached image: ${ex.name}] — use as a reference/asset.`;
      } else if (ex.content) {
        attachmentContext += `\n\n[Attached file: ${ex.name}]\n${ex.content}`;
      }
    }
    if (attachmentContext.length > 12000) attachmentContext = attachmentContext.slice(0, 12000) + '\n[...attachment truncated]';

    // Show thinking indicator while the planner works
    setMessages(prev => [
      ...prev.map(m => m.chipState === 'running' ? { ...m, chipState: 'done' } : m),
      { role: 'bot', chipLabel: 'Planning the edit', chipState: 'running', _planChip: true },
    ]);

    // r145: a drawn region no longer BYPASSES the planner — the bypass sent
    // even questions ("what font is this?") to the edit model, which mutated
    // the slide trying to answer. The planner now knows a region is active
    // (edits stay region-scoped; questions stay questions), and the region's
    // own slide becomes the target.
    const chatCtl = workspaceRef.current?.getChatControls?.();

    try {
      // Step 1: Plan operations via backend LLM
      const wsData = workspaceRef.current?.getData?.();
      const slideCount = wsData?.slides?.length || 0;
      const currentSlide = workspaceRef.current?.getChatControls?.()?.currentSlideIndex ?? 0;
      const slideTitles = (wsData?.slides || []).map((s, i) => ({ index: i + 1, title: s?.title || '(untitled)' }));

      const planResp = await fetch(withBackend('/api/chat/plan'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({
          message: message + attachmentContext,
          workspace_type: projectType,
          content_summary: getContentSummary(),
          slide_count: slideCount,
          current_slide: currentSlide,
          slide_titles: slideTitles,
          brand_identity_id: (answers.brandIdentity && answers.brandIdentity !== 'none') ? answers.brandIdentity : '',
          original_prompt: submittedPrompt || prompt,
          chat_model: CHAT_PLAN_MODEL[chatModel] || 'haiku',
          // r145: the planner's own confirm-then-execute protocol needs the
          // conversation; pronoun follow-ups planned blind without it.
          chat_history: messages
            .filter(m => (m.role === 'user' || m.role === 'bot') && (m.text || m.chipLabel))
            .slice(-6)
            .map(m => ({ role: m.role, text: (m.text || m.chipLabel || '').slice(0, 300) })),
          region_active: !!chatCtl?.hasActiveRegion,
        }),
      });
      if (!planResp.ok) throw new Error(`Planner returned ${planResp.status}`);
      const { plan } = await planResp.json();
      const ops = plan?.operations || [];
      // r145: with a region active, an untargeted edit op belongs to the
      // slide the region was DRAWN on.
      if (chatCtl?.hasActiveRegion && chatCtl.regionSlideIdx != null) {
        for (const op of ops) {
          if (op.type === 'edit' && op.scope !== 'all' && op.target_slide == null) {
            op.target_slide = chatCtl.regionSlideIdx + 1;
          }
        }
      }

      // Mark the planning chip done now that we have the plan
      setMessages(prev => prev.map(m =>
        m._planChip && m.chipState === 'running' ? { ...m, chipState: 'done' } : m
      ).filter(m => !m.isLoading));

      if (plan?.plan_failed) {
        // Planner errored (e.g. model failure). Do NOT silently run the single-op
        // fallback — that mangles a multi-part request onto the current slide.
        setMessages(prev => [...prev, {
          role: 'bot',
          text: "I couldn't plan that edit just now — the planner hit an error. Please try again in a moment, and if it's a big change, splitting it into a couple of smaller requests helps.",
          isError: true,
        }]);
      } else if (plan?.is_improvement) {
        // Brainstorm — vision-aware suggestions for a specific slide.
        // Falls through to /api/chat/improve-slide. Frontend renders the
        // returned suggestions list with Apply buttons; each Apply maps to
        // /edit-html-slide via the existing edit op flow.
        setMessages(prev => [
          ...prev.map(m => m.chipState === 'running' ? { ...m, chipState: 'done' } : m),
          { role: 'bot', chipLabel: 'Brainstorming improvements', chipState: 'running', _improveChip: true },
        ]);
        const targetSlide1 = plan?.target_slide;
        const wsData = workspaceRef.current?.getData?.();
        const slidesArr = wsData?.slides || [];
        const slideIdx = targetSlide1 != null ? (targetSlide1 - 1) : (workspaceRef.current?.getChatControls?.()?.currentSlideIndex ?? 0);
        const targetSlideObj = slidesArr[slideIdx] || null;
        const slideHtml = targetSlideObj?.html_source || '';
        const slideScreenshot = targetSlideObj?.preview_url || '';
        try {
          const improveResp = await fetch(withBackend('/api/chat/improve-slide'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
            body: JSON.stringify({
              slide_html: slideHtml,
              slide_screenshot: slideScreenshot,
              slide_index: slideIdx,
              user_message: message,
              brand_identity_id: (answers.brandIdentity && answers.brandIdentity !== 'none') ? answers.brandIdentity : '',
              chat_model: CHAT_PLAN_MODEL[chatModel] || 'haiku',
            }),
          });
          const data = await improveResp.json();
          setMessages(prev => {
            const updated = prev.map(m =>
              m._improveChip && m.chipState === 'running' ? { ...m, chipState: 'done' } : m
            ).filter(m => !m.isLoading);
            const suggestions = Array.isArray(data?.suggestions) ? data.suggestions : [];
            if (suggestions.length === 0) {
              return [...updated, { role: 'bot', text: 'I couldn\'t find anything to suggest right now.' }];
            }
            return [...updated, {
              role: 'bot',
              isImprovement: true,
              improveSummary: data?.summary || '',
              suggestions,
              appliedIds: [],
              targetSlideIndex: slideIdx,
            }];
          });
        } catch (e) {
          setMessages(prev => prev.filter(m => !m._improveChip && !m.isLoading).concat({
            role: 'bot', text: 'Sorry, I couldn\'t generate suggestions right now.',
          }));
        }
      } else if (plan?.is_inquiry || ops.length === 0) {
        // Inquiry — get a conversational answer
        setMessages(prev => [
          ...prev.map(m => m.chipState === 'running' ? { ...m, chipState: 'done' } : m),
          { role: 'bot', chipLabel: 'Thinking', chipState: 'running', _inquiryChip: true },
        ]);
        const recentHistory = messages.filter(m => m.role === 'user' || m.role === 'bot').slice(-6);
        const inquiryResp = await fetch(withBackend('/api/chat/inquiry'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
          body: JSON.stringify({
            message: message + attachmentContext,
            workspace_type: projectType,
            content_context: getContentContext(),
            chat_history: recentHistory,
            user_id: userId || '',
            brand_identity_id: (answers.brandIdentity && answers.brandIdentity !== 'none') ? answers.brandIdentity : '',
            chat_model: CHAT_PLAN_MODEL[chatModel] || 'haiku',
          }),
        });
        const { response } = await inquiryResp.json();
        setMessages(prev => {
          const updated = prev.map(m =>
            m._inquiryChip && m.chipState === 'running' ? { ...m, chipState: 'done' } : m
          ).filter(m => !m.isLoading);
          return [...updated, { role: 'bot', text: response || 'I couldn\'t generate a response.' }];
        });
      } else if (ops.length === 1) {
        // Single op — execute directly (no confirmation needed). AWAITED so
        // the submit latch covers the whole execution (r145).
        await executePlanOps(ops, message + attachmentContext, referenceImages);
      } else {
        // Multi-op plan — show confirmation. Stale confirmations lose their
        // buttons (they'd otherwise wire to the NEW pendingPlan).
        // r169: the ops render as a CHECKLIST — untick an operation to drop
        // it from the run (checkedOps is index-aligned with plan.operations).
        setMessages(prev => [...prev.map(m => m.isPlanConfirmation ? { ...m, isPlanConfirmation: false } : m), {
          role: 'bot',
          text: `**Plan: ${plan.plan_summary || `${ops.length} operations`}**\n\nUntick anything you don't want, then apply.`,
          isPlanConfirmation: true,
          planOps: ops.map(op => ({ type: op.type, brief: op.brief || '', target: op.target_slide ?? null })),
          checkedOps: ops.map(() => true),
        }]);
        setPendingPlan({ plan, originalMessage: message + attachmentContext, referenceImages });
      }
    } catch (err) {
      console.error('[handleWorkspaceChat] error:', err);
      setPrompt(message); // r145: never eat the user's typed request on a failure
      setMessages(prev => {
        const stamped = prev.map(m => m.chipState === 'running' ? { ...m, chipState: 'done' } : m);
        const filtered = stamped.filter(m => !m.isLoading);
        return [...filtered, { role: 'bot', text: 'Something went wrong — your message was restored to the input. Please try again.', isError: true }];
      });
    }

    setWsRegionMode(false);
    setWsApplyAll(false);
    // r145: the child's flags too — a latched apply-all used to hijack the
    // NEXT single-slide edit into a deck-wide rewrite.
    workspaceRef.current?.setApplyAll?.(false);
    workspaceRef.current?.setRegionMode?.(false);
  };

  // Execute a list of planned operations sequentially
  const executePlanOps = async (ops, originalMessage, referenceImages = []) => {
    // Fail LOUDLY when the mounted workspace can't accept edits — every op
    // below is guarded on the ref method existing, so a workspace without
    // sendEdit/sendStructural used to make the whole batch a silent no-op.
    if (ops.length && !workspaceRef.current?.sendEdit && !workspaceRef.current?.sendStructural) {
      toast.error("This workspace doesn't support AI edits.");
      return;
    }
    const pushOpChip = (label) => {
      setMessages(prev => [
        ...prev.map(m => m.chipState === 'running' ? { ...m, chipState: 'done' } : m),
        { role: 'bot', chipLabel: label, chipState: 'running', _opChip: true },
      ]);
    };
    // Snapshot every slide this batch will edit, BEFORE we start running ops.
    // Used by the chat-level "Revert this batch" card we render after the
    // batch completes. Add/delete/reorder don't have per-slide undo (they
    // change the deck shape) — so only edit ops are captured here.
    const batchSnapshots = [];
    if (workspaceRef.current?.getSlideSnapshot) {
      const seenSlides = new Set();
      for (const op of ops) {
        if (op.type === 'edit' && op.target_slide != null && !seenSlides.has(op.target_slide)) {
          seenSlides.add(op.target_slide);
          const snap = workspaceRef.current.getSlideSnapshot(op.target_slide - 1);
          if (snap) batchSnapshots.push(snap);
        }
      }
    }
    // r145: resolve every numbered target to a STABLE ID up front — the
    // plan numbers slides against the pre-plan deck, and each splice used
    // to shift every later op ("delete slides 3 and 5" deleted 3 then the
    // original 6).
    const preSlides = workspaceRef.current?.getData?.()?.slides || [];
    const uidFor = (n) => (n != null ? preSlides[n - 1]?._uid ?? null : null);
    const stampOp = (ok) => setMessages(prev => prev.map(m =>
      m._opChip && m.chipState === 'running'
        ? { ...m, chipState: 'done', ...(ok ? {} : { chipLabel: `${m.chipLabel} — failed` }) }
        : m
    ));
    // r157: ONE edit call per slide — the planner sometimes splits a
    // compound request into several sequential edits of the SAME slide, and
    // each pass re-judged (and re-worked) the whole request until the slide
    // drifted (the 30/08 pricing-slide incident: four ops on slide 5, each
    // refining its siblings' work). Merge consecutive same-slide edit ops
    // into a single numbered instruction; structural ops still break runs.
    const mergedOps = [];
    for (const op of ops) {
      const prev = mergedOps[mergedOps.length - 1];
      if (op.type === 'edit' && op.scope !== 'all' && op.target_slide != null
          && prev && prev.type === 'edit' && prev.scope !== 'all'
          && prev.target_slide === op.target_slide) {
        prev.brief = `${prev._merged ? prev.brief : `- ${prev.brief || ''}`}\n- ${op.brief || ''}`;
        prev._merged = true;
        if (Array.isArray(op.context_slides)) {
          prev.context_slides = [...new Set([...(prev.context_slides || []), ...op.context_slides])];
        }
        continue;
      }
      mergedOps.push({ ...op }); // clone — merging must never mutate the planner's array
    }
    // r160: a multi-op plan is a dangerous moment — checkpoint the saved
    // state first (fire-and-forget, fail-open; single-op plans skip it,
    // per-slide undo already covers those).
    if (mergedOps.length > 1) workspaceRef.current?.snapshotVersion?.('before batch edit');
    const ledger = [];
    setPlanRunning(true);
    planCancelRef.current = false;
    try {
      for (const op of mergedOps) {
        if (planCancelRef.current) break; // r145: user hit Stop
        let r = { ok: false };
        const editMsg = op.brief
          ? `${op.brief}\n\nUser's exact request (authoritative for quoted text, names, and colors): "${originalMessage}"`
          : originalMessage; // r145: verbatim text rides every brief
        if (op.type === 'add' && workspaceRef.current?.sendStructural) {
          pushOpChip(`Adding slide${op.brief ? ` — ${op.brief.slice(0, 60)}` : ''}`);
          r = await workspaceRef.current.sendStructural({
            message: originalMessage,
            operation: 'add',
            position: op.position || 'end',
            target_slide: op.target_slide,
            content_brief: op.brief,
            reference_images: referenceImages,
          });
        } else if ((op.type === 'delete' || op.type === 'duplicate') && workspaceRef.current?.sendStructural) {
          pushOpChip(`${op.type === 'delete' ? 'Deleting' : 'Duplicating'} slide ${op.target_slide || 'current'}`);
          r = await workspaceRef.current.sendStructural({
            message: originalMessage,
            operation: op.type,
            target_slide: op.target_slide,
            target_uid: uidFor(op.target_slide),
          });
        } else if (op.type === 'reorder' && workspaceRef.current?.sendStructural) {
          pushOpChip(`Moving slide ${op.target_slide || 'current'}`);
          r = await workspaceRef.current.sendStructural({
            message: originalMessage,
            operation: 'reorder',
            target_slide: op.target_slide,
            target_uid: uidFor(op.target_slide),
            to_position: op.to_position,
          });
        } else if (op.type === 'edit' && workspaceRef.current?.sendEdit) {
          // r146: cross-slide context — ship the referenced slides' HTML +
          // screenshots so the edit model can SEE the source material.
          const ctxSlides = Array.isArray(op.context_slides)
            ? op.context_slides
                .map(n => ({ index: n, html_source: preSlides[n - 1]?.html_source || '', preview_url: preSlides[n - 1]?.preview_url || '' }))
                .filter(c => c.html_source || c.preview_url)
                .slice(0, 3)
            : [];
          if (op.scope === 'all') {
            pushOpChip('Editing all slides');
            r = await workspaceRef.current.sendEdit(editMsg, { applyAll: true, referenceImages });
          } else if (op.target_slide != null) {
            pushOpChip(`Editing slide ${op.target_slide}`);
            // r146b: uid threads into the EDIT too — a structural op earlier
            // in the plan splices the array before this edit lands
            r = await workspaceRef.current.sendEdit(editMsg, { targetSlide: op.target_slide - 1, targetUid: uidFor(op.target_slide), referenceImages, contextSlides: ctxSlides });
          } else {
            pushOpChip('Applying edit');
            r = await workspaceRef.current.sendEdit(editMsg, { referenceImages, contextSlides: ctxSlides });
          }
        }
        // r146b: only CPW returns {ok} — the other workspaces' edit handlers
        // return undefined on success; stamping those "failed" was a trust
        // regression introduced by the honesty fix itself.
        const okFlag = r == null ? true : r.ok !== false;
        ledger.push({ type: op.type, target: op.target_slide ?? null, ok: okFlag });
        stampOp(okFlag);
        // r169: per-change diff card — before/after previews with a revert
        // that flips to redo. Only single-slide edits return a diff (sweeps
        // keep the batch-revert card below).
        if (okFlag && op.type === 'edit' && r?.diff?.before?.html_source && r?.diff?.after?.html_source) {
          const d = r.diff;
          setMessages(prev => [...prev, {
            role: 'bot',
            isEditDiff: true,
            diff: d,
            diffState: 'applied',
            text: `Edited slide ${(d.slideIdx ?? 0) + 1}.`,
          }]);
        }
        if (r && r.quota) {
          // out of quota — stop burning through the remaining ops
          setMessages(prev => [...prev, { role: 'bot', text: 'Usage limit reached — the remaining operations were not run.', isError: true }]);
          break;
        }
        await new Promise(res => setTimeout(res, 150));
      }
    } finally {
      setPlanRunning(false);
    }
    stampOp(true);
    const okCount = ledger.filter(l => l.ok).length;
    const failCount = ledger.length - okCount;
    if (planCancelRef.current && ledger.length < mergedOps.length) {
      setMessages(prev => [...prev, { role: 'bot', text: `Stopped — ${okCount} of ${mergedOps.length} operations completed.` }]);
    } else if (failCount > 0) {
      setMessages(prev => [...prev, {
        role: 'bot',
        text: `${okCount}/${ledger.length} operations succeeded — ${failCount} failed (see above).`,
        isError: true,
      }]);
    }
    // Truthful batch-revert card: only edits that actually SUCCEEDED count.
    const okEditTargets = new Set(ledger.filter(l => l.ok && l.type === 'edit' && l.target != null).map(l => l.target));
    const revertSnapshots = batchSnapshots.filter(s => okEditTargets.has(s.slideIdx + 1));
    if (revertSnapshots.length >= 2) {
      setMessages(prev => [...prev, {
        role: 'bot',
        isBatchRevert: true,
        batchSnapshots: revertSnapshots,
        text: `Edited ${revertSnapshots.length} slides.`,
      }]);
    }
  };

  // ── Direct pipeline: generate slides straight from source page images ──
  // Bypasses the outline step entirely when the user's intent is preserve/restructure
  // and their chosen HTML model supports vision (opus/pro/flash).
  const runDirectPipeline = async (clarification, userMessage, seedAnswers = {}) => {
    // Merge pages from every content-role attachment (primary + extras) in
    // upload order so multi-file decks generate end-to-end.
    const pages = getScopedContentPages();
    if (pages.length === 0) {
      toast.error('No page images available for direct generation');
      return false;
    }
    const usableModels = new Set(['opus', 'pro', 'flash', 'sonnet', 'sol', 'astra', 'kimi']);
    const chosenModel = usableModels.has(seedAnswers.htmlModel) ? seedAnswers.htmlModel : 'opus';
    const seedBrandId = seedAnswers.brandIdentity && seedAnswers.brandIdentity !== 'none' ? seedAnswers.brandIdentity : null;
    const seedSpecialInstructions = seedAnswers.specialInstructions || '';
    // Snapshot the conversation at the moment generation starts — don't keep
    // re-reading state because the progress messages we emit during per-page
    // generation would bloat the payload on every call.
    const conversationSnapshot = buildConversationHistory();
    // Design-reference images collected across ALL attachments (primary + extras).
    // Any file Haiku classified as design_reference contributes its image data.
    const designReferenceImages = [];
    const allAtts = [
      attachmentRef.current || attachment,
      ...(extraAttachmentsRef.current?.length ? extraAttachmentsRef.current : extraAttachments),
    ].filter(Boolean);
    for (const att of allAtts) {
      if (att?.role !== 'design_reference') continue;
      if (att.imageData) designReferenceImages.push(att.imageData);
      if (Array.isArray(att.pages)) {
        att.pages.slice(0, 8).forEach(p => { if (p?.image_data_url) designReferenceImages.push(p.image_data_url); });
      }
    }
    // Cap to a sensible max to avoid runaway prompt size
    const designRefSlice = designReferenceImages.slice(0, 8);

    // r140: mount the workspace immediately and stream slides into it —
    // the user watched a bare chat for the whole run before.
    directCancelRef.current = false;
    directFeedActiveRef.current = true;
    const _feedTitle = attachmentRef.current?.name?.replace(/\.[^.]+$/i, '') || 'Untitled Presentation';
    // r149: create the placeholder row FIRST so the run exists in Recents
    // from slide 1 and survives a closed tab. Fail-open: no row, old
    // behavior (in-memory only).
    feedPidRef.current = null;
    feedChainRef.current = Promise.resolve();
    try {
      const _pidResp = await fetch(withBackend('/api/projects/direct-feed'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({ name: _feedTitle, total_slides: pages.length, user_id: userId || '' }),
      });
      if (_pidResp.ok) {
        const _pd = await _pidResp.json();
        if (_pd.project_id) {
          feedPidRef.current = _pd.project_id;
          onProjectCreated?.(_pd.project_id, _feedTitle);
        }
      }
    } catch { /* fail-open */ }
    setWorkspaceData({
      outline: { title: _feedTitle, slides: [] },
      questionnaireData: { htmlModel: chosenModel, runQa: seedAnswers.runQa || 'yes', customThemeRequest: seedAnswers.customThemeRequest || '' },
      flowConfig: {
        projectType: 'presentation',
        generationMode: 'html',
        htmlModel: chosenModel,
        prompt: userMessage,
        language: 'auto',
        brandIdentityId: seedBrandId,
        runQa: seedAnswers.runQa || 'yes',
      },
      savedSlides: [],
      savedTitle: _feedTitle,
      metadata: { direct_pipeline: true, source_file: attachmentRef.current?.name, brand_identity_id: seedBrandId },
      feedProjectId: feedPidRef.current,
      externalFeed: true,
      externalFeedTotal: pages.length,
    });
    setFlowPhase('workspace');
    const modelLabel = { opus: 'Claude Opus 5', sonnet: 'Claude Sonnet 5', pro: 'Gemini 3 Pro', flash: 'Gemini 3 Flash', sol: 'GPT-5.6 Sol', astra: 'GPT-6 Astra', kimi: 'Kimi K3', gpt55: 'GPT-5.5', gpt: 'GPT-5.4' }[chosenModel] || chosenModel.toUpperCase();
    // Rough time estimates per model, per slide, with 3-concurrency factored in
    const secPerSlide = { opus: 35, sonnet: 25, pro: 25, flash: 15, sol: 40, astra: 45, kimi: 30 }[chosenModel] || 30;
    const estMin = Math.max(1, Math.round((pages.length * secPerSlide) / 3 / 60));
    const headerMsg = {
      role: 'bot',
      chipLabel: `Generating ${getUnitLabel(projectType, false)} 0 of ${pages.length} (${modelLabel}, ~${estMin} min)`,
      chipState: 'running',
      _directProgress: true,
      _progressId: `dp-${Date.now()}`,
    };
    setMessages(prev => [
      ...prev.map(m => m.chipState === 'running' ? { ...m, chipState: 'done' } : m),
      headerMsg,
    ]);

    const total = pages.length;
    const slides = new Array(total).fill(null);
    // Each merged page already has a .text field populated at extraction time
    // (PyMuPDF + python-pptx overlay for PPTX). Use that directly — no need
    // to parse the aggregate text with markers.
    const pageTextByNum = {};
    for (const p of pages) {
      if (p?.page && p?.text) pageTextByNum[p.page] = p.text;
    }

    let completedCount = 0;
    let failedCount = 0;
    // If the user picked a design variation, use its first rendered slide as
    // the initial style anchor for every per-page Opus call. Otherwise the
    // first successful slide becomes the anchor for subsequent pages.
    const chosenVariation = (selectedVariationIndex !== null && variations[selectedVariationIndex]) ? variations[selectedVariationIndex] : null;
    // r159 belt (server also strips): never ship base64 payloads or an
    // unbounded anchor over the wire — 23 requests x 3MB was real traffic.
    const _stripAnchor = (h) => (h || '')
      .replace(/data:[a-zA-Z0-9.+/-]+;base64,[A-Za-z0-9+/=]{256,}/g, 'data:,')
      .slice(0, 60000);
    let styleAnchor = _stripAnchor(chosenVariation?.slides?.[0]?.html_source || '');
    // r159b: the SAME asset embedded on many source pages is deck furniture
    // (recurring screenshot/watermark) — restoring it onto every redesigned
    // slide pasted it "multiple times" (30/08). Keep its first two
    // occurrences, drop the rest. Identity = base64 tail (cheap, stable).
    const _embedByPage = new Map();
    {
      const _seen = new Map();
      for (const pg of pages) {
        const kept = (Array.isArray(pg?.embedded_images) ? pg.embedded_images : []).filter(im => {
          const k = (im?.data_url || '').slice(-96);
          if (!k) return false;
          const n = (_seen.get(k) || 0) + 1;
          _seen.set(k, n);
          return n <= 2;
        });
        _embedByPage.set(pg, kept);
      }
    }
    const updateProgressLine = () => {
      const allDone = completedCount + failedCount >= total;
      setMessages(prev => prev.map(m =>
        m._progressId === headerMsg._progressId
          ? {
              ...m,
              chipLabel: allDone
                ? `Generated ${completedCount}/${total} ${getUnitLabel(projectType, false)}${total === 1 ? '' : 's'}${failedCount ? ` · ${failedCount} failed` : ''}`
                : `Generating ${getUnitLabel(projectType, false)} ${completedCount} of ${total} (${modelLabel})${failedCount ? ` · ${failedCount} failed` : ''}`,
              chipState: allDone ? 'done' : 'running',
            }
          : m
      ));
    };

    const genOne = async (page, i) => {
      try {
        const resp = await fetch(withBackend('/api/generate-slide-direct'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            page_image_data_url: page.image_data_url,
            page_text: pageTextByNum[page.page] || '',
            page_number: page.page,
            total_pages: total,
            prompt: userMessage,
            content_relationship: clarification.value,
            html_model: chosenModel,
            style_reference_html: styleAnchor,
            brand_identity_id: seedBrandId || '',
            special_instructions: seedSpecialInstructions,
            // Per-page embedded images (photos/logos/charts from the source)
            embedded_images: _embedByPage.get(page) ?? (page.embedded_images || []),
            design_reference_images: designRefSlice,
            conversation_history: conversationSnapshot,
            user_id: userId || '',
          }),
        });
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          throw new Error(err.error || `HTTP ${resp.status}`);
        }
        const data = await resp.json();
        // Extract title from the generated HTML (first h1/h2/title tag)
        let title = `Slide ${page.page}`;
        const titleMatch = data.html_source?.match(/<h1[^>]*>([\s\S]*?)<\/h1>|<h2[^>]*>([\s\S]*?)<\/h2>/i);
        if (titleMatch) {
          const raw = (titleMatch[1] || titleMatch[2] || '').replace(/<[^>]+>/g, '').trim();
          if (raw) title = raw.slice(0, 80);
        }
        slides[i] = {
          html_source: data.html_source,
          preview_url: data.preview_url,
          title,
          slide_number: page.page,
        };
        completedCount += 1;
        if (!styleAnchor && data.html_source) styleAnchor = _stripAnchor(data.html_source);
        // r140: stream the slide into the mounted workspace
        try { workspaceRef.current?.adoptExternalSlide?.(i, slides[i], completedCount + failedCount, total); } catch { /* feed is best-effort */ }
        // r149: persist as we go (serialized — the backend RMWs the row)
        if (feedPidRef.current) {
          const _pSlide = slides[i];
          const _pIdx = i;
          feedChainRef.current = feedChainRef.current.then(async () => {
            try {
              await fetch(withBackend(`/api/projects/direct-feed/${feedPidRef.current}`), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
                body: JSON.stringify({ op: 'slide', index: _pIdx, slide: _pSlide, user_id: userId || '' }),
              });
            } catch { /* persistence is best-effort */ }
          });
        }
      } catch (err) {
        console.error(`[DIRECT] page ${page.page} failed:`, err);
        failedCount += 1;
      }
      updateProgressLine();
    };

    // r140: slide 1 runs ALONE to establish the style anchor — with a
    // 3-wide start, slides 1-3 all generated anchorless and could diverge.
    const pending = pages.map((p, i) => [p, i]);
    if (!styleAnchor && pending.length > 1 && !directCancelRef.current) {
      const [p0, i0] = pending.shift();
      await genOne(p0, i0);
    }
    // Concurrency-limited runner (3 in flight)
    const queue = pending.map(([p, i]) => () => genOne(p, i));
    const CONCURRENCY = 3;
    const workers = Array(Math.min(CONCURRENCY, queue.length)).fill(null).map(async () => {
      while (queue.length > 0) {
        if (directCancelRef.current) return; // user stopped — keep finished slides
        const task = queue.shift();
        if (task) await task();
      }
    });
    await Promise.all(workers);
    directFeedActiveRef.current = false;
    // r140b: finalize the progress chip on EVERY exit — a cancel abandons
    // the queue before updateProgressLine's all-done condition can fire,
    // leaving the chip shimmering forever
    setMessages(prev => prev.map(m =>
      m._progressId === headerMsg._progressId && m.chipState === 'running'
        ? { ...m, chipLabel: `Generated ${completedCount}/${total} ${getUnitLabel(projectType, false)}${total === 1 ? '' : 's'}${failedCount ? ` · ${failedCount} failed` : ''}`, chipState: 'done' }
        : m
    ));

    const successSlides = slides.filter(Boolean);
    if (successSlides.length === 0) {
      setMessages(prev => [...prev, directCancelRef.current
        ? { role: 'bot', text: 'Stopped before any slides were generated.' }
        : { role: 'bot', text: 'All slide generations failed. Try again or use the standard flow.', isError: true }]);
      setWorkspaceData(null); // r140: unmount the fed workspace
      setFlowPhase('prompt');
      // r149: close out the placeholder row so it doesn't sit 'generating'
      if (feedPidRef.current) {
        try {
          await feedChainRef.current;
          await fetch(withBackend(`/api/projects/direct-feed/${feedPidRef.current}`), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
            body: JSON.stringify({ op: 'fail', cancelled: directCancelRef.current, user_id: userId || '' }),
          });
        } catch { /* best-effort */ }
      }
      return false;
    }

    // r140: the workspace is already mounted and fed — close the feed (review
    // mode + autosave live in the child); no remount, no data reset.
    const title = _feedTitle;
    const outline = {
      title,
      slides: successSlides.map((s, i) => ({
        slide_number: i + 1,
        title: s.title,
        content: '',
        bullet_points: [],
        slide_type: 'content',
      })),
    };
    const flowConfig = {
      projectType: 'presentation',
      generationMode: 'html',
      htmlModel: chosenModel,
      prompt: userMessage,
      language: 'auto',
      brandIdentityId: seedBrandId,
      runQa: seedAnswers.runQa || 'yes',
    };
    // r149: flip the row OUT of 'generating' BEFORE the child's autosave —
    // the whole-deck save endpoint refuses rows that look job-owned.
    if (feedPidRef.current) {
      try {
        await feedChainRef.current;
        await fetch(withBackend(`/api/projects/direct-feed/${feedPidRef.current}`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
          body: JSON.stringify({ op: 'finish', user_id: userId || '' }),
        });
      } catch { /* best-effort */ }
    }
    try { workspaceRef.current?.finishExternalGeneration?.(); } catch { /* noop */ }
    setMessages(prev => [...prev, {
      role: 'bot',
      text: `Ready — ${successSlides.length}/${total} slide${successSlides.length === 1 ? '' : 's'} generated${failedCount ? ` (${failedCount} failed)` : ''}${directCancelRef.current ? ' (stopped early)' : ''}.`,
    }]);
    if (onOutlineReady) onOutlineReady(outline, { htmlModel: chosenModel }, flowConfig);
    return true;
  };

  // ── Handle intent-clarification button click ──
  const handleIntentAnswer = (msgIdx, option, context) => {
    // Mark the clarification message as resolved so buttons hide
    setMessages(prev => prev.map((m, i) =>
      i === msgIdx ? { ...m, isIntentClarification: false, text: `${m.text}\n\n→ ${option.label}` } : m
    ));
    setMessages(prev => [...prev, { role: 'user', text: option.label }]);

    // Special "upload" answer: user doesn't have a file yet, offered to upload.
    // Open the file picker and don't re-enter handleSubmit — they'll re-submit
    // naturally once the file is attached and parsed.
    if (option.value === 'upload') {
      setMessages(prev => [...prev, {
        role: 'bot',
        text: 'Drop your file in the chat or tap the + icon below to attach it. I\'ll pick up from there.',
      }]);
      setTimeout(() => fileInputRef.current?.click(), 150);
      return;
    }

    // Mode-mismatch answers (round 74): switch remounts the right workspace
    // with the prompt carried over (auto-submits); keep re-enters as a
    // clarification so the intent pass — and this check-in — don't re-run.
    if (String(option.value).startsWith('__switch__:')) {
      const targetType = String(option.value).split(':')[1];
      onSwitchProjectType?.(targetType, context.userMessage);
      return;
    }
    if (option.value === '__keepmode__') {
      handleSubmit({ userMessage: context.userMessage, value: context.relationship || 'generate', label: option.label });
      return;
    }

    // Attachment-purpose answers (how to USE an uploaded image) set the role on
    // the attachment so downstream collectors route it correctly: embed_asset ->
    // collectEmbedImages (placed verbatim), design_reference -> style anchor,
    // content -> OCR'd into copy. Without this the role is lost (the re-submit
    // below skips the intent block that normally applies attachment_purposes).
    if (['content', 'design_reference', 'embed_asset'].includes(option.value)) {
      if (attachmentRef.current) {
        const withRole = { ...attachmentRef.current, role: option.value };
        attachmentRef.current = withRole;
        setAttachment(prev => prev ? { ...prev, role: option.value } : prev);
      }
      // Content is generated around the image (topic flow), so proceed as 'generate'.
      handleSubmit({ userMessage: context.userMessage, value: 'generate', label: option.label });
      return;
    }

    // Re-enter handleSubmit with the clarification payload
    handleSubmit({ userMessage: context.userMessage, value: option.value, hint: option.hint, label: option.label });
  };

  // ── Handle chat message from workspace (bot response) ──
  // ── Apply a single brainstorm suggestion ──
  // Triggered when the user clicks Apply on one of the suggestions cards.
  // Calls /edit-html-slide via the workspace's sendEdit ref with the
  // suggestion's fix_brief, then marks the suggestion as applied so the
  // UI shows a green check.
  const applyImprovementSuggestion = useCallback(async (messageIndex, suggestionId) => {
    const msg = messages[messageIndex];
    if (!msg || !Array.isArray(msg.suggestions)) return;
    const suggestion = msg.suggestions.find(s => s.id === suggestionId);
    if (!suggestion) return;
    if ((msg.appliedIds || []).includes(suggestionId)) return;
    if (!workspaceRef.current?.sendEdit) {
      toast.error('Workspace not ready for edits.');
      return;
    }
    setMessages(prev => prev.map((m, i) =>
      i === messageIndex
        ? { ...m, appliedIds: [...(m.appliedIds || []), suggestionId] }
        : m
    ));
    const r = await workspaceRef.current.sendEdit(suggestion.fix_brief, {
      targetSlide: msg.targetSlideIndex ?? undefined,
    });
    if (r?.ok === false) {
      // r145: the green check used to land BEFORE the edit ran — a dropped
      // edit still read as applied. Revert the mark so retry stays possible.
      setMessages(prev => prev.map((m, i) =>
        i === messageIndex
          ? { ...m, appliedIds: (m.appliedIds || []).filter(id => id !== suggestionId) }
          : m
      ));
    }
  }, [messages]);

  // r180: coverage-card actions. Fill composes a supplied fact into the slide
  // holding the placeholder; Fix gaps lists the missing requirements for one
  // edit. Supplied facts are recorded as post-draft facts (the frozen-block
  // exception) so a later regeneration does not re-emit a placeholder that
  // was already answered. Both ride the ordinary edit path.
  const applyCoverageFill = async (messageIndex, item, fact) => {
    if (!workspaceRef.current?.sendEdit) { toast.error("This workspace doesn't support AI edits."); return; }
    const req = messages[messageIndex]?.coverageRequirements?.[item.index];
    const reqText = req?.text || item.note || 'this requirement';
    workspaceRef.current.recordPostDraftFact?.(`${reqText}: ${fact}`);
    setMessages(prev => prev.map((m, idx) => idx === messageIndex
      ? { ...m, coverageItems: (m.coverageItems || []).map(it => it.index === item.index ? { ...it, status: 'covered', _filled: true } : it) }
      : m));
    const brief = `PROPOSAL FACT SUPPLIED (authoritative): "${fact}" answers the RFP requirement "${reqText}". Compose this fact into finished slide content in place of its [PLACEHOLDER] marker (or add it where it belongs on this slide). Change nothing else on the slide.`;
    const target = item.slide ? item.slide - 1 : null;
    await workspaceRef.current.sendEdit(brief, target != null ? { targetSlide: target } : {});
  };
  const applyCoverageFixGaps = async (messageIndex, missing) => {
    if (!workspaceRef.current?.sendEdit) { toast.error("This workspace doesn't support AI edits."); return; }
    const reqs = messages[messageIndex]?.coverageRequirements || [];
    const list = missing.map(it => `- [${reqs[it.index]?.category || 'admin'}] ${reqs[it.index]?.text || it.note}`).join('\n');
    const brief = `PROPOSAL COVERAGE GAPS. The RFP requires the following and the deck does not yet address them:\n${list}\nAdd real content where the supplied facts and instructions provide it; where they do not, add an explicit [PLACEHOLDER: ...] marker naming the missing input (facts-only contract - never invent). Keep every other slide unchanged.`;
    const target = missing.find(it => it.slide)?.slide;
    await workspaceRef.current.sendEdit(brief, target ? { targetSlide: target - 1 } : {});
  };

  const applyAllImprovementSuggestions = useCallback(async (messageIndex) => {
    const msg = messages[messageIndex];
    if (!msg || !Array.isArray(msg.suggestions)) return;
    const remaining = msg.suggestions.filter(s => !(msg.appliedIds || []).includes(s.id));
    if (remaining.length === 0) return;
    if (!workspaceRef.current?.sendEdit) {
      toast.error('Workspace not ready for edits.');
      return;
    }
    setMessages(prev => prev.map((m, i) =>
      i === messageIndex
        ? { ...m, appliedIds: msg.suggestions.map(s => s.id) }
        : m
    ));
    // Combine briefs into one edit so the model applies them as a coherent set
    const combined = remaining.map((s, i) => `${i + 1}. ${s.fix_brief}`).join('\n');
    const r = await workspaceRef.current.sendEdit(combined, {
      targetSlide: msg.targetSlideIndex ?? undefined,
    });
    if (r?.ok === false) {
      setMessages(prev => prev.map((m, i) =>
        i === messageIndex ? { ...m, appliedIds: (msg.appliedIds || []) } : m
      ));
    }
  }, [messages]);

  const handleChatMessage = useCallback((msg) => {
    setMessages(prev => {
      // Any incoming workspace message implies the prior phase finished —
      // flip any running chips to done so they stop animating.
      const stamped = prev.map(m => m.chipState === 'running' ? { ...m, chipState: 'done' } : m);
      if (typeof msg === 'string') {
        // Workspace components typically send completion notices as plain
        // strings ("Document generated!", "Document updated."). Render those
        // as done chips — but an error string must LOOK like an error, not a
        // check-icon chip (r145).
        if (/^(error|failed)/i.test(msg.trim())) {
          return [...stamped, { role: 'bot', text: msg, isError: true }];
        }
        return [...stamped, { role: 'bot', chipLabel: msg, chipState: 'done' }];
      }
      if (msg && msg.isError && msg.text) {
        return [...stamped, { role: 'bot', text: msg.text, isError: true }];
      }
      return [...stamped, { role: 'bot', ...msg }];
    });
  }, []);

  // ── File attachment handler ──
  // Route a newly-parsed attachment into either the primary slot or the extras
  // array. First file (when nothing's attached) becomes primary; subsequent
  // files become extras.
  const addAttachment = (att) => {
    // Decide primary-vs-extra from the TRUE current state via functional updaters,
    // not from refs that lag a React commit. Refs alone weren't enough: when two
    // uploads' parses overlap, both addAttachment calls could see an un-synced
    // null primary and the second silently overwrote the first. Functional
    // updaters are serialized by React and always see the latest committed state,
    // so the first file with no primary becomes primary and every later file
    // appends to extras — no file is lost. We also mirror into the refs for the
    // synchronous reads elsewhere (e.g. the classify-intent role assignment).
    setAttachment(prevPrimary => {
      if (prevPrimary) {
        setExtraAttachments(prevEx => {
          const next = [...prevEx, att];
          extraAttachmentsRef.current = next;
          return next;
        });
        return prevPrimary;
      }
      attachmentRef.current = att;
      return att;
    });
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    // Clear the input so the same file can be re-selected if user removes it
    try { if (e.target) e.target.value = ''; } catch { /* ignore */ }
    // Hold the parsing flag for the WHOLE batch. Each processSingleFile also
    // toggles it, but a single boolean flickers false between files (and during
    // the gap before the first parse starts), which let a submit slip through
    // while a file was still being read — the clarifier then ran without the
    // file and asked the user to provide it. Setting it here and clearing in
    // finally keeps the submit guard (handleSubmit) blocked for the whole batch.
    parseDepthRef.current += 1;
    isFileParsingRef.current = true;
    setIsFileParsing(true);
    // r141: expose the batch as a promise so submits WAIT for the parse
    // instead of bouncing (the bounce lost the homepage auto-submit and
    // forced blind-then-retry double classification)
    let _parseDone;
    fileParsePromiseRef.current = new Promise(r => { _parseDone = r; });
    try {
      for (const file of files) {
        await processSingleFile(file);
      }
    } finally {
      parseDepthRef.current -= 1;
      if (parseDepthRef.current <= 0) {
        parseDepthRef.current = 0;
        isFileParsingRef.current = false;
        setIsFileParsing(false);
      }
      try { _parseDone(); } catch { /* noop */ }
    }
  };

  const processSingleFile = async (file) => {
    const isSpreadsheet = /\.(xlsx|xls|csv)$/i.test(file.name);
    const isPDF = /\.pdf$/i.test(file.name);
    const isWord = /\.docx?$/i.test(file.name);
    const isPowerPoint = /\.pptx?$/i.test(file.name);
    const isImage = /\.(png|jpe?g|webp)$/i.test(file.name);

    if (isImage) {
      // Fast-path: the landing page may have already analyzed this file on upload
      // (Dashboard preExtractFile). Reuse the result instead of re-fetching.
      if (file._preExtracted && file._preBase64) {
        addAttachment({ name: file.name, type: 'image', content: file._preExtracted.extracted_text || '', imageData: file._preBase64 });
        toast.success(`Analyzed ${file.name} — ready to add to your slides`, { id: 'file-parse' });
        return;
      }
      toast.loading(`Analyzing slide image...`, { id: 'file-parse' });
      setIsFileParsing(true);
      setFileParseInfo({ name: file.name, stage: 'Analyzing image...' });
      try {
        // Read as base64 for visual reference later
        const base64Promise = new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (ev) => resolve(ev.target.result);
          reader.readAsDataURL(file);
        });
        // Send to backend for vision extraction
        const formData = new FormData();
        formData.append('file', file);
        const [base64DataUrl, resp] = await Promise.all([
          base64Promise,
          fetch(withBackend('/api/content/extract'), { method: 'POST', body: formData }),
        ]);
        if (!resp.ok) {
          const err = await resp.json();
          throw new Error(err.error || 'Failed to analyze image');
        }
        const result = await resp.json();
        // No pre-set role — Haiku's classify-intent will infer 'content' vs
        // 'design_reference' from the user's prompt + conversation context.
        addAttachment({ name: file.name, type: 'image', content: result.extracted_text, imageData: base64DataUrl });
        toast.success(`Analyzed ${file.name} — ready to add to your slides`, { id: 'file-parse' });
      } catch (err) {
        toast.error('Failed to analyze image: ' + err.message, { id: 'file-parse' });
      } finally {
        setIsFileParsing(false);
        setFileParseInfo(null);
      }
    } else if (isSpreadsheet) {
      // If the file was loaded from a cloud drive and already parsed server-side,
      // use the pre-parsed data directly instead of re-parsing client-side.
      if (file._parsedExcelData) {
        const excelData = file._parsedExcelData;
        addAttachment({
          name: file.name,
          type: 'excel',
          excelData,
          // Preserve drive source info so dashboards can refresh the data live
          driveSourceInfo: file._driveSourceInfo || null,
        });
        toast.success(`Parsed ${file.name}: ${excelData.map(s => `${s.sheet_name} (${s.total_rows || s.rows?.length || 0} rows)`).join(', ')}`);
        return;
      }

      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const workbook = XLSX.read(ev.target.result, { type: 'array' });
          const excelData = workbook.SheetNames.map(name => {
            const sheet = XLSX.utils.sheet_to_json(workbook.Sheets[name], { header: 1 });
            const headers = (sheet[0] || []).map(h => String(h ?? ''));
            const rows = sheet.slice(1, 201);
            return { sheet_name: name, headers, rows, total_rows: sheet.length - 1, truncated: sheet.length > 201 };
          });
          addAttachment({ name: file.name, type: 'excel', excelData });
          toast.success(`Parsed ${file.name}: ${excelData.map(s => `${s.sheet_name} (${s.total_rows} rows)`).join(', ')}`);
        } catch (err) {
          toast.error('Failed to parse spreadsheet: ' + err.message);
        }
      };
      reader.readAsArrayBuffer(file);
    } else if (isPDF || isWord || isPowerPoint) {
      // If file was pre-extracted from a cloud drive, use the text directly
      if (file._driveContent?.extracted_text) {
        const text = file._driveContent.extracted_text;
        addAttachment({ name: file.name, content: text, type: isPDF ? 'pdf' : isPowerPoint ? 'presentation' : 'document', driveSourceInfo: file._driveSourceInfo || null });
        toast.success(`Loaded ${file.name} — ${text.length.toLocaleString()} chars`);
        return;
      }
      // Fast-path: pre-extracted on upload (Dashboard preExtractFile). Reuse it.
      if (file._preExtracted?.extracted_text != null) {
        const r = file._preExtracted;
        const pageCount = r.source_info?.page_count || r.source_info?.slide_count;
        addAttachment({ name: file.name, content: r.extracted_text, type: isPDF ? 'pdf' : isPowerPoint ? 'presentation' : 'document', pageCount, images: r.source_info?.images || [], pages: r.source_info?.pages || [] });
        toast.success(`${file.name} ready${pageCount ? ` · ${pageCount} ${isPowerPoint ? 'slides' : 'pages'}` : ''}`, { id: 'file-parse' });
        return;
      }
      toast.loading(`Reading ${file.name}...`, { id: 'file-parse' });
      setIsFileParsing(true);
      setFileParseInfo({ name: file.name, stage: 'Reading pages...' });
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('structure', 'false');
        formData.append('full_text', 'true');
        // Fast extraction (no LLM) — render page images + raw text via PyMuPDF/PyPDF2.
        // Vision analysis happens at generation time with the user's picked model.
        formData.append('fast', 'true');
        // Extract embedded images (photos/logos/charts inside the source) so the
        // direct pipeline can preserve them. Enabled for presentation + document.
        if (projectType === 'presentation' || projectType === 'document') {
          formData.append('extract_images', 'true');
        }
        const resp = await fetch(withBackend('/api/content/extract'), { method: 'POST', body: formData });
        if (!resp.ok) {
          const err = await resp.json();
          throw new Error(err.error || 'Failed to extract content');
        }
        const result = await resp.json();
        const pageCount = result.source_info?.page_count || result.source_info?.slide_count;
        const images = result.source_info?.images || [];
        // pages = [{page, image_data_url}] — used by the direct-from-page generation
        // pipeline when the user's intent is preserve/restructure.
        const pages = result.source_info?.pages || [];
        // r159b: videos cannot ride a redesign — say so up front instead of
        // shipping a broken empty frame (30/08, slide 19).
        const _vidPages = result.source_info?.video_pages || [];
        if (_vidPages.length) {
          toast.info(
            `Slide${_vidPages.length > 1 ? 's' : ''} ${_vidPages.join(', ')} of ${file.name} contain${_vidPages.length > 1 ? '' : 's'} video — videos can't be carried into a redesign, so those slides note it instead.`,
            { duration: 9000 },
          );
        }
        addAttachment({ name: file.name, content: result.extracted_text, type: isPDF ? 'pdf' : isPowerPoint ? 'presentation' : 'document', pageCount, images, pages });
        toast.success(`${file.name} ready${pageCount ? ` · ${pageCount} ${isPowerPoint ? 'slides' : 'pages'}` : ''}`, { id: 'file-parse' });
      } catch (err) {
        toast.error('Failed to parse file: ' + err.message, { id: 'file-parse' });
      } finally {
        setIsFileParsing(false);
        setFileParseInfo(null);
      }
    } else {
      const reader = new FileReader();
      reader.onload = (ev) => {
        addAttachment({ name: file.name, content: ev.target.result, type: 'text' });
      };
      reader.readAsText(file);
    }
  };

  // ── Drag & drop handler for chat area ──
  const handleChatDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setChatDragOver(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      // Pass ALL dropped files through so multi-file drop works
      const dt = new DataTransfer();
      for (const f of files) dt.items.add(f);
      const syntheticEvent = { target: { files: dt.files } };
      handleFileSelect(syntheticEvent);
    }
  };

  // ── Paste image handler for chat textarea ──
  const handleChatPaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const imageItem = items.find(item => item.type.startsWith('image/'));
    if (imageItem) {
      e.preventDefault();
      const file = imageItem.getAsFile();
      if (file) {
        const dt = new DataTransfer();
        dt.items.add(file);
        const syntheticEvent = { target: { files: dt.files } };
        handleFileSelect(syntheticEvent);
      }
    }
  };

  // ── Determine input handler based on phase ──
  const handleInputSubmit = () => {
    if (flowPhase === 'workspace') {
      handleWorkspaceChat();
    } else if (isVariationPhase) {
      // User wants different variations — re-generate with their style hint
      const text = prompt.trim();
      if (!text) return;
      setPrompt('');
      setMessages(prev => [...prev, { role: 'user', text }]);
      // Abort any in-progress variation stream
      if (variationAbortRef.current) {
        variationAbortRef.current.abort();
        variationAbortRef.current = null;
      }
      // Pass user's message as special_instructions to the variation generator
      setVariations([]);
      setSelectedVariationIndex(null);
      variationSelectedRef.current = false;
      // Re-use startVariationGeneration but with the user's style hint
      setFlowPhase('generating-variations');
      // Build and fetch inline — startVariationGeneration uses component state
      (async () => {
        const abortController = new AbortController();
        variationAbortRef.current = abortController;
        setMessages(prev => [...prev, { role: 'system', text: `Generating new variations with your style preferences...`, isStatus: true }]);
        try {
          const response = await fetch(withBackend('/api/generate-variations-stream'), {
            method: 'POST',
            signal: abortController.signal,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              outline_slides: outlineSlides.map(s => ({
                title: s.title || '', content: s.content || '',
                bullet_points: s.bullet_points || s.bulletPoints || [],
                slide_type: s.slide_type || s.slideType || '',
              })),
              variation_count: parseInt(answers.variationCount) || 3,
              presentation_title: outlineTitle,
              visual_style: answers.visualStyle || 'professional',
              html_model: answers.htmlModel || getDefaultModel(),
              imagery_mode: answers.imageryMode || answers.imagery_mode || 'auto',
              art_direction: answers.artDirection || answers.art_direction || '',
              user_prompt: submittedPrompt || prompt,
              special_instructions: text,
              proposal_mode: !!proposalMode,
              pptx_safe: answers.exportTarget === 'pptx',
              brand_identity_id: variationBrandMode && answers.brandIdentity && answers.brandIdentity !== 'none' ? answers.brandIdentity : null,
              user_id: userId || '',
            }),
          });
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n\n');
            buffer = lines.pop() || '';
            for (const line of lines) {
              if (!line.startsWith('data: ')) continue;
              try {
                const event = JSON.parse(line.slice(6));
                switch (event.type) {
                  case 'start': setVariationSampleIndices(event.sample_indices || []); break;
                  case 'direction':
                    setVariations(prev => {
                      const updated = [...prev];
                      while (updated.length <= event.variation_index) updated.push({ direction: null, slides: [] });
                      updated[event.variation_index] = { direction: event.direction, slides: [] };
                      return updated;
                    }); break;
                  case 'variation_slide':
                    setVariations(prev => {
                      const updated = [...prev];
                      if (updated[event.variation_index]) {
                        const v = { ...updated[event.variation_index], slides: [...updated[event.variation_index].slides] };
                        while (v.slides.length <= event.slide_index) v.slides.push(null);
                        v.slides[event.slide_index] = { html_source: event.html_source };
                        updated[event.variation_index] = v;
                      }
                      return updated;
                    }); break;
                  case 'complete':
                    if (!variationSelectedRef.current) setFlowPhase('choosing-variation');
                    break;
                }
              } catch {}
            }
          }
        } catch (err) {
          if (err.name === 'AbortError') return;
          setMessages(prev => [...prev, { role: 'system', text: `Variation generation failed: ${err.message}`, isError: true }]);
          setFlowPhase('outline-ready');
        }
      })();
      return;
    } else if (isOutlinePhase) {
      handleOutlineEditRequest();
    } else if (flowPhase === 'questionnaire') {
      const lastMsg = messages[messages.length - 1];
      if (lastMsg?.isAmbiguity && prompt.trim()) {
        // User answered a clarifying question from the LLM — append clarification and re-generate outline
        const clarification = prompt.trim();
        setPrompt('');
        setMessages(prev => [...prev, { role: 'user', text: clarification }]);
        // Append clarification to the original prompt
        const updatedPrompt = `${submittedPromptRef.current}\n\nUser clarification: ${clarification}`;
        setSubmittedPrompt(updatedPrompt);
        submittedPromptRef.current = updatedPrompt;
        setMessages(prev => [...prev, { role: 'bot', text: 'Thanks! Regenerating the outline with your clarification...' }]);
        setFlowPhase('generating-outline');
        startOutlineGeneration(answers);
      } else if (lastMsg?.questionType === 'freetext' || lastMsg?.questionType === 'freetext-optional') {
        if (prompt.trim()) {
          if (lastMsg.questionType === 'freetext' && lastMsg.questionId === 'topicClarification') {
            setSubmittedPrompt(prev => { const v = `${prev}\n\nAdditional context: ${prompt.trim()}`; submittedPromptRef.current = v; return v; });
          }
          handleAnswer(lastMsg.questionId, prompt.trim(), prompt.trim());
          setPrompt('');
        }
      } else {
        handleSubmit();
      }
    } else {
      handleSubmit();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleInputSubmit();
    }
  };

  // Check if the latest message needs user response
  const lastMsg = messages[messages.length - 1];
  const isAwaitingAnswer = flowPhase === 'questionnaire' && lastMsg?.role === 'bot' && lastMsg?.options;
  const isFreetextQuestion = flowPhase === 'questionnaire' && lastMsg?.role === 'bot' && (lastMsg?.questionType === 'freetext' || lastMsg?.questionType === 'freetext-optional');
  const isAmbiguityQuestion = flowPhase === 'questionnaire' && lastMsg?.role === 'bot' && lastMsg?.isAmbiguity;
  const isWorkspacePhase = flowPhase === 'workspace';
  // Native decks are rendered .pptx files — the HTML edit-chat machinery
  // can't touch them (every op would silently no-op). NativePPTWorkspace
  // provides its own regenerate-with-notes composer instead.
  const isNativeDeck = workspaceData?.flowConfig?.generationMode === 'native-pptx' || !!workspaceData?.nativePpt;
  const showInput = flowPhase === 'prompt' || isOutlinePhase || isFreetextQuestion || isAmbiguityQuestion || (isWorkspacePhase && !isNativeDeck) || isVariationPhase;

  const TypeIcon = PROJECT_TYPE_ICONS[projectType] || Presentation;
  const typeLabel = PROJECT_TYPE_LABELS[projectType] || 'Presentation';
  const typeColor = PROJECT_TYPE_COLORS[projectType] || 'from-green-500 to-emerald-500';

  // ─── Render ────────────────────────────────────────────────────────────────

  if (flowPhase === 'clarifying-questions') {
    return (
      <div className="h-dvh flex flex-col bg-background">
        <div className="border-b border-border/30 px-6 py-3 flex items-center gap-3">
          <button
            onClick={handleClarifyingCancel}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            ← Back to chat
          </button>
          <div className="text-sm text-muted-foreground">/ {typeLabel}</div>
        </div>
        <div className="flex-1 overflow-hidden">
          <ClarifyingQuestionsPanel
            questions={clarifyingPayload.questions}
            rationale={clarifyingPayload.rationale}
            streaming={clarifyingPayload.streaming}
            onSubmit={handleClarifyingSubmit}
            onCancel={handleClarifyingCancel}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="h-dvh flex flex-col bg-background">
      {/* Main content area — two-panel layout */}
      <div className="flex-1 flex overflow-hidden">

        {/* ── Left: Chat Panel ── */}
        <motion.div
          initial={false}
          animate={{ width: chatCollapsed ? 48 : (outlineReviewVisible || isVariationPhase || isWorkspacePhase ? '380px' : '100%') }}
          transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
          className="flex flex-col h-full overflow-hidden border-r border-border/30 relative"
          style={{ flexShrink: 0 }}
        >
          {/* Collapse toggle (when outline or workspace is showing) */}
          {(outlineReviewVisible || isVariationPhase || isWorkspacePhase) && (
            <button
              onClick={() => setChatCollapsed(!chatCollapsed)}
              className={`absolute top-3 z-10 p-1.5 rounded-md hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-colors ${chatCollapsed ? 'left-1/2 -translate-x-1/2' : 'right-2'}`}
              title={chatCollapsed ? 'Expand chat' : 'Collapse chat'}
            >
              {chatCollapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
            </button>
          )}

          {/* Collapsed state — just the toggle button */}
          {chatCollapsed ? (
            <div className="flex-1" />
          ) : (
            <>
              {/* Chat header — height matches AppSidebar's logo row (min-h-[60px]) */}
              <div className="flex items-center justify-between px-4 border-b border-sidebar-border min-h-[60px]">
                <button onClick={onBack} className="text-sm text-muted-foreground hover:text-foreground transition-colors flex-shrink-0">
                  ← Back
                </button>
                <div className="w-12" />
              </div>

              {/* Chat messages */}
              <div className="flex-1 overflow-y-auto px-4 py-6">
                {/* Initial prompt hero (only before messages) */}
                {messages.length === 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="max-w-lg mx-auto text-center pt-12"
                  >
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${typeColor} flex items-center justify-center mx-auto mb-4`}>
                      <TypeIcon className="w-7 h-7 text-white" />
                    </div>
                    <h2 className="text-xl font-bold text-foreground mb-2">
                      Create a {typeLabel}
                    </h2>
                    <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
                      Describe what you'd like to create. Be as specific or vague as you'd like.
                    </p>
                    <div className="flex flex-wrap justify-center gap-2 text-xs text-muted-foreground">
                      {(projectType === 'infographic' ? [
                        '"AI adoption statistics worldwide"',
                        '"How climate change affects oceans"',
                        '"Company growth metrics Q1-Q4"',
                      ] : projectType === 'document' ? [
                        '"Annual business report for 2025"',
                        '"Product launch proposal"',
                        '"Employee handbook update"',
                      ] : projectType === 'dashboard' ? [
                        '"Sales analytics dashboard"',
                        '"Marketing KPI tracker"',
                        '"Financial overview dashboard"',
                      ] : projectType === 'sheets' ? [
                        '"Employee attendance tracker"',
                        '"Project budget spreadsheet"',
                        '"Sales pipeline tracker"',
                      ] : [
                        '"6-slide deck about AI in Jordan"',
                        '"Pitch deck for my SaaS startup"',
                        '"Create slides from this report..."',
                      ]).map((example, i) => (
                        <button
                          key={i}
                          onClick={() => setPrompt(example.replace(/"/g, ''))}
                          className="px-3 py-1.5 rounded-full border border-border/50 hover:border-accent/50 hover:bg-accent/5 transition-colors cursor-pointer"
                        >
                          {example}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Message list */}
                {messages.length > 0 && (
                  <div className="space-y-4">
                    {messages.filter(msg => msg.text || msg.isFactCheck || msg.isResearch || msg.isQa || msg.isIntentClarification || msg.options || msg.isLoading || msg.chipLabel || msg.isImprovement || msg.isBatchRevert || msg.isCoverage).map((msg, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        {msg.chipLabel ? (
                          <StatusChip label={msg.chipLabel} state={msg.chipState || 'running'} />
                        ) : msg.role === 'user' ? (
                          <div className="bg-accent text-accent-foreground px-4 py-2.5 rounded-2xl rounded-br-md max-w-[80%]">
                            <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                          </div>
                        ) : (
                          <div className="max-w-[90%]">
                            <div className="flex items-center gap-2.5">
                              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-500 to-violet-500 flex items-center justify-center flex-shrink-0">
                                <Brain className="w-3.5 h-3.5 text-white" />
                              </div>
                              <div className="flex-1">
                                {msg.isFactCheck ? (
                                  <Collapsible defaultOpen={true}>
                                    <CollapsibleTrigger className="flex items-center gap-1.5 text-sm font-medium text-emerald-600 dark:text-emerald-400 hover:underline mb-1">
                                      <ShieldCheck className="w-3.5 h-3.5" />
                                      Fact Check — {getUnitLabel(projectType)} {(msg.slideIndex ?? 0) + 1}
                                      <ChevronDown className="w-3 h-3" />
                                    </CollapsibleTrigger>
                                    <CollapsibleContent>
                                      <div className="space-y-2 mt-1">
                                        {msg.claims?.length > 0 ? msg.claims.map((claim, ci) => {
                                          const verdict = (claim.verdict || '').toUpperCase();
                                          const isTrue = verdict === 'TRUE' || verdict === 'SUPPORTED';
                                          const isFalse = verdict === 'FALSE' || verdict === 'UNSUPPORTED';
                                          const isPartial = !isTrue && !isFalse;
                                          const borderColor = isTrue ? 'border-green-300 dark:border-green-700' : isFalse ? 'border-red-300 dark:border-red-700' : 'border-yellow-300 dark:border-yellow-700';
                                          const bgColor = isTrue ? 'bg-green-50 dark:bg-green-950/30' : isFalse ? 'bg-red-50 dark:bg-red-950/30' : 'bg-yellow-50 dark:bg-yellow-950/30';
                                          const VerdictIcon = isTrue ? CheckCircle2 : isFalse ? XCircle : AlertTriangle;
                                          const verdictTextColor = isTrue ? 'text-green-600 dark:text-green-400' : isFalse ? 'text-red-600 dark:text-red-400' : 'text-yellow-600 dark:text-yellow-400';
                                          return (
                                            <div key={ci} className={`p-2.5 rounded-lg border ${borderColor} ${bgColor}`}>
                                              <div className="flex items-start gap-2">
                                                <VerdictIcon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${verdictTextColor}`} />
                                                <div className="flex-1 min-w-0">
                                                  <p className="text-xs font-medium text-foreground">{claim.claim}</p>
                                                  <p className={`text-[10px] font-semibold uppercase mt-0.5 ${verdictTextColor}`}>{claim.verdict}</p>
                                                  {claim.explanation && <p className="text-xs text-muted-foreground mt-1">{claim.explanation}</p>}
                                                  {(() => {
                                                    // Handle both `source` (string) and `sources` (array) from backend
                                                    const srcs = claim.sources?.length > 0
                                                      ? claim.sources
                                                      : claim.source ? [claim.source] : [];
                                                    return srcs.length > 0 ? (
                                                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                                                        {srcs.map((src, si) => {
                                                          const url = typeof src === 'string' ? src : (src.url || src);
                                                          if (!url) return null;
                                                          let label;
                                                          try { label = new URL(url).hostname; } catch { label = url; }
                                                          const isLink = url.startsWith('http');
                                                          return isLink ? (
                                                            <a key={si} href={url} target="_blank" rel="noopener noreferrer"
                                                              className="inline-flex items-center gap-1 text-[10px] text-blue-600 dark:text-blue-400 hover:underline">
                                                              <ExternalLink className="w-2.5 h-2.5" />
                                                              {label}
                                                            </a>
                                                          ) : (
                                                            <span key={si} className="text-[10px] text-muted-foreground">{label}</span>
                                                          );
                                                        })}
                                                      </div>
                                                    ) : null;
                                                  })()}
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        }) : (
                                          <div className="text-xs text-muted-foreground prose prose-xs dark:prose-invert max-w-none">
                                            <ReactMarkdown>{msg.text}</ReactMarkdown>
                                          </div>
                                        )}
                                        {msg.text && msg.claims?.length > 0 && (
                                          <div className="text-xs text-muted-foreground mt-1 italic prose prose-xs dark:prose-invert max-w-none">
                                            <ReactMarkdown>{msg.text}</ReactMarkdown>
                                          </div>
                                        )}
                                      </div>
                                    </CollapsibleContent>
                                  </Collapsible>
                                ) : msg.isResearch ? (
                                  <Collapsible defaultOpen={true}>
                                    <CollapsibleTrigger className="flex items-center gap-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline mb-1">
                                      <Globe className="w-3.5 h-3.5" />
                                      Web Research Results
                                      <ChevronDown className="w-3 h-3" />
                                    </CollapsibleTrigger>
                                    <CollapsibleContent>
                                      <div className="text-xs text-muted-foreground whitespace-pre-wrap p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg max-h-60 overflow-y-auto border border-blue-200 dark:border-blue-800">
                                        {msg.text}
                                      </div>
                                    </CollapsibleContent>
                                  </Collapsible>
                                ) : msg.isQa ? (
                                  <Collapsible defaultOpen={true}>
                                    <CollapsibleTrigger className="flex items-center gap-1.5 text-sm font-medium text-purple-600 dark:text-purple-400 hover:underline mb-1">
                                      <Sparkles className="w-3.5 h-3.5" />
                                      QA — {getUnitLabel(projectType)} {(msg.slideIndex ?? 0) + 1} ({msg.qaIssues?.length || 0} issue{(msg.qaIssues?.length || 0) === 1 ? '' : 's'})
                                      <ChevronDown className="w-3 h-3" />
                                    </CollapsibleTrigger>
                                    <CollapsibleContent>
                                      <div className="space-y-2 mt-1">
                                        {msg.qaIssues?.map((issueRaw, ii) => {
                                          // r159b: issues arrive as dicts OR plain strings
                                          // depending on the producing seam — a string
                                          // rendered a BLANK card with live buttons.
                                          const issue = (typeof issueRaw === 'string')
                                            ? { type: 'issue', description: issueRaw }
                                            : (issueRaw || {});
                                          return (
                                          <div key={ii} className="p-2.5 rounded-lg border border-purple-200 dark:border-purple-900 bg-purple-50/40 dark:bg-purple-950/20">
                                            <div className="flex items-start gap-2">
                                              <div className="flex-1 min-w-0">
                                                <p className="text-[10px] font-semibold uppercase text-purple-600 dark:text-purple-400">{issue.type}</p>
                                                <p className="text-xs text-foreground mt-0.5">{issue.description}</p>
                                                {issue.fix && <p className="text-xs text-muted-foreground mt-1 italic">Fix: {issue.fix}</p>}
                                              </div>
                                              <div className="flex flex-col gap-1">
                                                <button
                                                  onClick={() => workspaceRef.current?.applyQaFixes?.(msg.slideIndex, [issue])}
                                                  className="text-[10px] px-2 py-1 rounded-md border border-purple-300 dark:border-purple-700 hover:bg-purple-100 dark:hover:bg-purple-900/40 transition-colors whitespace-nowrap"
                                                  title="Fix just this issue"
                                                >
                                                  Fix
                                                </button>
                                                <button
                                                  onClick={() => {
                                                    setMessages(prev => prev.map((m, idx) =>
                                                      idx === i ? { ...m, qaIssues: m.qaIssues.filter((_, j) => j !== ii) } : m
                                                    ));
                                                  }}
                                                  className="text-[10px] px-2 py-1 rounded-md border border-border hover:bg-muted transition-colors whitespace-nowrap text-muted-foreground"
                                                  title="Ignore this issue"
                                                >
                                                  Ignore
                                                </button>
                                              </div>
                                            </div>
                                          </div>
                                          );
                                        })}
                                        {msg.qaIssues?.length > 1 && (
                                          <button
                                            onClick={() => workspaceRef.current?.applyQaFixes?.(msg.slideIndex, msg.qaIssues)}
                                            className="w-full mt-1 text-xs px-3 py-1.5 rounded-md bg-purple-500 hover:bg-purple-600 text-white transition-colors font-medium flex items-center justify-center gap-1.5"
                                          >
                                            <Sparkles className="w-3 h-3" /> Fix all {msg.qaIssues.length} issues
                                          </button>
                                        )}
                                      </div>
                                    </CollapsibleContent>
                                  </Collapsible>
                                ) : msg.isCoverage ? (
                                  <Collapsible defaultOpen={true}>
                                    <CollapsibleTrigger className="flex items-center gap-1.5 text-sm font-medium text-amber-700 dark:text-amber-400 hover:underline mb-1 text-left">
                                      <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0" />
                                      {msg.text}
                                      <ChevronDown className="w-3 h-3 flex-shrink-0" />
                                    </CollapsibleTrigger>
                                    <CollapsibleContent>
                                      {(() => {
                                        const items = (msg.coverageItems || []).filter(it => it.status !== 'covered');
                                        const reqs = msg.coverageRequirements || [];
                                        if (!items.length) {
                                          return <p className="text-xs text-muted-foreground mt-1">Every requirement in the RFP is addressed in the deck.</p>;
                                        }
                                        // r180 (spec §6): grouped by slide, untied items in a trailing General
                                        // group; the row shows the short to-do, the RFP's own phrasing lives in
                                        // the hover title (rendering both doubles every row).
                                        const groups = {};
                                        items.forEach(it => { const k = it.slide ? `Slide ${it.slide}` : 'General'; (groups[k] = groups[k] || []).push(it); });
                                        const missing = items.filter(it => it.status === 'missing');
                                        return (
                                          <div className="space-y-2 mt-1">
                                            {Object.entries(groups).map(([g, rows]) => (
                                              <div key={g} className="p-2.5 rounded-lg border border-amber-200 dark:border-amber-900 bg-amber-50/40 dark:bg-amber-950/20">
                                                <p className="text-[10px] font-semibold uppercase text-amber-700 dark:text-amber-400">{g}</p>
                                                {rows.map(it => (
                                                  <div key={it.index} className="mt-1.5" title={reqs[it.index]?.text || ''}>
                                                    <p className="text-xs text-foreground">
                                                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium mr-1.5 ${it.status === 'missing' ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'}`}>{it.status}</span>
                                                      {it.note || reqs[it.index]?.text}
                                                    </p>
                                                    <form
                                                      className="flex gap-1.5 mt-1"
                                                      onSubmit={(e) => { e.preventDefault(); const fact = e.target.elements.fact.value.trim(); if (!fact) return; applyCoverageFill(i, it, fact); e.target.reset(); }}
                                                    >
                                                      <input name="fact" placeholder="Type the fact to fill this in" className="flex-1 text-xs px-2 py-1 rounded-md border border-border bg-background" />
                                                      <button type="submit" className="text-[10px] px-2 py-1 rounded-md border border-amber-300 dark:border-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors whitespace-nowrap">Fill</button>
                                                    </form>
                                                  </div>
                                                ))}
                                              </div>
                                            ))}
                                            {missing.length > 0 && (
                                              <button
                                                onClick={() => applyCoverageFixGaps(i, missing)}
                                                className="w-full mt-1 text-xs px-3 py-1.5 rounded-md bg-amber-600 hover:bg-amber-700 text-white transition-colors font-medium flex items-center justify-center gap-1.5"
                                              >
                                                <ShieldCheck className="w-3 h-3" /> Fix {missing.length} gap{missing.length === 1 ? '' : 's'}
                                              </button>
                                            )}
                                          </div>
                                        );
                                      })()}
                                    </CollapsibleContent>
                                  </Collapsible>
                                ) : msg.isLoading ? (
                                  <div className="flex items-center gap-2 py-1">
                                    <span className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '0ms' }} />
                                    <span className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '150ms' }} />
                                    <span className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '300ms' }} />
                                    {msg.text && <span className="text-sm text-muted-foreground">{msg.text}</span>}
                                  </div>
                                ) : (
                                  <div className="text-sm text-foreground mb-2 [&_p]:mb-1.5 [&_p:last-child]:mb-0 [&_ul]:mb-1.5 [&_ul]:ml-4 [&_ul]:list-disc [&_ol]:mb-1.5 [&_ol]:ml-4 [&_ol]:list-decimal [&_li]:mb-0.5 [&_strong]:font-semibold [&_em]:italic [&_code]:bg-muted [&_code]:px-1 [&_code]:rounded [&_code]:text-xs">
                                    <ReactMarkdown>{msg.text}</ReactMarkdown>
                                  </div>
                                )}

                                {/* Intent clarification buttons — fire when the LLM flagged ambiguous intent */}
                                {msg.isIntentClarification && msg.intentOptions?.length > 0 && (
                                  <div className="flex flex-col gap-2 mt-1">
                                    {msg.intentOptions.map((opt, oi) => (
                                      <button
                                        key={oi}
                                        onClick={() => handleIntentAnswer(i, opt, msg.intentContext || {})}
                                        className="group flex flex-col items-start gap-0.5 px-3.5 py-2.5 rounded-xl border border-border hover:border-accent hover:bg-accent/5 transition-all text-left w-full"
                                      >
                                        <span className="text-sm font-medium text-foreground">{opt.label}</span>
                                        {opt.hint && <span className="text-xs text-muted-foreground leading-snug">{opt.hint}</span>}
                                      </button>
                                    ))}
                                  </div>
                                )}

                                {/* Option buttons for questionnaire */}
                                {msg.options && i === messages.length - 1 && flowPhase === 'questionnaire' && (
                                  <div className="flex flex-wrap gap-2">
                                    {msg.questionType === 'logo-upload' && (
                                      <button
                                        onClick={() => logoInputRef.current?.click()}
                                        className="px-3 py-1.5 text-sm rounded-full border border-green-500/50 bg-green-500/10 text-green-700 dark:text-green-300 hover:bg-green-500/20 transition-colors flex items-center gap-1.5"
                                      >
                                        <Upload className="w-3.5 h-3.5" /> Upload Logo
                                      </button>
                                    )}
                                    <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                                    {msg.options.map((opt) => {
                                      const IconComponent = opt.icon ? OPTION_ICONS[opt.icon] : null;
                                      return (
                                        <button
                                          key={opt.value}
                                          onClick={() => !opt.disabled && handleAnswer(msg.questionId, opt.value, opt.label)}
                                          disabled={opt.disabled}
                                          className={opt.disabled
                                            ? (opt.desc
                                              ? "group flex items-start gap-3 px-3.5 py-3 rounded-xl border border-border/50 opacity-50 cursor-not-allowed text-left w-full sm:w-[calc(50%-0.25rem)]"
                                              : "px-3 py-1.5 text-sm rounded-full border border-border/50 opacity-50 cursor-not-allowed text-foreground flex items-center gap-1.5")
                                            : (opt.desc
                                              ? "group flex items-start gap-3 px-3.5 py-3 rounded-xl border border-border hover:border-accent hover:bg-accent/5 transition-all text-left w-full sm:w-[calc(50%-0.25rem)]"
                                              : "px-3 py-1.5 text-sm rounded-full border border-border hover:border-accent hover:bg-accent/5 transition-colors text-foreground flex items-center gap-1.5")
                                          }
                                        >
                                          {opt.desc ? (
                                            <>
                                              {IconComponent && (
                                                <div className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
                                                  <IconComponent className="w-4 h-4 text-accent" />
                                                </div>
                                              )}
                                              <div className="min-w-0">
                                                <div className="text-sm font-medium text-foreground">{opt.label}</div>
                                                <div className="text-xs text-muted-foreground mt-0.5 leading-snug">{opt.desc}</div>
                                              </div>
                                            </>
                                          ) : (
                                            <>
                                              {IconComponent && <IconComponent className="w-3.5 h-3.5 text-muted-foreground" />}
                                              {opt.label}
                                            </>
                                          )}
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}

                                {/* Brand dropdown for 4+ brands */}
                                {msg.questionType === 'brand-dropdown' && msg.dropdownOptions && i === messages.length - 1 && flowPhase === 'questionnaire' && (
                                  <div className="flex flex-col gap-2 w-full max-w-sm">
                                    <select
                                      defaultValue=""
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        if (!val) return;
                                        if (val === 'none') {
                                          handleAnswer(msg.questionId, 'none', 'No brand — let AI decide');
                                        } else {
                                          const chosen = msg.dropdownOptions.find(o => o.value === val);
                                          handleAnswer(msg.questionId, val, chosen?.label || val);
                                        }
                                      }}
                                      className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-colors"
                                    >
                                      <option value="" disabled>Select a brand identity...</option>
                                      <option value="none">No brand — let AI decide</option>
                                      {msg.dropdownOptions.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                          {opt.label}{opt.desc ? ` — ${opt.desc}` : ''}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                )}

                                {/* Freetext-optional: show skip button when no text typed */}
                                {msg.questionType === 'freetext-optional' && i === messages.length - 1 && flowPhase === 'questionnaire' && (
                                  <div className="flex items-center gap-2 mt-2">
                                    <button
                                      onClick={() => handleAnswer(msg.questionId, '', 'Skipped')}
                                      className="px-3 py-1.5 text-sm rounded-full border border-border hover:border-accent hover:bg-accent/5 transition-colors text-muted-foreground"
                                    >
                                      Skip
                                    </button>
                                  </div>
                                )}

                                {/* Brainstorm suggestions list */}
                                {msg.isImprovement && Array.isArray(msg.suggestions) && msg.suggestions.length > 0 && (
                                  <div className="space-y-2 mt-1">
                                    {msg.improveSummary && (
                                      <p className="text-sm text-muted-foreground italic mb-2">{msg.improveSummary}</p>
                                    )}
                                    {msg.suggestions.map((s) => {
                                      const applied = (msg.appliedIds || []).includes(s.id);
                                      return (
                                        <div
                                          key={s.id}
                                          className={`rounded-xl border px-3 py-2.5 transition-colors ${
                                            applied
                                              ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/30'
                                              : 'border-border bg-card hover:border-accent/40'
                                          }`}
                                        >
                                          <div className="flex items-start gap-2">
                                            {applied ? (
                                              <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                                            ) : (
                                              <div className="w-4 h-4 rounded-full border border-border flex-shrink-0 mt-0.5" />
                                            )}
                                            <div className="flex-1 min-w-0">
                                              <div className="text-sm font-semibold text-foreground">{s.title}</div>
                                              {s.description && (
                                                <div className="text-xs text-muted-foreground mt-0.5 leading-snug">{s.description}</div>
                                              )}
                                            </div>
                                            {!applied && (
                                              <button
                                                onClick={() => applyImprovementSuggestion(i, s.id)}
                                                className="px-2.5 py-1 text-xs rounded-md bg-accent text-white hover:bg-accent/90 transition-colors font-medium flex-shrink-0"
                                              >
                                                Apply
                                              </button>
                                            )}
                                            {applied && (
                                              <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium flex-shrink-0">Applied</span>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                    {(msg.appliedIds || []).length < msg.suggestions.length && (
                                      <button
                                        onClick={() => applyAllImprovementSuggestions(i)}
                                        className="mt-2 px-3 py-1.5 text-xs rounded-full border border-accent/40 text-accent hover:bg-accent/5 transition-colors font-medium"
                                      >
                                        Apply all remaining
                                      </button>
                                    )}
                                  </div>
                                )}

                                {/* Batch-revert card — appears after a multi-op edit. */}
                                {msg.isBatchRevert && Array.isArray(msg.batchSnapshots) && msg.batchSnapshots.length > 0 && !msg.batchReverted && (
                                  <div className="mt-2 flex items-center gap-2">
                                    <button
                                      onClick={() => {
                                        if (workspaceRef.current?.restoreSlideSnapshots) {
                                          workspaceRef.current.restoreSlideSnapshots(msg.batchSnapshots);
                                        }
                                        setMessages(prev => prev.map((m, idx) =>
                                          idx === i ? { ...m, batchReverted: true } : m
                                        ));
                                      }}
                                      className="px-3 py-1.5 text-xs rounded-full border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 transition-colors font-medium flex items-center gap-1.5"
                                    >
                                      <Undo2 className="w-3 h-3" />
                                      Revert this batch ({msg.batchSnapshots.length} slides)
                                    </button>
                                  </div>
                                )}
                                {msg.isBatchRevert && msg.batchReverted && (
                                  <div className="mt-2 text-xs text-emerald-600 font-medium flex items-center gap-1">
                                    <Check className="w-3 h-3" />
                                    Reverted
                                  </div>
                                )}

                                {/* r169: per-change diff card — before/after previews, revert ↔ redo */}
                                {msg.isEditDiff && msg.diff && (
                                  <div className="mt-2 space-y-2 max-w-md">
                                    <div className="flex items-stretch gap-2">
                                      {[['Before', msg.diff.before, msg.diffState === 'reverted'], ['After', msg.diff.after, msg.diffState !== 'reverted']].map(([lbl, snap, isCurrent]) => (
                                        <div key={lbl} className={`flex-1 rounded-lg border p-1 ${isCurrent ? 'border-accent' : 'border-border opacity-60'}`}>
                                          <div className="text-[10px] font-medium text-muted-foreground mb-1 px-1">
                                            {lbl}{isCurrent ? ' · current' : ''}
                                          </div>
                                          {snap?.preview_url ? (
                                            <img src={snap.preview_url} alt={`${lbl} slide ${(msg.diff.slideIdx ?? 0) + 1}`} className="w-full rounded" />
                                          ) : (
                                            <div className="w-full aspect-video bg-muted rounded flex items-center justify-center text-[10px] text-muted-foreground">
                                              preview pending
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                    <button
                                      onClick={() => {
                                        const goingBack = msg.diffState !== 'reverted';
                                        const target = goingBack ? msg.diff.before : msg.diff.after;
                                        workspaceRef.current?.restoreSlideSnapshots?.(
                                          [{ slideIdx: msg.diff.slideIdx, uid: msg.diff.uid || null, html_source: target.html_source, preview_url: target.preview_url }],
                                          { verb: goingBack ? 'Reverted' : 'Re-applied' }
                                        );
                                        setMessages(prev => prev.map(m => m === msg ? { ...m, diffState: goingBack ? 'reverted' : 'applied' } : m));
                                      }}
                                      className="px-3 py-1.5 text-xs rounded-full border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 transition-colors font-medium flex items-center gap-1.5"
                                    >
                                      {msg.diffState === 'reverted' ? <Redo2 className="w-3 h-3" /> : <Undo2 className="w-3 h-3" />}
                                      {msg.diffState === 'reverted' ? 'Redo this edit' : 'Revert this edit'}
                                    </button>
                                  </div>
                                )}

                                {/* Plan confirmation: checklist + buttons (r169: untick ops to skip them) */}
                                {msg.isPlanConfirmation && pendingPlan && Array.isArray(msg.planOps) && msg.planOps.length > 0 && (
                                  <div className="mt-2 space-y-1">
                                    {msg.planOps.map((op, j) => (
                                      <label key={j} className="flex items-start gap-2 text-sm cursor-pointer group">
                                        <input
                                          type="checkbox"
                                          checked={msg.checkedOps?.[j] !== false}
                                          onChange={() => setMessages(prev => prev.map(m =>
                                            m === msg
                                              ? { ...m, checkedOps: (m.checkedOps || m.planOps.map(() => true)).map((c, k) => k === j ? !c : c) }
                                              : m
                                          ))}
                                          className="mt-0.5 accent-current"
                                        />
                                        <span className={msg.checkedOps?.[j] === false ? 'line-through text-muted-foreground' : ''}>
                                          <span className="font-semibold">{op.type}</span>
                                          {op.target != null ? ` (slide ${op.target})` : ''} — {op.brief || 'no description'}
                                        </span>
                                      </label>
                                    ))}
                                  </div>
                                )}
                                {msg.isPlanConfirmation && pendingPlan && (
                                  <div className="flex items-center gap-2 mt-2">
                                    <button
                                      onClick={async () => {
                                        if (isSubmitInFlightRef.current || planRunning) {
                                          toast.error("I'm still working — give me a moment.");
                                          return;
                                        }
                                        const { plan, originalMessage, referenceImages: pendingRefs } = pendingPlan;
                                        // r169: run only the ops still ticked in the checklist.
                                        const keptOps = Array.isArray(msg.checkedOps)
                                          ? plan.operations.filter((_, k) => msg.checkedOps[k] !== false)
                                          : plan.operations;
                                        if (keptOps.length === 0) {
                                          toast.error('Every operation is unticked — nothing to apply.');
                                          return;
                                        }
                                        setPendingPlan(null);
                                        setMessages(prev => prev.map(m =>
                                          m === msg ? { ...m, isPlanConfirmation: false } : m
                                        ));
                                        isSubmitInFlightRef.current = true;
                                        setIsSubmitInFlight(true);
                                        try {
                                          await executePlanOps(keptOps, originalMessage, pendingRefs || []);
                                        } finally {
                                          isSubmitInFlightRef.current = false;
                                          setIsSubmitInFlight(false);
                                        }
                                      }}
                                      className="px-4 py-1.5 text-sm rounded-full bg-accent text-white hover:bg-accent/90 transition-colors font-medium"
                                    >
                                      {Array.isArray(msg.checkedOps) && msg.checkedOps.some(c => c === false)
                                        ? `Apply ${msg.checkedOps.filter(c => c !== false).length} of ${msg.planOps?.length ?? pendingPlan.plan.operations.length} operations`
                                        : `Apply Plan (${pendingPlan.plan.operations.length} operations)`}
                                    </button>
                                    <button
                                      onClick={() => {
                                        setPendingPlan(null);
                                        setMessages(prev => prev.map((m, idx) =>
                                          idx === i ? { ...m, isPlanConfirmation: false, text: m.text + '\n\n*Cancelled.*' } : m
                                        ));
                                      }}
                                      className="px-4 py-1.5 text-sm rounded-full border border-border hover:border-red-500/50 hover:bg-red-500/5 text-muted-foreground transition-colors"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </motion.div>
                    ))}

                    {/* Thinking indicator */}
                    {isThinking && (
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-start gap-2.5">
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-500 to-violet-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Brain className="w-3.5 h-3.5 text-white animate-pulse" />
                        </div>
                        <div className="flex-1">
                          <Collapsible open={thinkingOpen} onOpenChange={setThinkingOpen}>
                            <CollapsibleTrigger className="flex items-center gap-1.5 text-xs text-purple-600 dark:text-purple-400 font-medium hover:underline mb-1">
                              {thinkingOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                              AI is thinking...
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                              <p className="text-xs text-muted-foreground italic whitespace-pre-wrap p-3 bg-muted/30 rounded-lg max-h-40 overflow-y-auto">
                                {displayedThinking || 'Analyzing your request...'}
                              </p>
                            </CollapsibleContent>
                          </Collapsible>
                        </div>
                      </motion.div>
                    )}

                    {/* No separate outline loading line — the purple status
                        chips already communicate this state; two indicators
                        for one wait read as noise. */}
                  </div>
                )}

                <div ref={chatEndRef} />
              </div>

              {/* Input area */}
              {showInput && (
                <div className="px-4 pb-4 pt-2">
                  {/* File parse indicator */}
                  {isFileParsing && fileParseInfo && (
                    <div className="flex items-center gap-2 mb-2 px-3 py-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg text-sm">
                      <Loader2 className="w-4 h-4 text-amber-600 dark:text-amber-400 animate-spin flex-shrink-0" />
                      <span className="text-amber-900 dark:text-amber-200 truncate flex-1">
                        Analyzing <span className="font-medium">{fileParseInfo.name}</span>… {fileParseInfo.stage}
                      </span>
                    </div>
                  )}
                  {/* Attachment chips — primary + extras. Each file renders as its own
                      chip. Classification (content vs design_reference) is LLM-inferred
                      and shown as a passive badge; to override, user types their intent. */}
                  {!isOutlinePhase && (attachment || extraAttachments.length > 0) && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {[attachment, ...extraAttachments].filter(Boolean).map((att, attIdx) => {
                        const isPrimary = attIdx === 0 && attachment;
                        return (
                          <div key={attIdx} className="flex items-center gap-2 px-3 py-1.5 bg-muted/50 rounded-lg text-sm max-w-full">
                            {att.type === 'image' && att.imageData ? (
                              <img src={att.imageData} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" />
                            ) : (
                              <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                            )}
                            <span className="text-muted-foreground truncate max-w-[240px]">
                              {att.name}
                              {att.excelData && (
                                <span className="text-xs text-emerald-600 dark:text-emerald-400 ml-1.5">
                                  ({att.excelData.map(s => `${s.sheet_name}: ${s.total_rows} rows`).join(' · ')})
                                </span>
                              )}
                              {(att.type === 'pdf' || att.type === 'document') && att.pageCount && (
                                <span className="text-xs text-emerald-600 dark:text-emerald-400 ml-1.5">
                                  ({att.pageCount} pages)
                                </span>
                              )}
                            </span>
                            {att.role === 'design_reference' && (
                              <span className="text-[11px] px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/40 border border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300 whitespace-nowrap" title="Detected as a visual style reference. To override, say so in a message.">
                                🎨 Style ref
                              </span>
                            )}
                            {att.role === 'content' && [attachment, ...extraAttachments].filter(x => x?.role === 'content').length > 1 && (
                              <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 border border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 whitespace-nowrap" title="Content source">
                                📄 Content
                              </span>
                            )}
                            <button
                              onClick={() => {
                                if (isPrimary) {
                                  // Promote first extra to primary
                                  if (extraAttachments.length > 0) {
                                    setAttachment(extraAttachments[0]);
                                    setExtraAttachments(prev => prev.slice(1));
                                  } else {
                                    setAttachment(null);
                                  }
                                } else {
                                  const extraIdx = attIdx - 1;
                                  setExtraAttachments(prev => prev.filter((_, i) => i !== extraIdx));
                                }
                              }}
                              className="text-muted-foreground hover:text-foreground flex-shrink-0"
                              title="Remove this attachment"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div
                    className={`rounded-xl border bg-card shadow-sm focus-within:border-emerald-400/50 focus-within:shadow-md transition-all ${
                      chatDragOver ? 'border-emerald-400 bg-emerald-50/30 dark:bg-emerald-900/10' : 'border-border/60'
                    }`}
                    onDragOver={(e) => { e.preventDefault(); setChatDragOver(true); }}
                    onDragLeave={(e) => { e.preventDefault(); setChatDragOver(false); }}
                    onDrop={handleChatDrop}
                  >
                    {chatDragOver && (
                      <div className="px-3.5 pt-2 text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                        Drop file here...
                      </div>
                    )}
                    <textarea
                      ref={textareaRef}
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      onKeyDown={handleKeyDown}
                      onPaste={handleChatPaste}
                      placeholder={
                        isWorkspacePhase ? "Ask AI to edit your content..." :
                        outlineReviewVisible ? "Ask AI to modify the outline..." :
                        flowPhase === 'generating-outline' ? 'Steer the outline as it generates — e.g. "keep it to 5 slides"' :
                        isOutlinePhase ? `Planning your ${typeLabel.toLowerCase()}...` :
                        isFreetextQuestion ? (lastMsg?.placeholder || "Type your answer...") :
                        `Describe your ${typeLabel.toLowerCase()}...`
                      }
                      rows={(isOutlinePhase || isWorkspacePhase) ? 1 : 2}
                      className="w-full text-sm bg-transparent px-3.5 pt-3 pb-1 resize-none focus:outline-none placeholder:text-muted-foreground/60"
                    />
                    <div className="flex items-center justify-between px-2 pb-2">
                      <div className="flex items-center gap-0.5">
                        {/* File upload — available in all phases except outline */}
                        {!isOutlinePhase && (
                          <>
                            <button
                              onClick={() => fileInputRef.current?.click()}
                              className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-muted-foreground hover:text-foreground transition-colors"
                              title="Attach file or image"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                            <input ref={fileInputRef} type="file" multiple accept=".txt,.md,.docx,.xlsx,.xls,.csv,.pdf,.pptx,.ppt,.png,.jpg,.jpeg,.webp" onChange={handleFileSelect} className="hidden" />
                      {planRunning && (
                        <button
                          onClick={() => { planCancelRef.current = true; workspaceRef.current?.cancelChatEdit?.(); }}
                          className="px-2.5 py-1 rounded-full bg-red-500/10 text-red-600 hover:bg-red-500/20 text-xs font-medium transition-colors"
                          title="Stop after the current operation — finished changes are kept"
                        >
                          Stop
                        </button>
                      )}
                          </>
                        )}
                        {/* Workspace phase: region toggle for documents.
                            Documents are one continuous iframe, not per-page
                            cards, so the region toggle lives in the chat
                            toolbar. Click flips DocumentWorkspace's regionMode
                            via the exposed ref method. */}
                        {isWorkspacePhase && projectType === 'document' && (
                          <button
                            onClick={() => {
                              const next = !wsRegionMode;
                              setWsRegionMode(next);
                              workspaceRef.current?.setRegionMode?.(next);
                            }}
                            className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                              wsRegionMode
                                ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400'
                                : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-muted-foreground hover:text-foreground'
                            }`}
                            title="Draw a region — your next edit will only modify that area"
                          >
                            <BoxSelect className="w-3.5 h-3.5" />
                            {wsRegionMode ? 'Cancel' : 'Region'}
                          </button>
                        )}
                        {/* Workspace phase: apply-all toggle for presentations */}
                        {isWorkspacePhase && projectType === 'presentation' && (
                          <button
                            onClick={() => {
                              const next = !wsApplyAll;
                              setWsApplyAll(next);
                              workspaceRef.current?.setApplyAll?.(next);
                            }}
                            className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                              wsApplyAll
                                ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400'
                                : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-muted-foreground hover:text-foreground'
                            }`}
                            title="Apply edit to all slides"
                          >
                            <Layers className="w-3.5 h-3.5" />
                            All
                          </button>
                        )}
                        {/* Workspace phase: chat model picker — the "thinking"
                            model that plans, analyzes, and writes back. */}
                        {isWorkspacePhase && (() => {
                          // One choice drives BOTH the planning model and the
                          // model that performs edits. `edit` is the html_model
                          // the edit endpoints receive; the planner value comes
                          // from CHAT_PLAN_MODEL (single source of truth —
                          // haiku|sonnet|opus|gemini-pro; others map to the
                          // nearest supported planner).
                          const CHAT_MODELS = [
                            { value: 'flash', label: 'Gemini Flash', desc: 'Fast', edit: 'flash' },
                            { value: 'haiku', label: 'Haiku 4.5', desc: 'Fast chat', edit: 'flash' },
                            { value: 'gemini-pro', label: 'Gemini 3.1 Pro', desc: 'Vision', edit: 'pro' },
                            { value: 'sonnet', label: 'Sonnet 5', desc: 'Balanced', edit: 'sonnet' },
                            { value: 'kimi', label: 'Kimi K3', desc: 'Moonshot', edit: 'kimi' },
                            { value: 'opus', label: 'Opus 5', desc: 'Best', edit: 'opus' },
                            { value: 'sol', label: 'GPT-5.6 Sol', desc: 'Premium', edit: 'sol' },
                            { value: 'astra', label: 'GPT-6 Astra', desc: 'Ultra', edit: 'astra' },
                          ];
                          // Display follows the model that will ACTUALLY edit:
                          // restored projects keep editModel 'flash' and fresh
                          // flows seed it from the deck's model — either way the
                          // stale chatModel default must not mislabel the picker.
                          const _wsEdit = workspaceRef.current?.getChatControls?.()?.editModel;
                          const current = CHAT_MODELS.find(m => m.edit === _wsEdit)
                            || CHAT_MODELS.find(m => m.value === chatModel)
                            || CHAT_MODELS.find(m => m.value === 'sonnet');
                          return (
                            <div className="relative">
                              <button
                                onClick={() => setShowChatModelMenu(v => !v)}
                                className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                                title="Pick the model that thinks, plans, and analyzes (separate from the deck's generation model)"
                              >
                                <Brain className="w-3.5 h-3.5" />
                                {current.label}
                                <ChevronDown className="w-3 h-3" />
                              </button>
                              {showChatModelMenu && (
                                <div className="absolute bottom-full left-0 mb-1 bg-card border border-border rounded-lg shadow-lg py-1 min-w-[200px] z-50">
                                  {CHAT_MODELS.map(m => (
                                    <button
                                      key={m.value}
                                      onClick={() => {
                                        setChatModel(m.value);
                                        setShowChatModelMenu(false);
                                        workspaceRef.current?.setEditModel?.(m.edit);
                                      }}
                                      className={`w-full px-3 py-1.5 text-left text-xs hover:bg-muted/50 flex items-center justify-between gap-2 ${
                                        chatModel === m.value ? 'bg-violet-50 dark:bg-violet-900/20 text-violet-900 dark:text-violet-200' : ''
                                      }`}
                                    >
                                      <span className="font-medium">{m.label}</span>
                                      <span className="text-muted-foreground">{m.desc}</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                        {/* Web search toggle — visible through the clarifier/
                            questionnaire too so an auto-enabled search can
                            still be turned off before the outline runs (the
                            notice tells users to tap it; it must exist). */}
                        {['prompt', 'clarifying-questions', 'questionnaire'].includes(flowPhase) && (
                          <button
                            onClick={() => { webSearchUserSetRef.current = true; setWebSearch(!webSearch); }}
                            className={`p-1.5 rounded-lg transition-colors flex items-center gap-1 text-xs ${webSearch ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-muted-foreground'}`}
                            title={webSearch ? 'Web search: ON' : 'Web search: OFF'}
                          >
                            <Globe className="w-3.5 h-3.5" />
                            {webSearch && <span>Web</span>}
                          </button>
                        )}
                        {/* Outline review toggle — prompt phase, outline-bearing types.
                            OFF (default) generates straight away; ON previews the
                            editable outline first. Persists in localStorage. */}
                        {flowPhase === 'prompt' && ['presentation', 'infographic', 'document'].includes(projectType) && (
                          <button
                            onClick={() => {
                              const next = !skipOutlinePreview;
                              setSkipOutlinePreview(next);
                              try { localStorage.setItem('skipOutlinePreview', next ? 'true' : 'false'); } catch { /* ignore */ }
                            }}
                            className={`p-1.5 rounded-lg transition-colors flex items-center gap-1 text-xs ${!skipOutlinePreview ? 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-400' : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-muted-foreground'}`}
                            title={skipOutlinePreview ? 'Outline review: OFF — generates straight away' : 'Outline review: ON — preview & edit the outline first'}
                          >
                            <Eye className="w-3.5 h-3.5" />
                            {!skipOutlinePreview && <span>Review</span>}
                          </button>
                        )}
                      </div>
                      <button
                        onClick={handleInputSubmit}
                        disabled={(!prompt.trim() && !attachment) || isFileParsing || isSubmitInFlight}
                        title={
                          isFileParsing ? 'Analyzing file — please wait'
                          : isSubmitInFlight ? 'Working on your last request — please wait'
                          : undefined
                        }
                        className="w-8 h-8 rounded-full bg-foreground text-background flex items-center justify-center hover:opacity-80 transition-opacity disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        {(isFileParsing || isSubmitInFlight) ? <Loader2 className="w-4 h-4 animate-spin" /> : (isOutlinePhase || isWorkspacePhase) ? <Send className="w-4 h-4" /> : <ArrowUp className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </motion.div>

        {/* ── Right: Outline Panel ── */}
        <AnimatePresence>
          {outlineReviewVisible && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: chatCollapsed ? 'calc(100% - 48px)' : 'calc(100% - 380px)', opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
              className="flex flex-col h-full overflow-hidden"
            >
              <div className="px-6 py-4 border-b border-border/50 bg-muted/20">
                <h3 className="text-lg font-semibold text-foreground">
                  {outlineTitle || 'Generating Outline...'}
                </h3>
                {outlineSlides.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {outlineSlides.length} {projectType === 'infographic' || projectType === 'document' ? 'section' : 'slide'}{outlineSlides.length !== 1 ? 's' : ''}
                    {outlineLoading && ' (generating...)'}
                  </p>
                )}
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
                {outlineSlides.length === 0 && outlineLoading && (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <Loader2 className="w-8 h-8 text-accent animate-spin mb-4" />
                    <p className="text-sm text-muted-foreground">Generating your outline...</p>
                  </div>
                )}

                <AnimatePresence mode="popLayout">
                  {outlineSlides.map((slide, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.05 }}
                      className="group border border-border/50 rounded-lg p-4 bg-card/60 hover:border-accent/30 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-7 h-7 rounded-md bg-accent/10 text-accent flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                          {i + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <input
                              value={slide.title || ''}
                              onChange={(e) => updateOutlineSlide(i, 'title', e.target.value)}
                              className="font-semibold text-sm text-foreground bg-transparent border-none outline-none flex-1 min-w-0 focus:ring-1 focus:ring-accent/30 rounded px-1 -ml-1"
                            />
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 flex-shrink-0">
                              {slide.slide_type || 'content'}
                            </Badge>
                            {flowPhase === 'outline-ready' && outlineSlides.length > 1 && (
                              <button
                                onClick={() => removeOutlineSlide(i)}
                                className="opacity-0 group-hover:opacity-100 p-1 text-muted-foreground hover:text-red-500 transition-all"
                                title={`Remove ${getUnitLabel(projectType, false)}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                          <textarea
                            value={slide.content || ''}
                            onChange={(e) => updateOutlineSlide(i, 'content', e.target.value)}
                            rows={2}
                            className="w-full text-xs text-muted-foreground bg-transparent border-none outline-none resize-none mb-1.5 focus:ring-1 focus:ring-accent/30 rounded px-1 -ml-1"
                            placeholder={`${getUnitLabel(projectType)} description...`}
                          />
                          {slide.bullet_points?.length > 0 && (
                            <ul className="space-y-1">
                              {slide.bullet_points.map((bp, j) => (
                                <li key={j} className="text-xs text-muted-foreground flex items-start gap-1.5">
                                  <span className="text-accent mt-1">•</span>
                                  <input
                                    value={bp}
                                    onChange={(e) => updateOutlineBullet(i, j, e.target.value)}
                                    className="flex-1 bg-transparent border-none outline-none focus:ring-1 focus:ring-accent/30 rounded px-1 -ml-1"
                                  />
                                  {flowPhase === 'outline-ready' && (
                                    <button
                                      onClick={() => removeOutlineBullet(i, j)}
                                      className="opacity-0 group-hover:opacity-100 p-0.5 text-muted-foreground hover:text-red-500 transition-all"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                          {flowPhase === 'outline-ready' && (
                            <button
                              onClick={() => addOutlineBullet(i)}
                              className="mt-1 text-[10px] text-muted-foreground hover:text-accent flex items-center gap-1 transition-colors"
                            >
                              <Plus className="w-3 h-3" /> Add bullet point
                            </button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>

                {flowPhase === 'outline-ready' && (
                  <button
                    onClick={addOutlineSlide}
                    className="w-full border border-dashed border-border/50 rounded-lg p-3 text-sm text-muted-foreground hover:text-accent hover:border-accent/30 transition-colors flex items-center justify-center gap-2"
                  >
                    <Plus className="w-4 h-4" /> {`Add ${getUnitLabel(projectType)}`}
                  </button>
                )}
              </div>

              {flowPhase === 'outline-ready' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="px-6 py-4 border-t border-border/50 bg-muted/20 space-y-3"
                >
                  {outlineMetadata && (
                    <div className="text-xs text-muted-foreground bg-background/50 rounded-lg p-3 space-y-1.5 border border-border/30">
                      <div className="font-medium text-foreground text-sm mb-2">Analysis Summary</div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1">
                        <span>{outlineSlides.length} {getUnitLabel(projectType, false)}s</span>
                        <span>Language: {outlineMetadata.detected_language || 'en'}</span>
                        <span>Type: {outlineMetadata.content_type === 'full_content' ? 'Content preserved' : outlineMetadata.content_type === 'detailed_brief' ? 'From brief' : 'Created from topic'}</span>
                      </div>
                      {outlineMetadata.special_requests?.length > 0 && (
                        <div className="text-accent">Special: {outlineMetadata.special_requests.join(', ')}</div>
                      )}
                      {outlineMetadata.topic_summary && (
                        <div className="italic">{outlineMetadata.topic_summary}</div>
                      )}
                    </div>
                  )}
                  <Button
                    onClick={handleGenerate}
                    className={`w-full h-12 bg-gradient-to-r ${typeColor} text-white hover:opacity-90 font-medium text-base shadow-lg hover:shadow-xl transition-all`}
                  >
                    <Sparkles className="w-5 h-5 mr-2" />
                    Generate {typeLabel}
                  </Button>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Right: Variations Panel ── */}
        <AnimatePresence>
          {isVariationPhase && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: chatCollapsed ? 'calc(100% - 48px)' : 'calc(100% - 380px)', opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
              className="flex flex-col h-full overflow-hidden"
            >
              <div className="px-6 py-4 border-b border-border/50 bg-muted/20">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-foreground">
                      {flowPhase === 'generating-variations' ? 'Generating Design Variations...' : 'Choose Your Design Style'}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      {flowPhase === 'generating-variations'
                        ? `${variations.filter(v => (v.slides?.filter(Boolean).length || 0) >= (variationSampleIndices.length || 3)).length} of ${parseInt(answers.variationCount) || 3} variations ready`
                        : 'Same content, different visual directions — pick your favorite'}
                    </p>
                  </div>
                  {flowPhase === 'generating-variations' && (
                    <button
                      type="button"
                      onClick={() => {
                        // Abort the SSE stream, drop any partial state, and
                        // bounce back to the questionnaire so the user can
                        // tweak the inputs instead of waiting it out.
                        try {
                          if (variationAbortRef.current) {
                            variationAbortRef.current.abort();
                            variationAbortRef.current = null;
                          }
                        } catch (_) { /* no-op */ }
                        setVariations([]);
                        setSelectedVariationIndex(null);
                        setFlowPhase('questionnaire');
                        toast.info('Variation generation cancelled.');
                      }}
                      className="px-3 py-1.5 text-xs font-medium rounded-md border border-border text-muted-foreground hover:text-foreground hover:border-foreground/40 transition-colors shrink-0"
                    >
                      Cancel
                    </button>
                  )}
                </div>
                {answers.brandIdentity && answers.brandIdentity !== 'none' && (
                  <div className="flex items-center gap-2 mt-2">
                    <button
                      onClick={() => { setVariationBrandMode(true); if (variations.length > 0) { startVariationGeneration(); } }}
                      className={`px-3 py-1 text-xs rounded-full border transition-colors ${variationBrandMode ? 'bg-accent text-accent-foreground border-accent' : 'border-border text-muted-foreground hover:text-foreground hover:border-foreground/30'}`}
                    >
                      On-brand layouts
                    </button>
                    <button
                      onClick={() => { setVariationBrandMode(false); if (variations.length > 0) { startVariationGeneration(); } }}
                      className={`px-3 py-1 text-xs rounded-full border transition-colors ${!variationBrandMode ? 'bg-accent text-accent-foreground border-accent' : 'border-border text-muted-foreground hover:text-foreground hover:border-foreground/30'}`}
                    >
                      Creative styles
                    </button>
                  </div>
                )}
              </div>

              <div className="flex-1 overflow-y-auto px-4 py-4">
                {variations.length === 0 && flowPhase === 'generating-variations' && (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <Loader2 className="w-8 h-8 text-accent animate-spin mb-4" />
                    <p className="text-sm text-muted-foreground">Creating your first variation...</p>
                  </div>
                )}

                <div className={`grid gap-4 ${variations.length <= 2 ? 'grid-cols-2' : variations.length === 3 ? 'grid-cols-3' : 'grid-cols-2 lg:grid-cols-4'}`}>
                  {variations.map((variation, vi) => (
                    <div
                      key={vi}
                      className={`rounded-xl border-2 transition-all cursor-pointer hover:shadow-lg ${
                        selectedVariationIndex === vi
                          ? 'border-accent shadow-lg ring-2 ring-accent/20'
                          : 'border-border/50 hover:border-border'
                      }`}
                      onClick={() => variation.slides?.length > 0 && setSelectedVariationIndex(vi)}
                    >
                      {/* Direction header */}
                      <div className="p-3 border-b border-border/30">
                        <h4 className="text-sm font-semibold text-foreground">{variation.direction?.name || `Variation ${vi + 1}`}</h4>
                        <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-2">{variation.direction?.description}</p>
                        {/* Color swatches */}
                        {variation.direction?.colors && (
                          <div className="flex gap-1.5 mt-2">
                            {Object.entries(variation.direction.colors).map(([key, color]) => (
                              <div
                                key={key}
                                className="w-4 h-4 rounded-full border border-black/10"
                                style={{ backgroundColor: color }}
                                title={`${key}: ${color}`}
                              />
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Slide previews */}
                      <div className="p-2 space-y-2">
                        {/* r183: one loading slot per sampled slide. The stream's start
                            event carries the real count; before it arrives, size from the
                            outline (min(3, n)) rather than a fixed three — a one-slide
                            redesign showed three spinners and then one slide. */}
                        {Array.from({ length: variationSampleIndices.length || Math.max(1, Math.min(3, outlineSlides.length || 3)) }, (_, si) => si).map((si) => {
                          const slide = variation.slides?.[si];
                          if (!slide?.html_source) {
                            return (
                              <div key={si} className="aspect-video bg-muted/50 rounded-md flex items-center justify-center">
                                <Loader2 className="w-4 h-4 text-muted-foreground animate-spin" />
                              </div>
                            );
                          }
                          return (
                            <div key={si} className="rounded-md overflow-hidden border border-border/30 relative" style={{ pointerEvents: 'none', paddingBottom: '56.25%' }}>
                              <iframe
                                srcDoc={slide.html_source}
                                title={`Variation ${vi + 1} Slide ${si + 1}`}
                                className="border-0"
                                style={{
                                  width: '1280px',
                                  height: '720px',
                                  transform: `scale(${1 / (1280 / (variations.length <= 2 ? 450 : variations.length === 3 ? 300 : 250))})`,
                                  transformOrigin: 'top left',
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                }}
                                sandbox="allow-scripts"
                              />
                            </div>
                          );
                        })}
                      </div>

                      {/* Select button — available as soon as variation has slides */}
                      {variation.slides?.length > 0 && (
                        <div className="p-2 pt-0">
                          <Button
                            size="sm"
                            className={`w-full ${selectedVariationIndex === vi ? 'bg-accent text-accent-foreground' : 'variant-outline'}`}
                            variant={selectedVariationIndex === vi ? 'default' : 'outline'}
                            onClick={(e) => { e.stopPropagation(); setSelectedVariationIndex(vi); }}
                          >
                            {selectedVariationIndex === vi ? '✓ Selected' : 'Select'}
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Bottom action bar — show as soon as a variation is selectable */}
              {(flowPhase === 'choosing-variation' || (flowPhase === 'generating-variations' && selectedVariationIndex !== null)) && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="px-6 py-4 border-t border-border/50 bg-muted/20 space-y-2">
                  <Button
                    onClick={() => selectedVariationIndex !== null && handleVariationSelect(selectedVariationIndex)}
                    disabled={selectedVariationIndex === null}
                    className={`w-full h-12 bg-gradient-to-r ${typeColor} text-white hover:opacity-90 font-medium text-base shadow-lg hover:shadow-xl transition-all disabled:opacity-50`}
                  >
                    <Sparkles className="w-5 h-5 mr-2" />
                    {flowPhase === 'generating-variations' ? 'Skip waiting — ' : ''}Generate with {selectedVariationIndex !== null ? variations[selectedVariationIndex]?.direction?.name : 'Selected Style'}
                  </Button>
                  {flowPhase === 'choosing-variation' && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full text-xs"
                      onClick={() => {
                        setVariations([]);
                        setSelectedVariationIndex(null);
                        startVariationGeneration();
                      }}
                    >
                      Regenerate variations
                    </Button>
                  )}
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Right: Workspace Panel ── */}
        {isWorkspacePhase && workspaceData && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
            className="flex-1 h-full overflow-hidden flex flex-col"
          >
            {/* Workspace toolbar — height matches chat header + sidebar logo row */}
            {(chatCollapsed || flowPhase === 'workspace') && (
              <div className="flex items-center justify-between px-4 border-b border-sidebar-border bg-background flex-shrink-0 min-h-[60px]">
                {chatCollapsed ? (
                  <button onClick={onBack} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    ← Back
                  </button>
                ) : <div />}
                <div className="flex items-center gap-2">
                  {chatCollapsed && (
                    <Badge className={`text-white border-0 px-2.5 py-0.5 text-xs bg-gradient-to-r ${typeColor}`}>
                      <TypeIcon className="w-3 h-3 mr-1" />
                      {typeLabel}
                    </Badge>
                  )}
                  {flowPhase === 'workspace' && (
                    <>
                      {projectType === 'dashboard' && workspaceRef.current?.hasDataSource && (
                        <button
                          onClick={() => workspaceRef.current?.refreshData?.()}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500/10 text-amber-600 hover:bg-amber-500/20 transition-colors text-xs font-medium"
                          title="Refresh data from connected sheet"
                        >
                          <RefreshCw className="w-3.5 h-3.5" />
                          Refresh Data
                        </button>
                      )}
                      <button
                        onClick={() => workspaceRef.current?.save?.()}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-muted/50 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors text-xs font-medium"
                        title="Save project"
                      >
                        <Save className="w-3.5 h-3.5" />
                        Save
                      </button>
                      {!(workspaceData?.flowConfig?.generationMode === 'native-pptx' || workspaceData?.nativePpt) && (
                        <button
                          onClick={() => workspaceRef.current?.share?.()}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-500/10 text-blue-600 hover:bg-blue-500/20 transition-colors text-xs font-medium"
                          title="Share"
                        >
                          <Share2 className="w-3.5 h-3.5" />
                          Share
                        </button>
                      )}
                      {projectType === 'presentation' && !(workspaceData?.flowConfig?.generationMode === 'native-pptx' || workspaceData?.nativePpt) && (
                        <button
                          onClick={() => workspaceRef.current?.saveAsTemplate?.()}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-purple-500/10 text-purple-600 hover:bg-purple-500/20 transition-colors text-xs font-medium"
                          title="Turn this deck into a reusable template"
                        >
                          <LayoutTemplate className="w-3.5 h-3.5" />
                          Template
                        </button>
                      )}
                      {projectType === 'presentation' && !(workspaceData?.flowConfig?.generationMode === 'native-pptx' || workspaceData?.nativePpt) && (
                        <button
                          onClick={() => { const on = workspaceRef.current?.toggleEditMode?.(); setWsDeckEditMode(!!on); }}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-colors text-xs font-medium ${wsDeckEditMode ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-blue-500/10 text-blue-600 hover:bg-blue-500/20'}`}
                          title={wsDeckEditMode ? 'Save manual edits and exit edit mode' : 'Edit slides by hand — click text to rewrite, drag to move, click any element to style it'}
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                          {wsDeckEditMode ? 'Done editing' : 'Edit mode'}
                        </button>
                      )}
                      {/* r142: Brand check removed (user ask); Teach brand
                          only renders when the deck actually has a brand —
                          the same metadata source the child reads, so the
                          gate and the dialog can never disagree. */}
                      {projectType === 'presentation'
                        && !(workspaceData?.flowConfig?.generationMode === 'native-pptx' || workspaceData?.nativePpt)
                        && !!(workspaceData?.flowConfig?.brandIdentityId || workspaceData?.metadata?.brand_identity_id) && (
                        <button
                          onClick={() => workspaceRef.current?.teachBrand?.()}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500/10 text-amber-600 hover:bg-amber-500/20 transition-colors text-xs font-medium"
                          title="Spotted something off-brand? Record a rule every future deck with this brand follows"
                        >
                          <GraduationCap className="w-3.5 h-3.5" />
                          Teach brand
                        </button>
                      )}
                      <button
                        onClick={() => workspaceRef.current?.present?.()}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-green-500/10 text-green-600 hover:bg-green-500/20 transition-colors text-xs font-medium"
                      >
                        <Play className="w-3.5 h-3.5" />
                        Present
                      </button>
                      {!(workspaceData?.flowConfig?.generationMode === 'native-pptx' || workspaceData?.nativePpt) && (
                      <div className="relative">
                        <button
                          onClick={() => setShowExportMenu(prev => !prev)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20 transition-colors text-xs font-medium"
                        >
                          <Download className="w-3.5 h-3.5" />
                          Export
                          <ChevronDown className="w-3 h-3" />
                        </button>
                        {showExportMenu && (
                          <div className="absolute top-full right-0 mt-1 bg-card border border-border rounded-lg shadow-lg py-1 min-w-[180px] z-50">
                            {projectType === 'presentation' && (
                              <button onClick={() => { setShowExportMenu(false); setShowEditablePptxModal(true); }} className="w-full px-3 py-1.5 text-left text-xs hover:bg-muted/50 flex items-center gap-2">
                                <FileText className="w-3.5 h-3.5" /> PPTX (Editable)
                              </button>
                            )}
                            {projectType === 'presentation' && (
                              <button onClick={() => { workspaceRef.current?.exportImages?.(); setShowExportMenu(false); }} className="w-full px-3 py-1.5 text-left text-xs hover:bg-muted/50 flex items-center gap-2">
                                <Image className="w-3.5 h-3.5" /> PPTX (Images)
                              </button>
                            )}
                            {(projectType === 'infographic' || projectType === 'document' || projectType === 'dashboard') && (
                              <button onClick={() => { workspaceRef.current?.exportHtml?.(); setShowExportMenu(false); }} className="w-full px-3 py-1.5 text-left text-xs hover:bg-muted/50 flex items-center gap-2">
                                <FileText className="w-3.5 h-3.5" /> HTML
                              </button>
                            )}
                            {projectType === 'document' && (
                              <button onClick={() => { workspaceRef.current?.exportDocx?.(); setShowExportMenu(false); }} className="w-full px-3 py-1.5 text-left text-xs hover:bg-muted/50 flex items-center gap-2">
                                <FileText className="w-3.5 h-3.5" /> Word (.docx)
                              </button>
                            )}
                            {projectType === 'sheets' && (
                              <button onClick={() => { workspaceRef.current?.exportCsv?.(); setShowExportMenu(false); }} className="w-full px-3 py-1.5 text-left text-xs hover:bg-muted/50 flex items-center gap-2">
                                <Table2 className="w-3.5 h-3.5" /> CSV
                              </button>
                            )}
                            <button onClick={() => { (workspaceRef.current?.exportPdf || workspaceRef.current?.exportPDF)?.(); setShowExportMenu(false); }} className="w-full px-3 py-1.5 text-left text-xs hover:bg-muted/50 flex items-center gap-2">
                              <FileText className="w-3.5 h-3.5" /> PDF
                            </button>
                          </div>
                        )}
                      </div>
                      )}
                      {showEditablePptxModal && (
                        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4" onClick={() => setShowEditablePptxModal(false)}>
                          <div className="bg-card border border-border rounded-xl shadow-xl max-w-md w-full p-5" onClick={e => e.stopPropagation()}>
                            <div className="flex items-start justify-between gap-3 mb-3">
                              <h3 className="text-sm font-semibold text-foreground">Before you open your editable PPTX</h3>
                              <button onClick={() => setShowEditablePptxModal(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
                            </div>
                            <ul className="text-xs text-muted-foreground space-y-2 mb-4 list-disc pl-4">
                              <li>After the file downloads and you open it in PowerPoint, <strong className="text-foreground">right-click each slide and choose "Convert to Shape"</strong> so every element becomes fully editable.</li>
                              <li>Some complex shapes may not export perfectly and could need minor touch-ups.</li>
                              <li>If the slide's font isn't installed on your computer, text may fall back to a substitute font. Install the native font to match the design exactly.</li>
                            </ul>
                            <div className="flex justify-end gap-2">
                              <button onClick={() => setShowEditablePptxModal(false)} className="px-3 py-1.5 rounded-md text-xs text-muted-foreground hover:bg-muted/50">Cancel</button>
                              <button onClick={() => { setShowEditablePptxModal(false); workspaceRef.current?.exportEditable?.(); }} className="px-3 py-1.5 rounded-md text-xs bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20 font-medium flex items-center gap-1.5">
                                <Download className="w-3.5 h-3.5" /> Download editable PPTX
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
            {/* Workspace content */}
            <div className="flex-1 overflow-hidden">
            {(() => {
              const { outline, questionnaireData, flowConfig, ...savedData } = workspaceData;
              const preloadedData = { outline, questionnaireData, flowConfig, ...savedData };
              const commonProps = {
                onBack: onBack,
                onFullscreen: onFullscreen,
                userId,
                existingProjectId,
                onComplete,
                onUpdate,
                onProjectCreated: handleProjectCreated,
                externalChatMode: true,
                onChatMessage: handleChatMessage,
                chatMessages: messages,
                onUsageRefresh,
                // r140: the direct pipeline's external feed — lets the deck
                // panel's Cancel stop the parent loop
                onExternalCancel: () => { directCancelRef.current = true; },
              };

              // Native PPT masters: their own engine + in-app viewer (the
              // artifact is already a .pptx — no export). Generate mode
              // consumes the outline; view mode rehydrates a saved project.
              if (preloadedData.nativePpt || flowConfig?.generationMode === 'native-pptx') {
                return (
                  <NativePPTWorkspace
                    ref={workspaceRef}
                    userId={userId}
                    saved={preloadedData.nativePpt || null}
                    nativeTemplateId={flowConfig?.nativeTemplateId}
                    outlineSlides={outline?.slides || []}
                    deckTitle={outline?.title || preloadedData.nativePpt?.title || 'Presentation'}
                    model={flowConfig?.htmlModel || preloadedData.nativePpt?.model || 'opus'}
                    onProjectCreated={handleProjectCreated}
                  />
                );
              }

              if (projectType === 'presentation') {
                // Treat anything with an existingProjectId as a saved project,
                // even if savedSlides is empty — that just means the deck is
                // still rendering. Falling into the preloadedOutline path here
                // would dump the user on step 2 (outline review) which is
                // confusing for someone who came in from a Claude link or
                // a refresh mid-generation. The workspace renders zero slides
                // gracefully and the user refreshes to see new ones (live
                // polling is a follow-up).
                const savedPres = preloadedData.externalFeed
                  ? {
                      // r140: external feed — mounts generating, slides
                      // stream in via the imperative handle
                      slides: [],
                      title: preloadedData.savedTitle,
                      metadata: preloadedData.metadata,
                      // r149: the feed's placeholder row — the live poller is
                      // guarded off while externalFeed is set, so the id is
                      // safe to thread (autosave then UPDATES this row).
                      projectId: preloadedData.feedProjectId || null,
                      pptProjectId: null,
                      externalFeed: true,
                      externalFeedTotal: preloadedData.externalFeedTotal || 0,
                    }
                  : existingProjectId
                  ? {
                      slides: preloadedData.savedSlides || [],
                      title: preloadedData.savedTitle,
                      metadata: preloadedData.metadata,
                      projectId: existingProjectId,
                      pptProjectId: preloadedData.metadata?.ppt_project_id || null,
                    }
                  : (preloadedData.savedSlides?.length > 0
                      ? {
                          slides: preloadedData.savedSlides,
                          title: preloadedData.savedTitle,
                          metadata: preloadedData.metadata,
                          projectId: existingProjectId,
                          pptProjectId: preloadedData.metadata?.ppt_project_id || null,
                        }
                      : existingProject);
                return (
                  <CreatePPTWorkspace
                    ref={workspaceRef}
                    {...commonProps}
                    existingProject={savedPres}
                    preloadedOutline={savedPres ? null : preloadedData}
                  />
                );
              }
              if (projectType === 'infographic') {
                return <InfographicWorkspace ref={workspaceRef} {...commonProps} preloadedData={preloadedData} />;
              }
              if (projectType === 'document') {
                return <DocumentWorkspace ref={workspaceRef} {...commonProps} preloadedData={preloadedData} />;
              }
              if (projectType === 'dashboard') {
                return <DashboardWorkspace ref={workspaceRef} {...commonProps} preloadedData={preloadedData} />;
              }
              if (projectType === 'sheets') {
                return <SheetsWorkspace ref={workspaceRef} {...commonProps} preloadedData={preloadedData} />;
              }
              return null;
            })()}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
